/**
 * WorldMapPanel.jsx - ENHANCED Fantasy World Map
 *
 * Features: HD rendering, subtle glows, grid background, vignette,
 * hover effects, distance rings, compass, quality shimmer,
 * movement ghost, coordinate labels, enhanced pathing
 *
 * ⚠️ PERFORMANCE CRITICAL ⚠️
 * This file handles 1000+ tiles at 60+ FPS using:
 * - Tile caching with signatures
 * - Viewport culling (only renders on-screen tiles)
 * - Stable memoized values to prevent re-renders
 *
 * 🚨 READ WORLDMAP_PERFORMANCE.md BEFORE MODIFYING 🚨
 * Breaking the rules WILL cause FPS drops at scale
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as PIXI from 'pixi.js';
import { useGame } from '../../context/GameContext';
import { WorldMapHelp } from './WorldMapHelp';
import DungeonEntryModal from '../modals/DungeonEntryModal';
import '../modals/DungeonEntryModal.css';
import TowerEntryModal from '../modals/TowerEntryModal';
import '../modals/TowerEntryModal.css';
import EncounterEntryModal from '../modals/EncounterEntryModal';
import '../modals/EncounterEntryModal.css';
import ExpeditionCompleteModal from '../modals/ExpeditionCompleteModal';
import '../modals/ExpeditionCompleteModal.css';
import OutpostCreationModal from '../modals/OutpostCreationModal';
import '../modals/OutpostCreationModal.css';
import OutpostModal from '../modals/OutpostModal';
import '../modals/OutpostModal.css';
import { DUNGEON_CONFIG, DUNGEON_MOB_POOLS, DUNGEON_BOSS_POOLS } from '../../data/dungeon';
import { ENCOUNTER_BIOMES } from '../../data/world-map/encounterBiomes';
import { TOWER_BIOMES } from '../../data/world-map/towerBiomes';
import { BALANCE } from '../../data';
import { KINGDOM_TYPES } from '../../data/world-map/kingdoms';
import {
  hexToPixel,
  pixelToHex,
  getHexPoints,
  hexDistance as hexDistanceUtil,
  HEX_DIRECTIONS,
} from '../../utils/hexGrid';
import { canBuildRouteSegment, calculateConnectedTiles } from '../../modules/tradeRoutes';
import {
  boostSaturation,
  colorToNum,
  createLayeredHex,
  createHexBorder,
  createCircleIndicator,
  generateTileSignature,
  cleanupTileCache,
  cleanupBorderCache,
  isCacheValid,
  storeTileInCache,
  reuseCachedTile,
} from '../../modules/worldMap';
import {
  MapControls,
  ExplorationLimitTooltip,
  ExpeditionProgress,
  ExpeditionSelector,
  GatheringUI,
  TilePreviewPanel,
  LocationCard,
  CooldownDisplay,
  EventLog,
  AutoMoveStatus,
  TileBuildingButton,
  BuiltTileInfo,
  ShrineActivationPanel,
  OutpostControlsPanel,
  ExplorationLimitBar,
  CombatEntrySection,
} from './world-map';
import TileBuildingModal from '../modals/TileBuildingModal';
import { GREAT_WORKS } from '../../data/world-map/greatWorks';
import { WONDER_SITE_BIOME } from '../../data/world-map/wonderSites';
import { SHRINE_BIOMES } from '../../data/world-map/shrineBiomes';
import { WONDERS } from '../../data/wonders';
import { EXPLORATION } from '../../data/world-map/balance';
import { technologies, buildings } from '../../data';
import { registerPixiApp, unregisterPixiApp, showWorldMapScene } from '../../services/PixiAppService';

const BASE_HEX_SIZE = 52;  // Tile geometry size (30% larger for more mini-icon space)
const ICON_SCALE = 40;     // Keep icons/text at original visual size
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2.0;
const CULL_PADDING = 100;
const GRID_SIZE = 25;
const DPR = Math.min(window.devicePixelRatio || 1, 2);
const TEXT_RESOLUTION = DPR * 2; // HD text rendering
const TEXTURE_SCALE = DPR * 2; // HD texture rendering
const GLOW_TEXTURE_SIZE = 256 * TEXTURE_SCALE;
const VIGNETTE_TEXTURE_SIZE = 512 * TEXTURE_SCALE;
const GRID_TEXTURE_SIZE = GRID_SIZE * 20 * TEXTURE_SCALE;
// Idle cleanup removed - was causing crashes when user returned

// Exploration limit tech bonuses
// Build exploration tech bonuses from balance config + technology data
const EXPLORATION_TECH_BONUSES = Object.fromEntries(
  Object.entries(EXPLORATION.techBonuses).map(([techId, bonus]) => {
    const tech = technologies[techId];
    return [techId, {
      bonus,
      name: tech?.name || techId,
      icon: tech?.icon || '📜',
    }];
  })
);

// Module-level singleton for Pixi app (persists across component mounts)
let pixiAppSingleton = null;
let pixiTextureCache = null;
let pixiInitPromise = null;
let animatedPoolSingleton = { adjacentTiles: [], fogTiles: [], beacons: [], kingdom: null, player: null, clickHighlight: null, pathSegments: [], pathTarget: null, teleport: null, sparkles: [], distanceRings: null, compass: null, hoverTile: null, movementGhost: null, etaLabel: null, coordLabels: [], tradeRouteLines: null, built: false };
let glowPhaseSingleton = 0;
let lastWorldStateSingleton = '';
let lastAnimatedStateSingleton = '';

// Module-level tile cache singletons (persist across component remounts for encounter/dungeon switching)
let tileCacheSingleton = new Map(); // nodeId -> { signature, elements, fadeElements }
let borderCacheSingleton = new Map(); // nodeId -> borderGraphics
let wonderSiteGraphicsSingleton = new Map(); // wonderId -> { graphics, elements }
let borderCacheByNodeSingleton = new Map(); // nodeId -> Graphics (elevated borders)

// Player movement animation state - Frog hop style
let playerMoveAnimation = {
  isAnimating: false,
  isTeleport: false, // Different animation for teleport
  startPos: { x: 0, y: 0 },
  targetPos: { x: 0, y: 0 },
  progress: 0,
  duration: 0.45, // 450ms - sweet spot
  lastCoords: { q: 0, r: 0 }, // Track last known coordinates
  landingPulse: 0, // Pulse effect when landing (0-1)
  landingSquash: 0, // Squash on landing (0-1)
  teleportTrail: 1.0, // Trail fade for teleport (1.0 -> 0)
  teleportShockwave: 0, // Shockwave effect on teleport arrival (0-1)
  teleportBurst: [], // Particle burst on teleport arrival
};

// Gathering animation state
let gatherAnimation = {
  tileShake: 0, // Shake/bounce on gathered tile (0-1)
  tilePulse: 0, // Glow pulse on gathered tile (0-1)
  particles: [], // Flying resource particles
  gatheredTilePos: { x: 0, y: 0 }, // Position of tile that was gathered from
};



// Lookup maps for O(1) sparkle association (avoid .find() in ticker)
let beaconLookupMap = new Map();
let adjacentLookupMap = new Map();

// Trade route line animation state
let tradeRouteAnimation = {
  phase: 0, // Animation phase for flowing dots (0 to 1)
  lines: [], // Array of { startPos, endPos, dots: [{t, size}] }
};

// Wonder site cinematic fade - elements near wonder sites that should fade when hovering
let wonderSiteFadeElements = []; // Array of { elements: [...PIXI objects], siteId: string, siteTiles: Set<string> }

// Great Work cinematic fade - elements near Great Works that should fade when hovering
let greatWorkFadeElements = []; // Array of { elements: [...PIXI objects], nodeId: string, workTile: string }

// Movement queue singletons (persist across tab switches)
let movementQueueSingleton = [];
let isAutoMovingSingleton = false;
let pathPreviewSingleton = null;
let queueIntervalId = null;
let queueCallbacks = {
  moveToNode: null,
  teleportToNode: null,
  getRemainingCooldown: null,
  onMove: null,
  onComplete: null,
  onError: null,
  BIOMES: null,
};

// Module-level queue processor
function startQueueProcessor() {
  if (queueIntervalId) return;
  queueIntervalId = setInterval(() => {
    if (!isAutoMovingSingleton || movementQueueSingleton.length === 0) return;
    if (!queueCallbacks.getRemainingCooldown || !queueCallbacks.moveToNode) return;
    if (queueCallbacks.getRemainingCooldown() > 0) return;
    
    const nextStep = movementQueueSingleton[0];
    if (!nextStep) {
      isAutoMovingSingleton = false;
      movementQueueSingleton = [];
      return;
    }
    
    let result;
    const wasTeleport = nextStep.isTeleport && nextStep.nodeId;
    if (wasTeleport && queueCallbacks.teleportToNode) {
      result = queueCallbacks.teleportToNode(nextStep.nodeId);
      if (result.success && pathPreviewSingleton) {
        pathPreviewSingleton = { ...pathPreviewSingleton, teleportFrom: null };
      }
    } else if (queueCallbacks.moveToNode) {
      result = queueCallbacks.moveToNode(nextStep.q, nextStep.r);
    }
    
    if (result?.success) {
      movementQueueSingleton = movementQueueSingleton.slice(1);
      if (result.event && result.node && queueCallbacks.onMove) {
        queueCallbacks.onMove(result, wasTeleport);
      }
      if (movementQueueSingleton.length === 0) {
        isAutoMovingSingleton = false;
        pathPreviewSingleton = null;
        // Pass final destination coordinates to onComplete
        if (queueCallbacks.onComplete) queueCallbacks.onComplete(wasTeleport, nextStep.q, nextStep.r, nextStep.nodeId);
      } else {
        // Update path preview
        if (pathPreviewSingleton) {
          pathPreviewSingleton = { ...pathPreviewSingleton, path: movementQueueSingleton, turns: movementQueueSingleton.length };
        }
      }
    } else {
      isAutoMovingSingleton = false;
      movementQueueSingleton = [];
      pathPreviewSingleton = null;
      if (queueCallbacks.onError && result?.error) queueCallbacks.onError(result.error);
    }
  }, 100);
}

function stopQueueProcessor() {
  if (queueIntervalId) {
    clearInterval(queueIntervalId);
    queueIntervalId = null;
  }
}

// Full cleanup to free memory - only called on unmount
function cleanupPixiResources() {
  // Unregister from shared service first
  unregisterPixiApp();

  if (pixiAppSingleton) {
    pixiAppSingleton.destroy(true, { children: true, texture: true, baseTexture: true });
    pixiAppSingleton = null;
  }
  pixiTextureCache = null;
  pixiInitPromise = null;
  animatedPoolSingleton = { adjacentTiles: [], fogTiles: [], beacons: [], kingdom: null, player: null, clickHighlight: null, pathSegments: [], pathTarget: null, teleport: null, sparkles: [], distanceRings: null, compass: null, hoverTile: null, movementGhost: null, etaLabel: null, coordLabels: [], built: false };
  glowPhaseSingleton = 0;
  lastWorldStateSingleton = '';
  lastAnimatedStateSingleton = '';
  beaconLookupMap.clear();
  adjacentLookupMap.clear();
  console.log('[WorldMap] Cleaned up Pixi resources on unmount');
}

// Idle cleanup functions removed - were causing crashes

// Import resources for icon lookup
import { resources } from '../../data/resources';

// Helper to get resource icon from canonical source
const getResourceIcon = (resourceId) => resources[resourceId]?.icon || '📦';

// Pre-computed sine table
const SINE_TABLE = new Float32Array(360);
for (let i = 0; i < 360; i++) SINE_TABLE[i] = Math.sin((i / 360) * Math.PI * 2);
const fastSin = (phase) => SINE_TABLE[((phase % (Math.PI * 2)) / (Math.PI * 2) * 360 + 360 | 0) % 360];

// Gradient texture generator - HD quality
function createGradientTexture(size, colorStops) {
  const canvas = document.createElement('canvas');
  const scaledSize = size * TEXTURE_SCALE;
  canvas.width = scaledSize;
  canvas.height = scaledSize;
  const ctx = canvas.getContext('2d');
  const gradient = ctx.createRadialGradient(scaledSize/2, scaledSize/2, 0, scaledSize/2, scaledSize/2, scaledSize/2);
  colorStops.forEach(([stop, color]) => gradient.addColorStop(stop, color));
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, scaledSize, scaledSize);
  const texture = PIXI.Texture.from(canvas);
  texture.source.resolution = TEXTURE_SCALE;
  return texture;
}

// Vignette texture - HD quality
function createVignetteTexture(size) {
  const canvas = document.createElement('canvas');
  const scaledSize = size * TEXTURE_SCALE;
  canvas.width = scaledSize;
  canvas.height = scaledSize;
  const ctx = canvas.getContext('2d');
  const gradient = ctx.createRadialGradient(scaledSize/2, scaledSize/2, 0, scaledSize/2, scaledSize/2, scaledSize/2);
  gradient.addColorStop(0, 'rgba(60,52,42,0)');
  gradient.addColorStop(0.5, 'rgba(50,44,36,0)');
  gradient.addColorStop(0.75, 'rgba(40,35,28,0.2)');
  gradient.addColorStop(1, 'rgba(30,26,20,0.5)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, scaledSize, scaledSize);
  const texture = PIXI.Texture.from(canvas);
  texture.source.resolution = TEXTURE_SCALE;
  return texture;
}

// Grid texture - HD quality with anti-aliased lines
function createGridTexture(gridSize, lineColor, lineAlpha) {
  const canvas = document.createElement('canvas');
  const scaledGridSize = gridSize * TEXTURE_SCALE;
  const tileSize = scaledGridSize * 20;
  canvas.width = tileSize;
  canvas.height = tileSize;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.strokeStyle = lineColor;
  ctx.globalAlpha = lineAlpha;
  ctx.lineWidth = TEXTURE_SCALE;
  for (let x = 0; x <= canvas.width; x += scaledGridSize) {
    ctx.beginPath();
    ctx.moveTo(x + 0.5, 0);
    ctx.lineTo(x + 0.5, canvas.height);
    ctx.stroke();
  }
  for (let y = 0; y <= canvas.height; y += scaledGridSize) {
    ctx.beginPath();
    ctx.moveTo(0, y + 0.5);
    ctx.lineTo(canvas.width, y + 0.5);
    ctx.stroke();
  }
  const texture = PIXI.Texture.from(canvas);
  texture.source.resolution = TEXTURE_SCALE;
  return texture;
}

// hexToPixel, pixelToHex, getHexPoints imported from '../../utils/hexGrid'

// Create a smooth, anti-aliased hex graphic
function createSmoothHex(size, fillColor, fillAlpha = 1, strokeColor = null, strokeWidth = 0, strokeAlpha = 1) {
  const g = new PIXI.Graphics();

  // Main fill
  if (fillColor !== null) {
    g.poly(getHexPoints(size));
    g.fill({ color: fillColor, alpha: fillAlpha });
  }

  // Stroke with rounded joins for smoothness
  if (strokeColor !== null && strokeWidth > 0) {
    g.poly(getHexPoints(size));
    g.stroke({ color: strokeColor, width: strokeWidth, alpha: strokeAlpha, join: 'round' });
  }

  return g;
}

// Helper functions moved to src/modules/worldMap/
// (createLayeredHex, colorToNum, boostSaturation imported from module)

// hexDistance and HEX_DIRECTIONS imported from '../../utils/hexGrid'
const hexDistance = hexDistanceUtil;

// Format seconds to "Xm Ys" or "Xs" format
function formatTime(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

// Draw organic curved dotted line with hand-drawn wobble - HD quality
function drawOrganicDottedLine(graphics, x1, y1, x2, y2, color, width, alpha, seed = 0) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist < 1) return;
  
  const perpX = -dy / dist;
  const perpY = dx / dist;
  
  // Add slight curve offset (hand-drawn feel)
  const curveAmount = dist * 0.08;
  const wobble1 = Math.sin(seed * 3.7) * curveAmount;
  const wobble2 = Math.cos(seed * 2.3) * curveAmount * 0.5;
  
  // Control points for bezier with wobble
  const cp1x = x1 + dx * 0.33 + perpX * wobble1;
  const cp1y = y1 + dy * 0.33 + perpY * wobble1;
  const cp2x = x1 + dx * 0.66 + perpX * wobble2;
  const cp2y = y1 + dy * 0.66 + perpY * wobble2;
  
  // Sample points along bezier curve - more points for smoother curve
  const numSegments = Math.max(16, Math.floor(dist / 4));
  const points = [];
  for (let i = 0; i <= numSegments; i++) {
    const t = i / numSegments;
    const mt = 1 - t;
    // Cubic bezier
    const x = mt*mt*mt*x1 + 3*mt*mt*t*cp1x + 3*mt*t*t*cp2x + t*t*t*x2;
    const y = mt*mt*mt*y1 + 3*mt*mt*t*cp1y + 3*mt*t*t*cp2y + t*t*t*y2;
    points.push({ x, y });
  }
  
  // Draw dashes along the curve with slight variation
  const dashLen = 10 + Math.sin(seed * 5) * 2;
  const gapLen = 7 + Math.cos(seed * 4) * 1.5;
  let accumDist = 0;
  let inDash = true;
  let dashStart = points[0];
  
  for (let i = 1; i < points.length; i++) {
    const segDist = Math.sqrt(
      Math.pow(points[i].x - points[i-1].x, 2) + 
      Math.pow(points[i].y - points[i-1].y, 2)
    );
    accumDist += segDist;
    
    const targetLen = inDash ? dashLen : gapLen;
    if (accumDist >= targetLen) {
      if (inDash) {
        graphics.moveTo(dashStart.x, dashStart.y);
        graphics.lineTo(points[i].x, points[i].y);
      }
      dashStart = points[i];
      accumDist = 0;
      inDash = !inDash;
    }
  }
  // Finish last dash if needed
  if (inDash && points.length > 0) {
    graphics.moveTo(dashStart.x, dashStart.y);
    graphics.lineTo(points[points.length - 1].x, points[points.length - 1].y);
  }
  
  graphics.stroke({ color, width, alpha, cap: 'round', join: 'round' });
}

export function WorldMapPanel({ greatWorkPlacement, onCancelGreatWorkPlacement, onNotification }) {
  const {
    worldMap, playerResources, moveToNode, gatherFromNode, calculateGatherYield,
    buildOnTile, getTileBuildCost, getAvailableTileBuildings, buildings, buildingCounts,
    isMovementOnCooldown, getRemainingCooldown, isGatherOnCooldown, getGatherCooldownRemaining,
    getCurrentNodeData, getExplorationLimit, teleportToNode, findPathToTile, BIOMES,
    researchedTechs, toggleAutoGather,
    // Expedition system
    isOnExpedition, getExpeditionProgress, calculateExpeditionRewards,
    startMapExpedition, cancelExpedition, clearExpeditionCompletion, getExpeditionDurations, EXPEDITION,
    // Dungeon system
    enterDungeon, enterEncounter, enterTower, playerCharacter, currentEra,
    // Tutorial
    triggerTutorial,
    completedTutorials,
    currentTutorial,
    // Guided Tours
    startGuidedTour,
    completedGuidedTours,
    currentGuidedTour,
    triggerGuidedTourAction,
    pendingGuidedTourModal,
    setPendingGuidedTourModal,
    // Great Works
    startGreatWorkConstruction,
    currentGreatWorkConstruction,
    greatWorkLocations,
    builtGreatWorks,
    getGreatWorkProgressInfo,
    // Dev Tools
    devCooldownsDisabled,
    devTeleportMode,
    // Trade Route placement mode
    buildingPlacementMode,
    placeBuildingOnTile,
    exitBuildingPlacementMode,
    // Trade Route building
    buildTradeRouteSegment,
    getTradeRouteCost,
    isTileOnTradeRoute,
    // Outpost system
    getOutpostConstructionProgress,
    // Shrine system
    activateShrine,
  } = useGame();

  const containerRef = useRef(null);
  const pixiAppRef = useRef(null);
  const worldContainerRef = useRef(null);
  const bgLayerRef = useRef(null);
  const staticLayerRef = useRef(null);
  const borderLayerRef = useRef(null); // New layer for special tile borders (dungeon, beacon, player)
  const animatedLayerRef = useRef(null);
  const fogLayerRef = useRef(null);
  const pathLayerRef = useRef(null);
  const uiLayerRef = useRef(null);
  const particleContainerRef = useRef(null);
  const glowPhaseRef = useRef(glowPhaseSingleton);
  
  const texturesRef = useRef({ goldGlow: null, blueGlow: null, purpleGlow: null, orangeGlow: null, sparkle: null, vignette: null, grid: null, ready: false });
  const animatedPoolRef = useRef(animatedPoolSingleton);
  const lastAnimatedStateRef = useRef(lastAnimatedStateSingleton);
  const viewportRef = useRef({ width: 800, height: 600 });
  
  const [zoom, setZoom] = useState(1.0);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [hasDragged, setHasDragged] = useState(false);
  const [moveCooldown, setMoveCooldown] = useState(0);
  const [gatherCooldown, setGatherCooldown] = useState(0);
  const [messages, setMessages] = useState([]);
  const [gatherCombo, setGatherCombo] = useState(0);
  const comboTimeoutRef = useRef(null);
  const [eventLog, setEventLog] = useState([]);
  const [eventLogIndex, setEventLogIndex] = useState(0);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [pathPreview, setPathPreview] = useState(pathPreviewSingleton);
  const [movementQueue, setMovementQueue] = useState(movementQueueSingleton);
  const [isAutoMoving, setIsAutoMoving] = useState(isAutoMovingSingleton);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [showTileBuildingModal, setShowTileBuildingModal] = useState(false);
  const [dungeonEntryBiomeId, setDungeonEntryBiomeId] = useState(null);
  const [dungeonEntryNodeId, setDungeonEntryNodeId] = useState(null);
  const [dungeonCooldownMs, setDungeonCooldownMs] = useState(null); // Remaining cooldown in ms
  const [encounterEntryBiomeId, setEncounterEntryBiomeId] = useState(null);
  const [encounterEntryNodeId, setEncounterEntryNodeId] = useState(null);
  const [encounterCooldownMs, setEncounterCooldownMs] = useState(null); // Remaining cooldown in ms for encounters
  const [towerEntryBiomeId, setTowerEntryBiomeId] = useState(null);
  const [towerEntryNodeId, setTowerEntryNodeId] = useState(null);
  const [towerCooldownMs, setTowerCooldownMs] = useState(null); // Remaining cooldown in ms for towers
  const [pendingModal, setPendingModal] = useState(null); // { type: 'dungeon'|'encounter'|'outpost', biomeId?, nodeId } - show after tutorial
  const [outpostCreationNodeId, setOutpostCreationNodeId] = useState(null); // NodeId for outpost creation modal
  const [outpostManageNodeId, setOutpostManageNodeId] = useState(null); // NodeId for outpost management modal
  const [clickedTile, setClickedTile] = useState(null);
  const [previewTile, setPreviewTile] = useState(null);
  const [hoveredTile, setHoveredTile] = useState(null);
  const [pixiReady, setPixiReady] = useState(false);
  const revealingTilesRef = useRef({}); // { nodeId: timestamp } - tracks tiles being revealed with animation
  const [selectedExpeditionDuration, setSelectedExpeditionDuration] = useState(null);
  const [expeditionProgress, setExpeditionProgress] = useState(null);
  const [expeditionComplete, setExpeditionComplete] = useState(null); // Show completion modal
  const [greatWorkRemaining, setGreatWorkRemaining] = useState(null); // Great Work construction remaining ms
  const [outpostConstructionKey, setOutpostConstructionKey] = useState(0); // Force re-render for outpost construction progress
  const [viewportSize, setViewportSize] = useState({ width: 800, height: 600 });
  const [perfDebugEnabled, setPerfDebugEnabled] = useState(false); // PERF: Debug mode toggle
  const [perfStats, setPerfStats] = useState({
    fps: 0,
    objects: 0,
    fogTiles: 0,
    // Advanced stats
    tilesTotal: 0,
    tilesCached: 0,
    tilesVisible: 0,
    bordersTotal: 0,
    bordersVisible: 0,
    wonderSitesTotal: 0,
    wonderSitesVisible: 0,
    memoryMB: 0,
  }); // PERF: Stats for debug display
  const [tradeRouteOverlay, setTradeRouteOverlay] = useState(false); // Trade Route view mode
  
  const lastClickRef = useRef({ time: 0, nodeId: null });
  const hoveredTileRef = useRef(null);
  const currentCoordsRef = useRef({ q: 0, r: 0 });
  const lastHoverUpdateRef = useRef(0); // PERF: Throttle hover updates
  const fpsFramesRef = useRef([]); // PERF: Track frame times for FPS calculation
  const lastPerfUpdateRef = useRef(0); // PERF: Throttle perf stats updates
  const lastWorldStateRef = useRef(lastWorldStateSingleton);

  // PERF: Persistent tile cache - uses module-level singletons to survive remounts
  const tileCacheRef = useRef(tileCacheSingleton);
  const borderCacheRef = useRef(borderCacheSingleton);
  const wonderSiteGraphicsRef = useRef(wonderSiteGraphicsSingleton);

  const beaconNodes = useMemo(() => {
    const nodes = worldMap?.discoveredNodes || {};
    return Object.values(nodes).filter(n => !n.isHub && BIOMES?.[n.biome]?.isTeleportBeacon);
  }, [worldMap?.discoveredNodes, BIOMES]);

  // PERF: Pre-compute reverse lookup for Great Works (O(1) instead of O(n²) in render loop)
  const nodeIdToGreatWork = useMemo(() => {
    const map = {};
    Object.entries(greatWorkLocations || {}).forEach(([workId, nodeId]) => {
      map[nodeId] = workId;
    });
    return map;
  }, [greatWorkLocations]);

  // PERF: Memoize discovered count to avoid repeated Object.keys() calls
  const discoveredCount = useMemo(() =>
    Object.keys(worldMap?.discoveredNodes || {}).length,
    [worldMap?.discoveredNodes]
  );

  // PERF: Memoize wonder site adjacency computation
  const { wonderSiteTileIds, tilesAdjacentToWonderSites } = useMemo(() => {
    const tileIds = new Set();
    const adjacentMap = new Map();
    const discoveredWonderSites = worldMap?.discoveredWonderSites || [];
    const wonderSites = worldMap?.wonderSites || {};
    const discoveredNodes = worldMap?.discoveredNodes || {};

    discoveredWonderSites.forEach(wonderId => {
      const site = wonderSites[wonderId];
      if (!site?.fullyRevealed) return;

      site.tileDefinitions.forEach(tileDef => {
        const { q, r } = tileDef.coordinates;
        const nodeId = `node_${q}_${r}`;
        tileIds.add(nodeId);

        HEX_DIRECTIONS.forEach(dir => {
          const adjQ = q + dir.q, adjR = r + dir.r;
          const adjId = `node_${adjQ}_${adjR}`;
          if (!tileIds.has(adjId) && discoveredNodes[adjId]) {
            if (!adjacentMap.has(adjId)) adjacentMap.set(adjId, new Set());
            adjacentMap.get(adjId).add(wonderId);
          }
        });
      });
    });
    return { wonderSiteTileIds: tileIds, tilesAdjacentToWonderSites: adjacentMap };
  }, [worldMap?.discoveredWonderSites, worldMap?.wonderSites, worldMap?.discoveredNodes]);

  // PERF: Memoize Great Work adjacency computation
  const { greatWorkTileIds, tilesAdjacentToGreatWorks } = useMemo(() => {
    const tileIds = new Set();
    const adjacentMap = new Map();
    const discoveredNodes = worldMap?.discoveredNodes || {};

    Object.entries(greatWorkLocations || {}).forEach(([workId, nodeId]) => {
      const node = discoveredNodes[nodeId];
      if (!node) return;

      const { q, r } = node.coordinates || { q: 0, r: 0 };
      tileIds.add(nodeId);

      HEX_DIRECTIONS.forEach(dir => {
        const adjQ = q + dir.q, adjR = r + dir.r;
        const adjId = `node_${adjQ}_${adjR}`;
        if (!tileIds.has(adjId) && discoveredNodes[adjId]) {
          if (!adjacentMap.has(adjId)) adjacentMap.set(adjId, new Set());
          adjacentMap.get(adjId).add(nodeId);
        }
      });
    });
    return { greatWorkTileIds: tileIds, tilesAdjacentToGreatWorks: adjacentMap };
  }, [greatWorkLocations, worldMap?.discoveredNodes]);

  // PERF: Memoize worldMap sub-properties to prevent unnecessary render triggers
  // These stable references prevent the render useEffect from running when worldMap object changes
  // but the actual rendering data hasn't changed
  const stableDiscoveredNodes = useMemo(() => worldMap?.discoveredNodes || {}, [worldMap?.discoveredNodes]);
  const stableOutposts = useMemo(() => worldMap?.outposts || {}, [worldMap?.outposts]);

  // PERF: Memoize teleport options for pathfinding (Kingdom + Beacons + Outposts)
  const teleportOptions = useMemo(() => {
    const options = [{ nodeId: 'kingdom', q: 0, r: 0, name: 'Kingdom' }];
    // Add teleport beacons
    beaconNodes.forEach(b => {
      const { q, r } = b.coordinates || { q: 0, r: 0 };
      const nodeId = `node_${q}_${r}`;
      options.push({ nodeId, q, r, name: BIOMES?.[b.biome]?.name || 'Waystone' });
    });
    // Add completed outposts as teleport destinations
    Object.entries(stableOutposts).forEach(([nodeId, outpost]) => {
      if (!outpost.isConstructing) {
        const coords = nodeId.match(/node_(-?\d+)_(-?\d+)/);
        if (coords) {
          const q = parseInt(coords[1], 10);
          const r = parseInt(coords[2], 10);
          options.push({ nodeId, q, r, name: outpost.name || 'Outpost' });
        }
      }
    });
    return options;
  }, [beaconNodes, BIOMES, stableOutposts]);

  const stableTileBuildings = useMemo(() => worldMap?.tileBuildings || {}, [worldMap?.tileBuildings]);
  const stableCurrentNode = useMemo(() => worldMap?.currentNode || 'kingdom', [worldMap?.currentNode]);
  const stableDiscoveredWonderSites = useMemo(() => worldMap?.discoveredWonderSites || [], [worldMap?.discoveredWonderSites]);
  const stableWonderSites = useMemo(() => worldMap?.wonderSites || {}, [worldMap?.wonderSites]);
  const stableTradeRouteSegments = useMemo(() => worldMap?.tradeRoutes?.segments || {}, [worldMap?.tradeRoutes?.segments]);
  const stableConnectedToKingdom = useMemo(() => {
    const raw = worldMap?.tradeRoutes?.connectedToKingdom;
    return raw instanceof Set ? raw : new Set();
  }, [worldMap?.tradeRoutes?.connectedToKingdom]);

  const getCurrentCoords = useCallback(() => {
    if (!stableCurrentNode || stableCurrentNode === 'kingdom') return { q: 0, r: 0 };
    const match = stableCurrentNode.match(/node_(-?\d+)_(-?\d+)/);
    return match ? { q: parseInt(match[1]), r: parseInt(match[2]) } : { q: 0, r: 0 };
  }, [stableCurrentNode]);

  // Check if dungeon is on cooldown and get remaining time
  const getDungeonCooldown = useCallback((nodeId) => {
    if (devCooldownsDisabled) return null; // Dev mode bypass
    if (!worldMap?.dungeonCooldowns?.[nodeId]) return null;

    const cooldownExpires = worldMap.dungeonCooldowns[nodeId];
    const now = Date.now();

    if (now >= cooldownExpires) {
      return null; // Cooldown expired
    }

    return cooldownExpires - now; // Return remaining milliseconds
  }, [worldMap?.dungeonCooldowns, devCooldownsDisabled]);

  // Track if we're waiting for user to click the outpost button during tour
  const [outpostTourNodeId, setOutpostTourNodeId] = useState(null);

  // Handle opening Outpost Creation Modal with tutorial/guided tour trigger
  const handleCreateOutpost = useCallback((nodeId) => {
    // Check if guided tour hasn't been completed yet (prefer guided tour over static tutorial)
    if (!completedGuidedTours?.includes('outposts')) {
      // If tour is already active, this is the actual button click - open modal
      if (currentGuidedTour === 'outposts') {
        // Signal action for guided tour
        triggerGuidedTourAction?.('outpostModalOpened');
        // Open the modal
        setOutpostCreationNodeId(nodeId);
      } else {
        // Start guided tour - DON'T open modal yet
        // Store the nodeId so we know which tile to use
        setOutpostTourNodeId(nodeId);
        startGuidedTour?.('outposts');
      }
    } else if (!completedTutorials?.includes('outposts')) {
      // Fall back to static tutorial if guided tour completed but tutorial not
      triggerTutorial('outposts');
      setPendingModal({ type: 'outpost', nodeId });
    } else {
      // Both completed, show modal directly
      setOutpostCreationNodeId(nodeId);
    }
  }, [triggerTutorial, completedTutorials, completedGuidedTours, startGuidedTour, currentGuidedTour, triggerGuidedTourAction]);

  // Handle opening Tile Building Modal with tutorial trigger
  const handleOpenTileBuildingModal = useCallback(() => {
    // Check if tutorial hasn't been completed yet
    if (!completedTutorials?.includes('firstTileBuild')) {
      // Trigger tutorial first, queue the modal to show after
      triggerTutorial('firstTileBuild');
      setPendingModal({ type: 'tileBuild' });
    } else {
      // Tutorial already completed, show modal directly
      setShowTileBuildingModal(true);
    }
  }, [triggerTutorial, completedTutorials]);

  // Handle shrine activation
  const handleActivateShrine = useCallback((nodeId) => {
    if (!activateShrine) return;
    const result = activateShrine(nodeId);
    if (result?.success) {
      showMessage(`✨ Blessing received: ${result.bonus}`, 'blessing');
    } else if (result?.error) {
      showMessage(`❌ ${result.error}`, 'error');
    }
  }, [activateShrine]);

  // Listen for pending guided tour modal requests
  useEffect(() => {
    if (pendingGuidedTourModal === 'outpostCreation') {
      // Open the outpost creation modal using the stored node ID from tour start
      if (outpostTourNodeId) {
        setOutpostCreationNodeId(outpostTourNodeId);
        // Clear the pending request
        setPendingGuidedTourModal?.(null);
      }
    }
  }, [pendingGuidedTourModal, outpostTourNodeId, setPendingGuidedTourModal]);

  // Clear outpostTourNodeId when outposts tour ends (completed or skipped)
  useEffect(() => {
    if (currentGuidedTour !== 'outposts' && outpostTourNodeId) {
      setOutpostTourNodeId(null);
    }
  }, [currentGuidedTour, outpostTourNodeId]);

  // Sync hoveredTile to ref for ticker access (avoids re-rendering animated layer)
  useEffect(() => {
    hoveredTileRef.current = hoveredTile;
  }, [hoveredTile]);


  // Sync current coords to ref for ticker access
  useEffect(() => {
    currentCoordsRef.current = getCurrentCoords();
  }, [getCurrentCoords]);

  const isInViewport = useCallback((worldX, worldY, padding = CULL_PADDING) => {
    const vp = viewportRef.current;
    const screenX = (worldX * zoom) + vp.width / 2 + panOffset.x * zoom;
    const screenY = (worldY * zoom) + vp.height / 2 + panOffset.y * zoom;
    return screenX >= -padding && screenX <= vp.width + padding && screenY >= -padding && screenY <= vp.height + padding;
  }, [zoom, panOffset]);

  // Initialize Pixi & Textures
  useEffect(() => {
    if (!containerRef.current) return;
    
    let cancelled = false;
    
    const initPixi = async () => {
      // Reuse existing app if available
      if (pixiAppSingleton && !pixiAppSingleton.renderer.destroyed) {
        const app = pixiAppSingleton;
        
        // Just reattach canvas
        if (!containerRef.current.contains(app.canvas)) {
          containerRef.current.appendChild(app.canvas);
        }
        
        // Resize to current container
        app.renderer.resize(containerRef.current.clientWidth, containerRef.current.clientHeight);
        viewportRef.current = { width: app.screen.width, height: app.screen.height };
        setViewportSize({ width: app.screen.width, height: app.screen.height });
        
        pixiAppRef.current = app;
        texturesRef.current = pixiTextureCache;
        
        // Reattach layer refs (don't clear - content is still valid)
        worldContainerRef.current = app.stage.children.find(c => c.sortableChildren);
        if (worldContainerRef.current) {
          bgLayerRef.current = worldContainerRef.current.children.find(c => c.zIndex === -2);
          staticLayerRef.current = worldContainerRef.current.children.find(c => c.zIndex === 0 && !(c instanceof PIXI.ParticleContainer));
          borderLayerRef.current = worldContainerRef.current.children.find(c => c.zIndex === 0.25);
          fogLayerRef.current = worldContainerRef.current.children.find(c => c.zIndex === 0.5);
          animatedLayerRef.current = worldContainerRef.current.children.find(c => c.zIndex === 1);
          particleContainerRef.current = worldContainerRef.current.children.find(c => c instanceof PIXI.ParticleContainer);
          pathLayerRef.current = worldContainerRef.current.children.find(c => c.zIndex === 3);
        }
        uiLayerRef.current = app.stage.children.find(c => c.zIndex === 10);
        
        // Keep pool.built = true so ticker keeps running smoothly
        // Layers will update on next worldMap change if needed

        // Ensure WorldMap scene is active (in case coming back from Dungeon)
        showWorldMapScene();

        // Force tile re-render on remount by invalidating state key
        // This ensures tiles are properly re-added to layers after scene switch
        lastWorldStateSingleton = '';

        app.ticker.start();
        setPixiReady(true);
        return;
      }
      
      // Wait if already initializing
      if (pixiInitPromise) {
        await pixiInitPromise;
        if (!cancelled) initPixi();
        return;
      }
      
      // Create new app
      pixiInitPromise = (async () => {
        const app = new PIXI.Application();
        const dpr = Math.min(window.devicePixelRatio || 1, 2); // Cap at 2 for performance
        await app.init({
          width: containerRef.current?.clientWidth || 800,
          height: containerRef.current?.clientHeight || 600,
          backgroundColor: 0x4a4238,
          antialias: true,
          resolution: dpr,
          autoDensity: true,
          powerPreference: 'high-performance',
        });
        
        if (cancelled || !containerRef.current) { 
          app.destroy(true); 
          pixiInitPromise = null;
          return null; 
        }
        
        containerRef.current.appendChild(app.canvas);
        pixiAppSingleton = app;
        pixiAppRef.current = app;
        viewportRef.current = { width: app.screen.width, height: app.screen.height };
        setViewportSize({ width: app.screen.width, height: app.screen.height });

        // Register with shared service for DungeonPanel
        registerPixiApp(app);
        
        // Create and cache textures
        const tex = {
          goldGlow: createGradientTexture(256, [[0,'rgba(255,240,180,0.007)'],[0.15,'rgba(255,210,120,0.0045)'],[0.4,'rgba(255,180,60,0.00225)'],[0.7,'rgba(200,140,40,0.00075)'],[1,'rgba(150,100,30,0)']]),
          blueGlow: createGradientTexture(256, [[0,'rgba(180,220,255,0.007)'],[0.15,'rgba(120,180,255,0.0045)'],[0.4,'rgba(80,140,220,0.00225)'],[0.7,'rgba(50,100,180,0.00075)'],[1,'rgba(30,60,120,0)']]),
          purpleGlow: createGradientTexture(256, [[0,'rgba(240,200,255,0.0075)'],[0.15,'rgba(200,140,255,0.00525)'],[0.4,'rgba(160,100,220,0.003)'],[0.7,'rgba(120,60,180,0.0012)'],[1,'rgba(80,40,140,0)']]),
          orangeGlow: createGradientTexture(256, [[0,'rgba(255,220,150,0.007)'],[0.2,'rgba(255,180,100,0.0045)'],[0.5,'rgba(255,140,60,0.00225)'],[1,'rgba(200,100,40,0)']]),
          sparkle: createGradientTexture(64, [[0,'rgba(255,255,255,0.012)'],[0.3,'rgba(255,250,220,0.0075)'],[0.6,'rgba(255,240,180,0.003)'],[1,'rgba(255,220,150,0)']]),
          vignette: createVignetteTexture(512),
          grid: createGridTexture(GRID_SIZE, '#6a6255', 0.12),
          ready: true
        };
        pixiTextureCache = tex;
        texturesRef.current = tex;
        
        // Create layer hierarchy
        const worldContainer = new PIXI.Container();
        worldContainer.sortableChildren = true;
        app.stage.addChild(worldContainer);
        worldContainerRef.current = worldContainer;
        
        const bgLayer = new PIXI.Container();
        bgLayer.zIndex = -2;
        worldContainer.addChild(bgLayer);
        bgLayerRef.current = bgLayer;
        
        const gridSprite = new PIXI.TilingSprite({ texture: tex.grid, width: 4000, height: 4000 });
        gridSprite.position.set(-2000, -2000);
        gridSprite.alpha = 0.5;
        bgLayer.addChild(gridSprite);
        
        const staticLayer = new PIXI.Container();
        staticLayer.zIndex = 0;
        worldContainer.addChild(staticLayer);
        staticLayerRef.current = staticLayer;

        // Border layer for special tile borders (dungeon, beacon, player) - sits above tiles
        const borderLayer = new PIXI.Container();
        borderLayer.zIndex = 0.25;
        worldContainer.addChild(borderLayer);
        borderLayerRef.current = borderLayer;

        // New fog layer for animated unexplored tiles
        const fogLayer = new PIXI.Container();
        fogLayer.zIndex = 0.5;
        worldContainer.addChild(fogLayer);
        fogLayerRef.current = fogLayer;
        
        const animatedLayer = new PIXI.Container();
        animatedLayer.zIndex = 1;
        worldContainer.addChild(animatedLayer);
        animatedLayerRef.current = animatedLayer;
        
        const particleContainer = new PIXI.ParticleContainer(500, { scale: true, position: true, alpha: true, tint: true });
        particleContainer.zIndex = 2;
        worldContainer.addChild(particleContainer);
        particleContainerRef.current = particleContainer;
        
        const pathLayer = new PIXI.Container();
        pathLayer.zIndex = 3;
        worldContainer.addChild(pathLayer);
        pathLayerRef.current = pathLayer;
        
        const uiLayer = new PIXI.Container();
        uiLayer.zIndex = 10;
        app.stage.addChild(uiLayer);
        uiLayerRef.current = uiLayer;
        
        const vignette = new PIXI.Sprite(tex.vignette);
        vignette.width = app.screen.width * 1.5;
        vignette.height = app.screen.height * 1.5;
        vignette.anchor.set(0.5);
        vignette.position.set(app.screen.width / 2, app.screen.height / 2);
        uiLayer.addChild(vignette);
        
        const compass = new PIXI.Container();
        compass.position.set(60, app.screen.height - 60);
        
        const compassBg = new PIXI.Graphics();
        compassBg.circle(0, 0, 28);
        compassBg.fill({ color: 0x3a352e, alpha: 0.8 });
        compassBg.circle(0, 0, 28);
        compassBg.stroke({ color: 0x6a6255, width: 2, alpha: 0.6 });
        compass.addChild(compassBg);
        
        const compassRose = new PIXI.Graphics();
        compassRose.moveTo(0, -20);
        compassRose.lineTo(5, -5);
        compassRose.lineTo(0, -10);
        compassRose.lineTo(-5, -5);
        compassRose.closePath();
        compassRose.fill({ color: 0xd4a055, alpha: 0.9 });
        compassRose.moveTo(0, 20);
        compassRose.lineTo(4, 5);
        compassRose.lineTo(-4, 5);
        compassRose.closePath();
        compassRose.fill({ color: 0x7a7265, alpha: 0.7 });
        compassRose.circle(15, 0, 3);
        compassRose.fill({ color: 0x7a7265, alpha: 0.6 });
        compassRose.circle(-15, 0, 3);
        compassRose.fill({ color: 0x7a7265, alpha: 0.6 });
        compass.addChild(compassRose);
        
        const nLabel = new PIXI.Text({ resolution: TEXT_RESOLUTION, text: 'N', style: { fontFamily: 'serif', fontSize: 12, fontWeight: 'bold', fill: 0xd4a055 } });
        nLabel.anchor.set(0.5);
        nLabel.position.set(0, -22);
        compass.addChild(nLabel);
        
        uiLayer.addChild(compass);
        animatedPoolRef.current.compass = compass;

        pixiInitPromise = null;
        return app;
      })();
      
      await pixiInitPromise;
      if (!cancelled) setPixiReady(true);
    };
    
    initPixi();
    
    const handleResize = () => {
      if (containerRef.current && pixiAppRef.current?.renderer) {
        pixiAppRef.current.renderer.resize(containerRef.current.clientWidth, containerRef.current.clientHeight);
        viewportRef.current = { width: containerRef.current.clientWidth, height: containerRef.current.clientHeight };
        setViewportSize({ width: containerRef.current.clientWidth, height: containerRef.current.clientHeight });
      }
    };
    
    window.addEventListener('resize', handleResize);

    return () => {
      cancelled = true;
      window.removeEventListener('resize', handleResize);
      // Just stop ticker, don't destroy - app persists for fast re-mount
      if (pixiAppRef.current) {
        pixiAppRef.current.ticker.stop();
      }
      setPixiReady(false);
    };
  }, []);

  // Manage ticker when component visibility changes
  useEffect(() => {
    const app = pixiAppRef.current;
    const container = containerRef.current;
    if (!app || !pixiReady || !container) return;

    // Ensure ticker is running when component is visible
    if (container.offsetParent !== null && !app.ticker.started) {
      app.ticker.start();
    }

    // Use MutationObserver to detect when panel becomes visible/hidden
    const observer = new MutationObserver(() => {
      if (container.offsetParent !== null) {
        // Panel became visible - start ticker if not running
        if (!app.ticker.started) {
          app.ticker.start();
        }
      }
    });

    // Observe parent element for visibility changes
    if (container.parentElement) {
      observer.observe(container.parentElement, {
        attributes: true,
        attributeFilter: ['style', 'class'],
        childList: false,
        subtree: false
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [pixiReady]);

  // Zoom/Pan transform
  useEffect(() => {
    const app = pixiAppRef.current, wc = worldContainerRef.current;
    if (!app || !wc || !pixiReady) return;
    wc.scale.set(zoom);
    wc.position.set(app.screen.width / 2 + panOffset.x * zoom, app.screen.height / 2 + panOffset.y * zoom);
  }, [zoom, panOffset, pixiReady]);

  // ========================================
  // ⚠️ CRITICAL PERFORMANCE SECTION ⚠️
  // ========================================
  // This useEffect handles rendering of 1000+ tiles at 60+ FPS
  // READ WORLDMAP_PERFORMANCE.md BEFORE MODIFYING
  //
  // Safety rules:
  // 1. NEVER add player position (getCurrentCoords, stableCurrentNode) to dependency array
  // 2. ALWAYS use stable memoized values (stableDiscoveredNodes, NOT worldMap)
  // 3. ALWAYS check el.parent before addChild to prevent duplicate add/remove
  // 4. ALWAYS use caching with signatures for any new PIXI objects
  // 5. ALWAYS add viewport culling in ticker for new static graphics
  // ========================================

  // Build static layer (discovered tiles only - fog moved to animated)
  useEffect(() => {
    const staticLayer = staticLayerRef.current, borderLayer = borderLayerRef.current, tex = texturesRef.current;
    if (!staticLayer || !borderLayer || !tex.ready || !pixiReady) {
      return;
    }

    // Use stable references to prevent unnecessary re-renders
    const discoveredNodes = stableDiscoveredNodes;
    const currentCoords = getCurrentCoords();
    const discoveredWonderSites = stableDiscoveredWonderSites;
    const wonderSites = stableWonderSites;
    // PERF: Removed gwTimerSec from stateKey - timer updates handled in ticker
    const isInPlacementMode = !!greatWorkPlacement?.validPlacements?.length;
    const placementModeKey = isInPlacementMode ? `pm${greatWorkPlacement.workId}` : '';
    const tradeRouteSegmentCount = Object.keys(stableTradeRouteSegments).length;
    const tradeRouteOverlayKey = tradeRouteOverlay ? 'tr1' : 'tr0';
    const buildingPlacementKey = buildingPlacementMode ? `bp${buildingPlacementMode.buildingId}` : '';
    // PERF: Removed currentCoords and stableCurrentNode from stateKey
    // Player position changes are handled separately - elevated borders drawn in separate useEffect
    // This prevents useEffect from running on every movement, dramatically improving FPS
    const stateKey = `${Object.keys(discoveredNodes).length}-${Object.keys(stableOutposts).length}-${Object.keys(stableTileBuildings).length}-ws${discoveredWonderSites.length}-gw${Object.keys(greatWorkLocations||{}).length}-gc${currentGreatWorkConstruction?.nodeId||''}-${placementModeKey}-trs${tradeRouteSegmentCount}-${tradeRouteOverlayKey}-${buildingPlacementKey}`;
    if (stateKey === lastWorldStateSingleton) return;
    lastWorldStateSingleton = stateKey;

    // Pre-compute valid placement tiles for Great Work placement mode
    const validPlacementNodeIds = new Set(
      greatWorkPlacement?.validPlacements?.map(p => p.nodeId) || []
    );

    // Pre-compute valid placement tiles for building placement mode
    const validBuildingPlacementTiles = buildingPlacementMode?.validTiles || new Set();
    const isInBuildingPlacementMode = validBuildingPlacementTiles.size > 0;

    // Trade route connected tiles (kingdom is always connected)
    const connectedRouteTiles = stableConnectedToKingdom;
    const tradeRouteSegments = stableTradeRouteSegments;

    // PERF: Persistent tile caching - only remove tiles that no longer exist or are destroyed
    const tileCache = tileCacheRef.current;
    const borderCache = borderCacheRef.current;
    const currentNodeIds = new Set(Object.keys(discoveredNodes));

    // Clean up invalid cache entries (uses module function)
    cleanupTileCache(tileCache, currentNodeIds);
    cleanupBorderCache(borderCache, currentNodeIds);

    // Clear wonder site fade elements and rebuild
    wonderSiteFadeElements = [];
    greatWorkFadeElements = [];

    // PERF: Wonder site and Great Work adjacency now use memoized values from useMemo hooks
    // (wonderSiteTileIds, tilesAdjacentToWonderSites, greatWorkTileIds, tilesAdjacentToGreatWorks)

    // Track elements for each wonder site that should fade
    const wonderSiteFadeMap = new Map(); // wonderSiteId -> array of PIXI elements
    const greatWorkFadeMap = new Map(); // greatWorkNodeId -> array of PIXI elements

    // PERF: Draw discovered tiles using persistent cache
    Object.values(discoveredNodes).forEach(node => {
      const { q, r } = node.coordinates || { q: 0, r: 0 };
      const pos = hexToPixel(q, r, BASE_HEX_SIZE);
      const nodeId = node.id;
      // Treat 'kingdom' node ID as hub even if isHub property is missing
      const isHub = node.isHub || node.id === 'kingdom';
      const biome = isHub ? null : BIOMES?.[node.biome];
      const isCurrentNode = (q === currentCoords.q && r === currentCoords.r);
      const outpostData = !isHub ? stableOutposts[nodeId] : null;
      const hasOutpost = !!outpostData && !outpostData.isConstructing;
      const hasOutpostConstructing = !!outpostData && outpostData.isConstructing;
      const isBeacon = biome?.isTeleportBeacon || isHub;
      const isDungeon = biome?.isDungeonBiome;
      const isKingdom = biome?.isKingdomBiome;
      const hasTileBuilding = stableTileBuildings[node.id];
      const hasTradeRouteSegment = tradeRouteSegments[node.id]?.built;
      const isWonderSite = node.biome === 'wonderSite' || node.wonderSiteId;

      // Check if this tile is part of a fully revealed wonder site (skip icon/label for these)
      const wonderSiteId = node.wonderSiteId;
      const wonderSiteData = wonderSiteId ? wonderSites?.[wonderSiteId] : null;
      const isPartOfFullyRevealedWonderSite = wonderSiteData?.fullyRevealed === true;

      // Check for completed Great Work on this tile (PERF: O(1) lookup using pre-computed map)
      const completedGreatWorkId = nodeIdToGreatWork[node.id];
      const completedGreatWork = completedGreatWorkId ? GREAT_WORKS[completedGreatWorkId] : null;
      const hasCompletedGreatWork = !!completedGreatWork;

      let fillColor = isHub ? 0x5a5550 : (biome?.color ? boostSaturation(colorToNum(biome.color), 1.5) : 0x504a45);
      // Wonder sites use WONDER_SITE_BIOME color
      if (isWonderSite) {
        fillColor = boostSaturation(colorToNum(WONDER_SITE_BIOME.color), 1.5);
      }
      // Completed Great Works get a special purple-tinted fill
      if (hasCompletedGreatWork) {
        fillColor = 0x4a3a5a; // Purple-tinted for Great Works
      }

      // Determine border - special tiles get elevated borders drawn on border layer
      let borderColor = 0x5a5448, borderWidth = 1;
      let needsElevatedBorder = false;
      let borderPriority = 0;

      if (isCurrentNode) {
        borderColor = 0xffd480; borderWidth = 2.5;
        needsElevatedBorder = true; borderPriority = 10; // Highest - player position
      } else if (isHub) {
        borderColor = 0x80c0ff; borderWidth = 2;
        needsElevatedBorder = true; borderPriority = 5;
      } else if (hasCompletedGreatWork) {
        borderColor = 0x8b5cf6; borderWidth = 2.5;
        needsElevatedBorder = true; borderPriority = 4;
      } else if (isWonderSite) {
        borderColor = 0xd4a055; borderWidth = 2.5;
        needsElevatedBorder = true; borderPriority = 3;
      } else if (isKingdom) {
        borderColor = 0xdaa520; borderWidth = 3; // Gold border for kingdoms
        needsElevatedBorder = true; borderPriority = 3.5; // Between wonder sites and great works
      } else if (isDungeon) {
        borderColor = 0xa060d0; borderWidth = 2;
        needsElevatedBorder = true; borderPriority = 2; // Second layer
      } else if (isBeacon) {
        borderColor = 0x60a0d0; borderWidth = 2;
        needsElevatedBorder = true; borderPriority = 2; // Second layer
      } else if (hasOutpost || hasOutpostConstructing) {
        borderColor = 0x80c0ff; borderWidth = 2; // Blue border like Kingdom
        needsElevatedBorder = true; borderPriority = 4; // Same priority as Great Works
      }

      // Great Work placement mode: fade borders/icons/labels on non-valid tiles
      // Building placement mode: fade non-valid tiles, highlight valid ones
      // Trade Route overlay: fade tiles not connected to kingdom (but keep route tiles visible)
      const isOnTradeRoute = connectedRouteTiles.has(node.id) || tradeRouteSegments[node.id]?.built;
      const isValidBuildingPlacement = validBuildingPlacementTiles.has(node.id);
      let placementFadeAlpha = 1.0;
      if (isInPlacementMode && !validPlacementNodeIds.has(node.id)) {
        placementFadeAlpha = 0.15;
      } else if (isInBuildingPlacementMode && !isValidBuildingPlacement && !isHub) {
        placementFadeAlpha = 0.20; // Dim non-valid tiles in building placement mode
      } else if (tradeRouteOverlay && !isOnTradeRoute && !isHub) {
        placementFadeAlpha = 0.20; // Dim non-route tiles in overlay mode
      }

      // PERF: Check if tile needs rebuilding by comparing signature
      const isUnderConstruction = currentGreatWorkConstruction?.nodeId === node.id;
      // PERF: Use base border values for signature since elevated borders are drawn separately
      // This prevents cache invalidation when player moves (isCurrentNode changes)
      const baseBorderColor = needsElevatedBorder ? 0x5a5448 : borderColor;
      const baseBorderWidth = needsElevatedBorder ? 1 : borderWidth;
      const tileSignature = generateTileSignature({
        fillColor,
        borderColor: baseBorderColor,
        borderWidth: baseBorderWidth,
        placementFadeAlpha,
        isCurrentNode: false, // Don't include in signature - elevated border handles this
        hasOutpost,
        hasOutpostConstructing,
        outpostName: outpostData?.name || '',
        hasTileBuilding,
        hasTradeRouteSegment,
        isValidBuildingPlacement,
        isInBuildingPlacementMode,
        isOnTradeRoute,
        tradeRouteOverlay,
        isUnderConstruction,
        hasCompletedGreatWork,
        isPartOfFullyRevealedWonderSite,
      });

      const cachedTile = tileCache.get(nodeId);
      // Pass staticLayer to detect orphaned elements from component remounting
      if (isCacheValid(cachedTile, tileSignature, staticLayer)) {
        // Tile hasn't changed, reuse cached elements (uses module function)
        // CRITICAL: Double-check elements aren't destroyed before reuse (catches race conditions)
        const allElementsValid = cachedTile.elements.every(el => el && !el.destroyed);
        if (allElementsValid) {
          reuseCachedTile(cachedTile, staticLayer);

          // Still need to track fade elements and borders
          const adjacentWonderSiteIds = tilesAdjacentToWonderSites.get(node.id);
          const shouldTrackForWonderFade = adjacentWonderSiteIds && adjacentWonderSiteIds.size > 0;
          const adjacentGreatWorkNodeIds = tilesAdjacentToGreatWorks.get(node.id);
          const shouldTrackForGreatWorkFade = adjacentGreatWorkNodeIds && adjacentGreatWorkNodeIds.size > 0;

          if (shouldTrackForWonderFade && cachedTile.fadeElements) {
            adjacentWonderSiteIds.forEach(wsId => {
              if (!wonderSiteFadeMap.has(wsId)) wonderSiteFadeMap.set(wsId, []);
              wonderSiteFadeMap.get(wsId).push(...cachedTile.fadeElements);
            });
          }
          if (shouldTrackForGreatWorkFade && cachedTile.fadeElements) {
            adjacentGreatWorkNodeIds.forEach(gwNodeId => {
              if (!greatWorkFadeMap.has(gwNodeId)) greatWorkFadeMap.set(gwNodeId, []);
              greatWorkFadeMap.get(gwNodeId).push(...cachedTile.fadeElements);
            });
          }
          return; // Skip recreation
        }
        // Elements became destroyed between cache check and reuse - fall through to rebuild
      }

      // Tile needs rebuilding - collect all elements
      const tileElements = [];
      const tileFadeElements = [];

      // Use layered hex for smooth, polished look - baseBorderColor/Width already calculated above
      const g = createLayeredHex(BASE_HEX_SIZE, fillColor, baseBorderColor, baseBorderWidth, false, placementFadeAlpha);
      g.position.set(pos.x, pos.y);
      staticLayer.addChild(g);
      tileElements.push(g);

      // Building placement mode: add purple glow border for valid placement tiles
      if (isInBuildingPlacementMode && isValidBuildingPlacement) {
        const placementBorder = new PIXI.Graphics();
        placementBorder.poly(getHexPoints(BASE_HEX_SIZE - 1));
        placementBorder.stroke({ color: 0xa855f7, width: 2.5, alpha: 0.85 }); // Purple glow
        placementBorder.position.set(pos.x, pos.y);
        staticLayer.addChild(placementBorder);
        tileElements.push(placementBorder);
      }

      // Trade Route overlay: add gold border for connected route tiles
      if (tradeRouteOverlay && isOnTradeRoute && !isHub) {
        const routeBorder = new PIXI.Graphics();
        routeBorder.poly(getHexPoints(BASE_HEX_SIZE - 1));
        routeBorder.stroke({ color: 0xdab050, width: 2.5, alpha: 0.85 });
        routeBorder.position.set(pos.x, pos.y);
        staticLayer.addChild(routeBorder);
        tileElements.push(routeBorder);
      }

      // Check if this tile is adjacent to any wonder sites (for cinematic fade)
      const adjacentWonderSiteIds = tilesAdjacentToWonderSites.get(node.id);
      const shouldTrackForWonderFade = adjacentWonderSiteIds && adjacentWonderSiteIds.size > 0;

      // Check if this tile is adjacent to any Great Works (for cinematic fade)
      const adjacentGreatWorkNodeIds = tilesAdjacentToGreatWorks.get(node.id);
      const shouldTrackForGreatWorkFade = adjacentGreatWorkNodeIds && adjacentGreatWorkNodeIds.size > 0;

      // Icon - native size with high resolution
      // Skip only if part of fully revealed wonder site (player indicator is on animated layer)
      if (!isPartOfFullyRevealedWonderSite) {
        // Use Great Work icon if completed, else outpost icon, else wonder site icon, else biome icon
        let iconText = isHub ? '🏰' : (biome?.icon || '?');
        if (hasCompletedGreatWork) {
          iconText = completedGreatWork.icon || '🏛️';
        } else if (hasOutpost) {
          iconText = '🏰'; // Completed outpost
        } else if (hasOutpostConstructing) {
          iconText = '🏗️'; // Outpost under construction
        } else if (isWonderSite) {
          iconText = node.siteIcon || WONDER_SITE_BIOME.icon;
        }

        // Great Works get a medallion badge (similar to wonder sites)
        if (hasCompletedGreatWork) {
          const medallionRadius = 22;
          const medallionGraphics = new PIXI.Graphics();
          medallionGraphics.circle(0, 0, medallionRadius);
          medallionGraphics.fill({ color: 0x4a3a5a, alpha: 0.85 * placementFadeAlpha }); // Purple-tinted for Great Works
          medallionGraphics.position.set(pos.x, pos.y);
          staticLayer.addChild(medallionGraphics);
          tileElements.push(medallionGraphics);

          // Great Work icon centered in medallion
          const iconShadow = new PIXI.Text({
            text: iconText,
            style: { fontFamily: 'Arial', fontSize: 20, fill: 0x000000 },
            resolution: TEXT_RESOLUTION,
          });
          iconShadow.anchor.set(0.5);
          iconShadow.alpha = 0.3 * placementFadeAlpha;
          iconShadow.position.set(pos.x + 1, pos.y + 1);
          staticLayer.addChild(iconShadow);
          tileElements.push(iconShadow);

          const iconMain = new PIXI.Text({
            text: iconText,
            style: { fontFamily: 'Arial', fontSize: 20, fill: '#ffffff' },
            resolution: TEXT_RESOLUTION,
          });
          iconMain.anchor.set(0.5);
          iconMain.alpha = placementFadeAlpha;
          iconMain.position.set(pos.x, pos.y);
          staticLayer.addChild(iconMain);
          tileElements.push(iconMain);
        } else {
          // Regular icon rendering for non-Great Work tiles
          const shadow = new PIXI.Text({ resolution: TEXT_RESOLUTION, text: iconText, style: { fontFamily: 'Arial', fontSize: ICON_SCALE * 0.52, fill: 0x000000 } });
          shadow.anchor.set(0.5); shadow.alpha = 0.3 * placementFadeAlpha; shadow.position.set(pos.x + 1, pos.y - BASE_HEX_SIZE * 0.08);
          staticLayer.addChild(shadow);
          tileElements.push(shadow);
          const icon = new PIXI.Text({ resolution: TEXT_RESOLUTION, text: iconText, style: { fontFamily: 'Arial', fontSize: ICON_SCALE * 0.52, fill: 0xffffff } });
          icon.anchor.set(0.5); icon.alpha = placementFadeAlpha; icon.position.set(pos.x, pos.y - BASE_HEX_SIZE * 0.1);
          staticLayer.addChild(icon);
          tileElements.push(icon);

          // Track for cinematic fade if adjacent to wonder site
          if (shouldTrackForWonderFade) {
            tileFadeElements.push(shadow, icon);
            adjacentWonderSiteIds.forEach(wsId => {
              if (!wonderSiteFadeMap.has(wsId)) wonderSiteFadeMap.set(wsId, []);
              wonderSiteFadeMap.get(wsId).push(shadow, icon);
            });
          }

          // Track for cinematic fade if adjacent to Great Work
          if (shouldTrackForGreatWorkFade) {
            tileFadeElements.push(shadow, icon);
            adjacentGreatWorkNodeIds.forEach(gwNodeId => {
              if (!greatWorkFadeMap.has(gwNodeId)) greatWorkFadeMap.set(gwNodeId, []);
              greatWorkFadeMap.get(gwNodeId).push(shadow, icon);
            });
          }
        }
      }

      // Label - ALL CAPS, native size with high resolution
      // Skip if part of fully revealed wonder site (center medallion displays name instead)
      // Skip label for completed Great Works (show only icon, name appears on hover)
      if (!isPartOfFullyRevealedWonderSite && !hasCompletedGreatWork) {
        let labelText = (isHub ? 'Kingdom' : (biome?.name || '')).toUpperCase();
        if (hasOutpost && outpostData?.name) {
          labelText = outpostData.name.toUpperCase();
        } else if (hasOutpostConstructing && outpostData?.name) {
          labelText = outpostData.name.toUpperCase();
        } else if (isWonderSite) {
          labelText = (node.siteName || WONDER_SITE_BIOME.name).toUpperCase();
        }
        const labelShadow = new PIXI.Text({ resolution: TEXT_RESOLUTION, text: labelText, style: { fontFamily: 'serif', fontSize: ICON_SCALE * 0.18, fontWeight: 'bold', fill: 0x000000 } });
        labelShadow.anchor.set(0.5); labelShadow.alpha = 0.5 * placementFadeAlpha; labelShadow.position.set(pos.x + 0.5, pos.y + BASE_HEX_SIZE * 0.44);
        staticLayer.addChild(labelShadow);
        tileElements.push(labelShadow);
        const label = new PIXI.Text({ resolution: TEXT_RESOLUTION, text: labelText, style: { fontFamily: 'serif', fontSize: ICON_SCALE * 0.18, fontWeight: 'bold', fill: 0xfff8e8, stroke: { color: 0x2a2520, width: 1 } } });
        label.anchor.set(0.5); label.alpha = placementFadeAlpha; label.position.set(pos.x, pos.y + BASE_HEX_SIZE * 0.43);
        staticLayer.addChild(label);
        tileElements.push(label);

        // Track for cinematic fade if adjacent to wonder site
        if (shouldTrackForWonderFade) {
          tileFadeElements.push(labelShadow, label);
          adjacentWonderSiteIds.forEach(wsId => {
            if (!wonderSiteFadeMap.has(wsId)) wonderSiteFadeMap.set(wsId, []);
            wonderSiteFadeMap.get(wsId).push(labelShadow, label);
          });
        }

        // Track for cinematic fade if adjacent to Great Work
        if (shouldTrackForGreatWorkFade) {
          tileFadeElements.push(labelShadow, label);
          adjacentGreatWorkNodeIds.forEach(gwNodeId => {
            if (!greatWorkFadeMap.has(gwNodeId)) greatWorkFadeMap.set(gwNodeId, []);
            greatWorkFadeMap.get(gwNodeId).push(labelShadow, label);
          });
        }
      }
      
      // Indicators
      // Outpost constructing indicator (shows hammer icon in corner)
      if (hasOutpostConstructing) {
        const bg = new PIXI.Graphics(); bg.circle(0, 0, ICON_SCALE * 0.18); bg.fill({ color: 0xd4a855, alpha: 0.5 * placementFadeAlpha }); bg.position.set(pos.x + BASE_HEX_SIZE * 0.42, pos.y - BASE_HEX_SIZE * 0.42); staticLayer.addChild(bg);
        tileElements.push(bg);
        const ind = new PIXI.Text({ resolution: TEXT_RESOLUTION, text: '🔨', style: { fontFamily: 'Arial', fontSize: ICON_SCALE * 0.24, fill: 0xffffff } }); ind.anchor.set(0.5); ind.alpha = placementFadeAlpha; ind.position.set(pos.x + BASE_HEX_SIZE * 0.42, pos.y - BASE_HEX_SIZE * 0.42); staticLayer.addChild(ind);
        tileElements.push(ind);
      }
      // Tile building indicator (only if NOT an outpost - outposts show their own main icon)
      if (hasTileBuilding && !hasOutpost && !hasOutpostConstructing) {
        const bg = new PIXI.Graphics(); bg.circle(0, 0, ICON_SCALE * 0.16); bg.fill({ color: 0xd4a855, alpha: 0.4 * placementFadeAlpha }); bg.position.set(pos.x + BASE_HEX_SIZE * 0.38, pos.y + BASE_HEX_SIZE * 0.28); staticLayer.addChild(bg);
        tileElements.push(bg);
        const ind = new PIXI.Text({ resolution: TEXT_RESOLUTION, text: '🏠', style: { fontFamily: 'Arial', fontSize: ICON_SCALE * 0.24, fill: 0xffffff } }); ind.anchor.set(0.5); ind.alpha = placementFadeAlpha; ind.position.set(pos.x + BASE_HEX_SIZE * 0.38, pos.y + BASE_HEX_SIZE * 0.28); staticLayer.addChild(ind);
        tileElements.push(ind);
      }
      if (isBeacon && !isHub && !isCurrentNode) {
        const bg = new PIXI.Graphics(); bg.circle(0, 0, ICON_SCALE * 0.16); bg.fill({ color: 0x5090c0, alpha: 0.5 * placementFadeAlpha }); bg.position.set(pos.x - BASE_HEX_SIZE * 0.42, pos.y - BASE_HEX_SIZE * 0.42); staticLayer.addChild(bg);
        tileElements.push(bg);
        const ind = new PIXI.Text({ resolution: TEXT_RESOLUTION, text: '✦', style: { fontFamily: 'Arial', fontSize: ICON_SCALE * 0.26, fill: 0xc0e8ff } }); ind.anchor.set(0.5); ind.alpha = placementFadeAlpha; ind.position.set(pos.x - BASE_HEX_SIZE * 0.42, pos.y - BASE_HEX_SIZE * 0.42); staticLayer.addChild(ind);
        tileElements.push(ind);
      }
      // Trade route segment indicator (bottom-left corner)
      if (hasTradeRouteSegment && !isHub) {
        const bg = new PIXI.Graphics(); bg.circle(0, 0, ICON_SCALE * 0.16); bg.fill({ color: 0xd4a020, alpha: 0.5 * placementFadeAlpha }); bg.position.set(pos.x - BASE_HEX_SIZE * 0.38, pos.y + BASE_HEX_SIZE * 0.28); staticLayer.addChild(bg);
        tileElements.push(bg);
        const ind = new PIXI.Text({ resolution: TEXT_RESOLUTION, text: '🛤️', style: { fontFamily: 'Arial', fontSize: ICON_SCALE * 0.22, fill: 0xffffff } }); ind.anchor.set(0.5); ind.alpha = placementFadeAlpha; ind.position.set(pos.x - BASE_HEX_SIZE * 0.38, pos.y + BASE_HEX_SIZE * 0.28); staticLayer.addChild(ind);
        tileElements.push(ind);
      }

      // Great Work - Under Construction indicator (isUnderConstruction already computed above for signature)
      if (isUnderConstruction) {
        const constructWork = GREAT_WORKS[currentGreatWorkConstruction.workId];
        // Orange construction border
        const constructBorder = new PIXI.Graphics();
        constructBorder.poly(getHexPoints(BASE_HEX_SIZE - 1));
        constructBorder.stroke({ color: 0xf59e0b, width: 3.5, alpha: 0.9, join: 'round' });
        constructBorder.position.set(pos.x, pos.y);
        staticLayer.addChild(constructBorder);
        tileElements.push(constructBorder);
        // Orange fill for construction tile
        const constructFill = new PIXI.Graphics();
        constructFill.poly(getHexPoints(BASE_HEX_SIZE - 3));
        constructFill.fill({ color: 0xf59e0b, alpha: 0.15 });
        constructFill.position.set(pos.x, pos.y);
        staticLayer.addChild(constructFill);
        tileElements.push(constructFill);
        // Construction icon indicator (top-left)
        const cBg = new PIXI.Graphics(); cBg.circle(0, 0, ICON_SCALE * 0.22); cBg.fill({ color: 0xf59e0b, alpha: 0.6 }); cBg.position.set(pos.x - BASE_HEX_SIZE * 0.38, pos.y - BASE_HEX_SIZE * 0.38); staticLayer.addChild(cBg);
        tileElements.push(cBg);
        const cIcon = new PIXI.Text({ resolution: TEXT_RESOLUTION, text: '🔨', style: { fontFamily: 'Arial', fontSize: ICON_SCALE * 0.28, fill: 0xffffff } }); cIcon.anchor.set(0.5); cIcon.position.set(pos.x - BASE_HEX_SIZE * 0.38, pos.y - BASE_HEX_SIZE * 0.38); staticLayer.addChild(cIcon);
        tileElements.push(cIcon);
        // PERF: Construction timer moved to ticker (animated layer) - updates every second without rebuilding tile
        // Great Work icon (bottom-right)
        if (constructWork) {
          const gwBg = new PIXI.Graphics(); gwBg.circle(0, 0, ICON_SCALE * 0.2); gwBg.fill({ color: 0x8b5cf6, alpha: 0.5 }); gwBg.position.set(pos.x + BASE_HEX_SIZE * 0.35, pos.y + BASE_HEX_SIZE * 0.3); staticLayer.addChild(gwBg);
          const gwIcon = new PIXI.Text({ resolution: TEXT_RESOLUTION, text: constructWork.icon || '🗿', style: { fontFamily: 'Arial', fontSize: ICON_SCALE * 0.26, fill: 0xffffff } }); gwIcon.anchor.set(0.5); gwIcon.position.set(pos.x + BASE_HEX_SIZE * 0.35, pos.y + BASE_HEX_SIZE * 0.3); staticLayer.addChild(gwIcon);
        }
      }

      // Note: Completed Great Works now display their icon/name directly on the tile
      // (handled above in the icon and label rendering sections)

      // PERF: Save tile to cache for reuse (uses module function)
      storeTileInCache(tileCache, nodeId, tileSignature, tileElements, tileFadeElements);
    });

    // === ELEVATED BORDERS ===
    // Moved to separate useEffect below for performance (only updates on player movement)

    // === WONDER SITE GLOW ===
    // Draw glowing hex borders around each tile of discovered wonder sites
    // Color based on wonder tier/theme
    const WONDER_GLOW_COLORS = {
      // Tier 1 - warm earthy tones
      stonehenge: 0x8b7355,      // stone brown
      great_bonfire: 0xff6b35,   // fire orange
      tribal_monument: 0x7c9a5e, // forest green
      // Tier 2 - richer colors
      great_granary: 0xdaa520,   // golden wheat
      stone_citadel: 0x708090,   // slate gray
      sacred_grove: 0x228b22,    // forest green
      // Tier 3 - vibrant
      hanging_gardens: 0x32cd32, // lime green
      great_library: 0x4169e1,   // royal blue
      colosseum: 0xcd853f,       // peru/sand
      // Default fallback
      default: 0xd4a055,         // golden amber
    };

    // PERF: Clean up wonder site graphics for sites that no longer exist
    const wonderSiteCache = wonderSiteGraphicsRef.current;
    const currentWonderSiteIds = new Set(discoveredWonderSites);
    for (const wonderId of wonderSiteCache.keys()) {
      if (!currentWonderSiteIds.has(wonderId)) {
        const cached = wonderSiteCache.get(wonderId);
        cached.elements.forEach(el => {
          if (el.parent) el.parent.removeChild(el);
          el.destroy();
        });
        wonderSiteCache.delete(wonderId);
      }
    }

    discoveredWonderSites.forEach(wonderId => {
      const site = wonderSites[wonderId];
      if (!site || !site.fullyRevealed) return;

      const wonder = WONDERS[wonderId];
      if (!wonder) return;
      const glowColor = WONDER_GLOW_COLORS[wonderId] || WONDER_GLOW_COLORS.default;

      // Get pixel positions for all 3 tiles
      const positions = site.tileDefinitions.map(tileDef => {
        const { q, r } = tileDef.coordinates;
        return hexToPixel(q, r, BASE_HEX_SIZE);
      });
      if (positions.length !== 3) return;

      // Calculate triangle center for medallion
      const centerX = (positions[0].x + positions[1].x + positions[2].x) / 3;
      const centerY = (positions[0].y + positions[1].y + positions[2].y) / 3;

      // Wonder sites can't be valid placements, so always apply fade in placement mode
      const wonderFadeAlpha = isInPlacementMode ? 0.15 : 1.0;

      // PERF: Check if cached wonder site graphics exist and are still valid
      const signature = `${wonderId}-${wonderFadeAlpha}`;
      const cached = wonderSiteCache.get(wonderId);
      if (cached && cached.signature === signature) {
        // Reuse cached graphics - just ensure they're in the layer
        cached.elements.forEach(el => {
          if (el.parent !== staticLayer) {
            staticLayer.addChild(el);
          }
        });
        return; // Skip recreation
      }

      // Remove old cached graphics if they exist
      if (cached) {
        cached.elements.forEach(el => {
          if (el.parent) el.parent.removeChild(el);
          el.destroy();
        });
      }

      // Create new wonder site graphics
      const elements = [];
      const wonderGraphics = new PIXI.Graphics();

      // Draw glowing hex border around each tile
      // hexPoints is flat array: [x0, y0, x1, y1, x2, y2, ...]
      positions.forEach(pos => {
        const hexPoints = getHexPoints(BASE_HEX_SIZE);

        // Outer glow (soft, wider)
        wonderGraphics.moveTo(pos.x + hexPoints[0], pos.y + hexPoints[1]);
        for (let i = 1; i < 6; i++) {
          wonderGraphics.lineTo(pos.x + hexPoints[i * 2], pos.y + hexPoints[i * 2 + 1]);
        }
        wonderGraphics.closePath();
        wonderGraphics.stroke({ width: 6, color: glowColor, alpha: 0.25 * wonderFadeAlpha });

        // Inner glow (brighter, thinner)
        wonderGraphics.moveTo(pos.x + hexPoints[0], pos.y + hexPoints[1]);
        for (let i = 1; i < 6; i++) {
          wonderGraphics.lineTo(pos.x + hexPoints[i * 2], pos.y + hexPoints[i * 2 + 1]);
        }
        wonderGraphics.closePath();
        wonderGraphics.stroke({ width: 2.5, color: glowColor, alpha: 0.6 * wonderFadeAlpha });
      });

      // === MEDALLION ON EACH TILE ===
      // Draw circular medallion with icon on each of the 3 tiles
      const medallionRadius = 22; // Larger medallions

      staticLayer.addChild(wonderGraphics);
      elements.push(wonderGraphics);

      positions.forEach(pos => {
        const medallionGraphics = new PIXI.Graphics();

        // Medallion background - lighter gray, no border
        medallionGraphics.circle(0, 0, medallionRadius);
        medallionGraphics.fill({ color: 0x4a4540, alpha: 0.85 * wonderFadeAlpha }); // Lighter gray

        medallionGraphics.position.set(pos.x, pos.y);
        staticLayer.addChild(medallionGraphics);
        elements.push(medallionGraphics);

        // Wonder icon centered in medallion
        if (wonder?.icon) {
          const iconShadow = new PIXI.Text({
            text: wonder.icon,
            style: { fontFamily: 'Arial', fontSize: 20, fill: 0x000000 },
            resolution: TEXT_RESOLUTION,
          });
          iconShadow.anchor.set(0.5);
          iconShadow.alpha = 0.3 * wonderFadeAlpha;
          iconShadow.position.set(pos.x + 1, pos.y + 1);
          staticLayer.addChild(iconShadow);
          elements.push(iconShadow);

          const iconText = new PIXI.Text({
            text: wonder.icon,
            style: { fontFamily: 'Arial', fontSize: 20, fill: '#ffffff' },
            resolution: TEXT_RESOLUTION,
          });
          iconText.anchor.set(0.5);
          iconText.alpha = wonderFadeAlpha;
          iconText.position.set(pos.x, pos.y);
          staticLayer.addChild(iconText);
          elements.push(iconText);
        }
      });

      // Store in cache for reuse
      wonderSiteCache.set(wonderId, { signature, elements });
    });

    // Finalize wonder site fade elements for ticker access
    wonderSiteFadeElements = Array.from(wonderSiteFadeMap.entries()).map(([siteId, elements]) => {
      const site = wonderSites[siteId];
      const siteTiles = new Set(site?.tileDefinitions?.map(t => `${t.coordinates.q},${t.coordinates.r}`) || []);
      return { siteId, elements, siteTiles };
    });

    // Finalize Great Work fade elements for ticker access
    greatWorkFadeElements = Array.from(greatWorkFadeMap.entries()).map(([nodeId, elements]) => {
      const node = discoveredNodes[nodeId];
      const { q, r } = node?.coordinates || { q: 0, r: 0 };
      return { nodeId, elements, workTileKey: `${q},${r}` };
    });
  }, [stableDiscoveredNodes, stableOutposts, stableTileBuildings, stableDiscoveredWonderSites, stableWonderSites, stableTradeRouteSegments, stableConnectedToKingdom, BIOMES, pixiReady, greatWorkRemaining, currentGreatWorkConstruction, greatWorkLocations, getGreatWorkProgressInfo, greatWorkPlacement, wonderSiteTileIds, tilesAdjacentToWonderSites, greatWorkTileIds, tilesAdjacentToGreatWorks, nodeIdToGreatWork, tradeRouteOverlay, buildingPlacementMode]);

  // PERF: Store border graphics persistently and only update player border on movement
  // This prevents redrawing ALL borders when player moves
  const borderCacheByNodeRef = useRef(borderCacheByNodeSingleton); // Uses module singleton

  useEffect(() => {
    const borderLayer = borderLayerRef.current;
    if (!borderLayer || !pixiReady) return;

    const discoveredNodes = stableDiscoveredNodes;
    const borderCache = borderCacheByNodeRef.current;
    const currentCoords = getCurrentCoords();

    // Remove borders for nodes that no longer exist
    const currentNodeIds = new Set(Object.keys(discoveredNodes));
    for (const nodeId of borderCache.keys()) {
      if (!currentNodeIds.has(nodeId)) {
        const graphics = borderCache.get(nodeId);
        if (graphics.parent) graphics.parent.removeChild(graphics);
        graphics.destroy();
        borderCache.delete(nodeId);
      }
    }

    // Update or create borders for all discovered nodes
    Object.values(discoveredNodes).forEach(node => {
      const { q, r } = node.coordinates || { q: 0, r: 0 };
      const pos = hexToPixel(q, r, BASE_HEX_SIZE);
      const nodeId = node.id;
      const isHub = node.isHub || node.id === 'kingdom';
      const biome = isHub ? null : BIOMES?.[node.biome];
      const isCurrentNode = (q === currentCoords.q && r === currentCoords.r);
      const isDungeon = biome?.isDungeonBiome;
      const isKingdom = biome?.isKingdomBiome;
      const isBeacon = biome?.isTeleportBeacon || isHub;
      const isWonderSite = node.biome === 'wonderSite' || node.wonderSiteId;
      const completedGreatWorkId = nodeIdToGreatWork[node.id];
      const hasCompletedGreatWork = !!completedGreatWorkId;
      // Check for outpost on this tile
      const outpostData = !isHub ? stableOutposts[nodeId] : null;
      const hasOutpost = !!outpostData && !outpostData.isConstructing;
      const hasOutpostConstructing = !!outpostData && outpostData.isConstructing;

      let borderColor = 0x5a5448, borderWidth = 1;
      let needsElevatedBorder = false;
      let borderPriority = 0;

      if (isCurrentNode) {
        borderColor = 0xffd480; borderWidth = 2.5;
        needsElevatedBorder = true; borderPriority = 10;
      } else if (isHub) {
        borderColor = 0x80c0ff; borderWidth = 2;
        needsElevatedBorder = true; borderPriority = 5;
      } else if (hasCompletedGreatWork) {
        borderColor = 0x8b5cf6; borderWidth = 2.5;
        needsElevatedBorder = true; borderPriority = 4;
      } else if (hasOutpost || hasOutpostConstructing) {
        borderColor = 0x80c0ff; borderWidth = 2; // Blue border like Kingdom
        needsElevatedBorder = true; borderPriority = 4;
      } else if (isWonderSite) {
        borderColor = 0xd4a055; borderWidth = 2.5;
        needsElevatedBorder = true; borderPriority = 3;
      } else if (isKingdom) {
        borderColor = 0xdaa520; borderWidth = 3; // Gold border for kingdoms
        needsElevatedBorder = true; borderPriority = 3.5; // Between wonder sites and great works
      } else if (isDungeon) {
        borderColor = 0xa060d0; borderWidth = 2;
        needsElevatedBorder = true; borderPriority = 2;
      } else if (isBeacon) {
        borderColor = 0x60a0d0; borderWidth = 2;
        needsElevatedBorder = true; borderPriority = 2;
      }

      if (!needsElevatedBorder) {
        // Remove border if it exists but is no longer needed
        if (borderCache.has(nodeId)) {
          const graphics = borderCache.get(nodeId);
          if (graphics.parent) graphics.parent.removeChild(graphics);
          graphics.destroy();
          borderCache.delete(nodeId);
        }
        return;
      }

      // Check if border needs updating
      const cached = borderCache.get(nodeId);
      const signature = `${borderColor}-${borderWidth}-${isCurrentNode ? 1 : 0}-${borderPriority}`;

      if (cached && cached._borderSignature === signature) {
        // Border hasn't changed, just ensure it's in the layer
        if (!cached.parent) borderLayer.addChild(cached);
        return;
      }

      // Remove old border if exists
      if (cached) {
        if (cached.parent) cached.parent.removeChild(cached);
        cached.destroy();
      }

      // Create new border
      const borderGraphics = createHexBorder(BASE_HEX_SIZE, borderColor, borderWidth, isCurrentNode, 1);
      borderGraphics.position.set(pos.x, pos.y);
      borderGraphics._borderSignature = signature;
      borderGraphics._borderPriority = borderPriority;
      borderLayer.addChild(borderGraphics);
      borderCache.set(nodeId, borderGraphics);
    });

    // Sort border layer children by priority (z-index)
    borderLayer.children.sort((a, b) => (a._borderPriority || 0) - (b._borderPriority || 0));
  }, [stableCurrentNode, stableDiscoveredNodes, BIOMES, pixiReady, nodeIdToGreatWork, greatWorkPlacement, buildingPlacementMode, stableOutposts]);

  // Build animated objects (including fog tiles now)
  useEffect(() => {
    const animatedLayer = animatedLayerRef.current, fogLayer = fogLayerRef.current, pathLayer = pathLayerRef.current, particleContainer = particleContainerRef.current, tex = texturesRef.current;
    if (!animatedLayer || !fogLayer || !pathLayer || !particleContainer || !tex.ready || !pixiReady) return;
    
    const currentCoords = getCurrentCoords();
    const discoveredNodes = worldMap?.discoveredNodes || {};
    
    // Collect ALL unexplored tiles
    const allUnexplored = new Map();
    Object.values(discoveredNodes).forEach(node => {
      const { q, r } = node.coordinates || { q: 0, r: 0 };
      HEX_DIRECTIONS.forEach(dir => {
        const adjQ = q + dir.q, adjR = r + dir.r, adjId = `node_${adjQ}_${adjR}`;
        if (!discoveredNodes[adjId] && !(adjQ === 0 && adjR === 0)) allUnexplored.set(adjId, { q: adjQ, r: adjR });
      });
    });
    
    const adjacentIds = new Set();
    HEX_DIRECTIONS.forEach(dir => adjacentIds.add(`node_${currentCoords.q + dir.q}_${currentCoords.r + dir.r}`));

    // PERF: Simplified state key - use counts instead of joining all IDs (avoids O(n) string allocation)
    const validPlacementCount = greatWorkPlacement?.validPlacements?.length || 0;
    const tradeRouteSegments = worldMap?.tradeRoutes?.segments || {};
    const tradeRouteSegmentCount = Object.keys(tradeRouteSegments).length;
    const stateKey = `${currentCoords.q},${currentCoords.r},${adjacentIds.size},${allUnexplored.size},${beaconNodes.length},${clickedTile?.q ?? ''},${clickedTile?.r ?? ''},${pathPreview?.targetQ ?? ''},${pathPreview?.targetR ?? ''},${pathPreview?.teleportFrom?.q ?? ''},${pathPreview?.path?.length ?? 0},${validPlacementCount},tr${tradeRouteOverlay ? 1 : 0}-${tradeRouteSegmentCount}`;
    if (stateKey === lastAnimatedStateSingleton) return;
    lastAnimatedStateSingleton = stateKey;

    animatedLayer.removeChildren();
    fogLayer.removeChildren();
    pathLayer.removeChildren();
    particleContainer.removeParticles();

    const pool = animatedPoolRef.current;
    pool.adjacentTiles = []; pool.fogTiles = []; pool.beacons = []; pool.kingdom = null; pool.player = null; pool.clickHighlight = null; pool.pathSegments = []; pool.pathTarget = null; pool.teleport = null; pool.sparkles = []; pool.hoverTile = null; pool.movementGhost = null; pool.etaLabel = null; pool.distanceRings = null; pool.gatherEffects = null; pool.validPlacements = []; pool.revealOverlays = []; pool.tradeRouteLines = null;
    beaconLookupMap.clear();
    adjacentLookupMap.clear();
    
    const createGlow = (texture, x, y, scale) => {
      const s = new PIXI.Sprite(texture); s.anchor.set(0.5); s.position.set(x, y); s.scale.set(scale); s.blendMode = 'add'; return s;
    };
    
    // Distance rings around player - smooth circles
    const playerPos = hexToPixel(currentCoords.q, currentCoords.r, BASE_HEX_SIZE);
    pool.distanceRings = new PIXI.Graphics();
    for (let i = 1; i <= 3; i++) {
      const radius = BASE_HEX_SIZE * 2.2 * i;
      pool.distanceRings.circle(playerPos.x, playerPos.y, radius);
      pool.distanceRings.stroke({ color: 0x6a6255, width: 1.5, alpha: 0.1 / i });
    }
    animatedLayer.addChild(pool.distanceRings);
    
    // NON-ADJACENT FOG TILES (breathing animation)
    allUnexplored.forEach(({ q, r }, id) => {
      if (adjacentIds.has(id)) return; // Skip adjacent - they get special treatment

      const pos = hexToPixel(q, r, BASE_HEX_SIZE);
      const dist = hexDistance(q, r, currentCoords.q, currentCoords.r);
      
      // Create smooth fog hex - DARKER and more opaque for stronger fog of war
      const fogHex = new PIXI.Graphics();
      fogHex.poly(getHexPoints(BASE_HEX_SIZE - 2));
      fogHex.fill({ color: 0x1a1816, alpha: 0.95 }); // Much darker base
      fogHex.poly(getHexPoints(BASE_HEX_SIZE - 6));
      fogHex.fill({ color: 0x2a2824, alpha: 0.5 }); // Darker inner gradient
      fogHex.poly(getHexPoints(BASE_HEX_SIZE - 2));
      fogHex.stroke({ color: 0x3a3530, width: 1, alpha: 0.6, join: 'round' }); // Darker border
      fogHex.position.set(pos.x, pos.y);
      fogLayer.addChild(fogHex);

      // Mysterious fog overlay with subtle noise effect
      const fogOverlay = new PIXI.Graphics();
      fogOverlay.poly(getHexPoints(BASE_HEX_SIZE - 4));
      fogOverlay.fill({ color: 0x0d0c0a, alpha: 0.3 }); // Extra dark layer
      fogOverlay.position.set(pos.x, pos.y);
      fogLayer.addChild(fogOverlay);

      const fogText = new PIXI.Text({ resolution: TEXT_RESOLUTION, text: '?', style: { fontFamily: 'serif', fontSize: ICON_SCALE * 0.45, fontWeight: 'bold', fill: 0x504840 } });
      fogText.anchor.set(0.5);
      fogText.alpha = 0.15; // More subtle
      fogText.position.set(pos.x, pos.y);
      fogLayer.addChild(fogText);

      pool.fogTiles.push({ q, r, pos, dist, hex: fogHex, overlay: fogOverlay, text: fogText });
    });
    
    // ADJACENT UNEXPLORED - darker, more mysterious, subtle glow
    Array.from(adjacentIds).forEach(id => {
      if (discoveredNodes[id]) return; // Already discovered
      const match = id.match(/node_(-?\d+)_(-?\d+)/);
      if (!match) return;
      const q = parseInt(match[1]), r = parseInt(match[2]);
      if (q === 0 && r === 0) return; // Skip kingdom
      
      const pos = hexToPixel(q, r, BASE_HEX_SIZE);
      
      // Create smooth adjacent hex - DARKER fog but with gold border showing explorable
      const adjHex = new PIXI.Graphics();
      adjHex.poly(getHexPoints(BASE_HEX_SIZE - 2));
      adjHex.fill({ color: 0x1d1a16, alpha: 0.92 }); // Darker base
      adjHex.poly(getHexPoints(BASE_HEX_SIZE - 6));
      adjHex.fill({ color: 0x2a2622, alpha: 0.5 }); // Darker inner gradient
      adjHex.poly(getHexPoints(BASE_HEX_SIZE - 2));
      adjHex.stroke({ color: 0xfbbf24, width: 1.5, alpha: 0.5, join: 'round' }); // Keep gold border visible
      adjHex.position.set(pos.x, pos.y);

      // Add subtle fog overlay
      const adjOverlay = new PIXI.Graphics();
      adjOverlay.poly(getHexPoints(BASE_HEX_SIZE - 4));
      adjOverlay.fill({ color: 0x0d0c0a, alpha: 0.25 }); // Extra dark layer
      adjOverlay.position.set(pos.x, pos.y);

      const adjText = new PIXI.Text({ resolution: TEXT_RESOLUTION, text: '?', style: { fontFamily: 'serif', fontSize: ICON_SCALE * 0.48, fontWeight: 'bold', fill: 0x808090 } });
      adjText.anchor.set(0.5);
      adjText.alpha = 0.35; // More subtle
      adjText.position.set(pos.x, pos.y);
      
      // Much subtler glow - use blue/purple tint for mystery instead of gold
      const tile = {
        q, r, pos,
        outer: createGlow(tex.purpleGlow, pos.x, pos.y, 1.8),
        inner: createGlow(tex.purpleGlow, pos.x, pos.y, 1.2),
        hex: adjHex,
        overlay: adjOverlay,
        text: adjText
      };

      // Reduce glow visibility
      tile.outer.alpha = 0.01;
      tile.inner.alpha = 0.015;
      animatedLayer.addChild(tile.outer);
      animatedLayer.addChild(tile.inner);
      animatedLayer.addChild(tile.hex);
      animatedLayer.addChild(tile.overlay);
      animatedLayer.addChild(tile.text);
      
      // Fewer, subtler sparkles
      for (let i = 0; i < 2; i++) {
        const sp = new PIXI.Sprite(tex.sparkle); 
        sp.anchor.set(0.5); 
        sp.scale.set(0.1); 
        sp.position.set(pos.x, pos.y); 
        sp.alpha = 0;
        sp.tint = 0xa0a0c0; // Cooler tint
        particleContainer.addParticle(sp);
        pool.sparkles.push({ sprite: sp, tileQ: q, tileR: r, index: i, type: 'adjacent' });
      }
      pool.adjacentTiles.push(tile);
      adjacentLookupMap.set(`${q},${r}`, tile);
    });
    
    // Beacons
    beaconNodes.forEach(node => {
      const { q, r } = node.coordinates || { q: 0, r: 0 };
      const pos = hexToPixel(q, r, BASE_HEX_SIZE);
      const beacon = { q, r, pos, outer: createGlow(tex.blueGlow, pos.x, pos.y, 3.0), mid: createGlow(tex.blueGlow, pos.x, pos.y, 2.2), inner: createGlow(tex.blueGlow, pos.x, pos.y, 1.5) };
      animatedLayer.addChild(beacon.outer); animatedLayer.addChild(beacon.mid); animatedLayer.addChild(beacon.inner);
      for (let i = 0; i < 4; i++) {
        const sp = new PIXI.Sprite(tex.sparkle); sp.anchor.set(0.5); sp.scale.set(0.125); sp.tint = 0xc0e8ff;
        particleContainer.addParticle(sp);
        pool.sparkles.push({ sprite: sp, tileQ: q, tileR: r, index: i, type: 'beacon' });
      }
      pool.beacons.push(beacon);
      beaconLookupMap.set(`${q},${r}`, beacon);
    });
    
    // Kingdom
    if (!(currentCoords.q === 0 && currentCoords.r === 0)) {
      const kPos = hexToPixel(0, 0, BASE_HEX_SIZE);
      pool.kingdom = { pos: kPos, outer: createGlow(tex.blueGlow, kPos.x, kPos.y, 3.2), mid: createGlow(tex.blueGlow, kPos.x, kPos.y, 2.4), inner: createGlow(tex.blueGlow, kPos.x, kPos.y, 1.6) };
      animatedLayer.addChild(pool.kingdom.outer); animatedLayer.addChild(pool.kingdom.mid); animatedLayer.addChild(pool.kingdom.inner);
    }

    // Valid Great Work placement tiles (only show tiles within 5 hex distance from player)
    const GREAT_WORK_PLACEMENT_RANGE = 5;
    if (greatWorkPlacement?.validPlacements?.length > 0) {
      greatWorkPlacement.validPlacements.forEach(({ nodeId }) => {
        // Parse coordinates from nodeId
        const match = nodeId.match(/node_(-?\d+)_(-?\d+)/);
        if (!match) return;
        const q = parseInt(match[1]), r = parseInt(match[2]);

        // Skip kingdom tile (0,0) - cannot build Great Works there
        if (q === 0 && r === 0) return;

        // Only show tiles within range of player
        const distanceFromPlayer = hexDistanceUtil(currentCoords.q, currentCoords.r, q, r);
        if (distanceFromPlayer > GREAT_WORK_PLACEMENT_RANGE) return;

        const pos = hexToPixel(q, r, BASE_HEX_SIZE);

        // Create solid BRIGHT BLUE fill covering the entire tile
        const hexFill = new PIXI.Graphics();
        hexFill.poly(getHexPoints(BASE_HEX_SIZE - 2));
        hexFill.fill({ color: 0x2563eb, alpha: 0.6 }); // Brighter electric blue (blue-600)
        hexFill.position.set(pos.x, pos.y);

        // Create visible BLUE hex border for valid placement
        const hexBorder = new PIXI.Graphics();
        hexBorder.poly(getHexPoints(BASE_HEX_SIZE - 1));
        hexBorder.stroke({ color: 0x3b82f6, width: 3, alpha: 0.8, join: 'round' }); // Slightly less prominent border
        hexBorder.position.set(pos.x, pos.y);

        // Add a center icon (faded into background)
        const placeIcon = new PIXI.Text({
          resolution: TEXT_RESOLUTION,
          text: '✦',
          style: { fontFamily: 'Arial', fontSize: ICON_SCALE * 0.4, fill: 0xffffff }
        });
        placeIcon.anchor.set(0.5);
        placeIcon.position.set(pos.x, pos.y);
        placeIcon.alpha = 0.3; // Faded icon

        const tile = {
          q, r, pos, distanceFromPlayer,
          outer: createGlow(tex.blueGlow, pos.x, pos.y, 3.0),
          inner: createGlow(tex.blueGlow, pos.x, pos.y, 2.0),
          border: hexBorder,
          fill: hexFill,
          icon: placeIcon,
        };

        tile.outer.alpha = 0.12;
        tile.inner.alpha = 0.18;
        animatedLayer.addChild(tile.outer);
        animatedLayer.addChild(tile.inner);
        animatedLayer.addChild(tile.fill);
        animatedLayer.addChild(tile.border);
        animatedLayer.addChild(tile.icon);

        pool.validPlacements.push(tile);
      });
    }

    // REVEAL OVERLAYS - fog lifting animation for newly discovered tiles
    const revealingNow = revealingTilesRef.current;
    Object.entries(revealingNow).forEach(([nodeId, startTime]) => {
      const node = discoveredNodes[nodeId];
      if (!node?.coordinates) return;
      const { q, r } = node.coordinates;
      const pos = hexToPixel(q, r, BASE_HEX_SIZE);

      // Create fog overlay that will fade out
      const revealFog = new PIXI.Graphics();
      revealFog.poly(getHexPoints(BASE_HEX_SIZE - 1));
      revealFog.fill({ color: 0x1a1816, alpha: 1 });
      revealFog.position.set(pos.x, pos.y);
      animatedLayer.addChild(revealFog);

      // Shimmer/glow effect
      const revealGlow = new PIXI.Graphics();
      revealGlow.poly(getHexPoints(BASE_HEX_SIZE + 4));
      revealGlow.fill({ color: 0xdab050, alpha: 0 }); // Gold glow
      revealGlow.position.set(pos.x, pos.y);
      animatedLayer.addChild(revealGlow);

      pool.revealOverlays.push({ q, r, pos, startTime, fog: revealFog, glow: revealGlow });
    });

    // Player indicator with breathing effect - smooth ring + character icon (crown)
    // Icon only shows when NOT at kingdom
    const isAtKingdom = currentCoords.q === 0 && currentCoords.r === 0;
    const playerIcon = new PIXI.Text({ resolution: TEXT_RESOLUTION, text: '👑', style: { fontFamily: 'serif', fontSize: ICON_SCALE * 0.6, fontWeight: 'normal' } });
    playerIcon.anchor.set(0.5);
    // Match tile icon positioning for perfect centering (same offset as biome icons)
    playerIcon.position.set(playerPos.x, playerPos.y - BASE_HEX_SIZE * 0.1);

    // Detect player position change and start movement animation
    if (playerMoveAnimation.lastCoords.q !== currentCoords.q || playerMoveAnimation.lastCoords.r !== currentCoords.r) {
      // Player moved to a new tile - start animation from last position
      const lastPos = hexToPixel(playerMoveAnimation.lastCoords.q, playerMoveAnimation.lastCoords.r, BASE_HEX_SIZE);
      const distance = hexDistanceUtil(playerMoveAnimation.lastCoords.q, playerMoveAnimation.lastCoords.r, currentCoords.q, currentCoords.r);

      // Teleport if distance > 1 (not adjacent)
      const isTeleport = distance > 1;

      playerMoveAnimation.isAnimating = true;
      playerMoveAnimation.isTeleport = isTeleport;
      playerMoveAnimation.startPos = { x: lastPos.x, y: lastPos.y };
      playerMoveAnimation.targetPos = { x: playerPos.x, y: playerPos.y };
      playerMoveAnimation.progress = 0;
      playerMoveAnimation.duration = isTeleport ? 0.3 : 0.45; // Teleport is faster
      playerMoveAnimation.lastCoords = { q: currentCoords.q, r: currentCoords.r };

      if (isTeleport) {
        playerMoveAnimation.teleportTrail = 1.0; // Start trail fade
      }
    }

    pool.player = {
      pos: playerPos,
      targetPos: playerPos, // Store target for change detection
      outer: createGlow(tex.goldGlow, playerPos.x, playerPos.y, 2.8),
      mid: createGlow(tex.goldGlow, playerPos.x, playerPos.y, 2.0),
      inner: createGlow(tex.goldGlow, playerPos.x, playerPos.y, 1.4),
      ring: new PIXI.Graphics(),
      icon: playerIcon,
      isAtKingdom: isAtKingdom,
      teleportTrail: new PIXI.Graphics(), // Trail line for teleport animation
      shockwaveRing: new PIXI.Graphics(), // Shockwave ring for teleport arrival
      burstParticles: new PIXI.Graphics() // Particle burst graphics
    };
    animatedLayer.addChild(pool.player.outer); animatedLayer.addChild(pool.player.mid); animatedLayer.addChild(pool.player.inner);
    pool.player.ring.circle(0, 0, BASE_HEX_SIZE * 1.05); pool.player.ring.stroke({ color: 0xffd060, width: 2.5, alpha: 0.5 }); pool.player.ring.position.set(playerPos.x, playerPos.y);
    animatedLayer.addChild(pool.player.ring);
    animatedLayer.addChild(pool.player.shockwaveRing); // Add shockwave below player
    animatedLayer.addChild(pool.player.burstParticles); // Add burst particles
    animatedLayer.addChild(pool.player.teleportTrail); // Add trail to animated layer
    animatedLayer.addChild(pool.player.icon);
    for (let i = 0; i < 6; i++) {
      const sp = new PIXI.Sprite(tex.sparkle); sp.anchor.set(0.5); sp.scale.set(0.175); sp.tint = 0xffd870;
      particleContainer.addParticle(sp);
      pool.sparkles.push({ sprite: sp, index: i, type: 'player', baseX: playerPos.x, baseY: playerPos.y });
    }

    // Gather effects
    pool.gatherEffects = {
      tileGlow: new PIXI.Graphics(),
      particles: new PIXI.Graphics()
    };
    animatedLayer.addChild(pool.gatherEffects.tileGlow);
    animatedLayer.addChild(pool.gatherEffects.particles);

    // Hover highlight - smooth ring (always created, visibility controlled by ticker)
    pool.hoverTile = { ring: new PIXI.Graphics() };
    pool.hoverTile.ring.poly(getHexPoints(BASE_HEX_SIZE - 1));
    pool.hoverTile.ring.stroke({ color: 0xffffff, width: 2, alpha: 0.3, join: 'round' });
    pool.hoverTile.ring.visible = false;
    animatedLayer.addChild(pool.hoverTile.ring);
    
    // Click highlight - smooth ring with glow
    if (clickedTile) {
      const clickPos = hexToPixel(clickedTile.q, clickedTile.r, BASE_HEX_SIZE);
      pool.clickHighlight = { pos: clickPos, glow: createGlow(tex.purpleGlow, clickPos.x, clickPos.y, 2.2), ring: new PIXI.Graphics() };
      animatedLayer.addChild(pool.clickHighlight.glow);
      pool.clickHighlight.ring.poly(getHexPoints(BASE_HEX_SIZE));
      pool.clickHighlight.ring.stroke({ color: 0xd0b0f0, width: 2.5, alpha: 0.7, join: 'round' });
      pool.clickHighlight.ring.position.set(clickPos.x, clickPos.y);
      animatedLayer.addChild(pool.clickHighlight.ring);
    }
    
    // Path preview
    if (pathPreview?.path?.length > 0) {
      const hasTeleport = pathPreview.teleportFrom?.q !== undefined;
      let startPos = hasTeleport ? hexToPixel(pathPreview.teleportFrom.q, pathPreview.teleportFrom.r, BASE_HEX_SIZE) : hexToPixel(currentCoords.q, currentCoords.r, BASE_HEX_SIZE);
      
      if (hasTeleport) {
        pool.teleport = { pos: startPos, outer: createGlow(tex.purpleGlow, startPos.x, startPos.y, 3.5), mid: createGlow(tex.purpleGlow, startPos.x, startPos.y, 2.5), inner: createGlow(tex.purpleGlow, startPos.x, startPos.y, 1.6), ring: new PIXI.Graphics(), icon: new PIXI.Text({ resolution: TEXT_RESOLUTION, text: '✦', style: { fontFamily: 'Arial', fontSize: ICON_SCALE * 0.65, fontWeight: 'bold', fill: 0xfff0ff } }), label: new PIXI.Text({ resolution: TEXT_RESOLUTION, text: 'TELEPORT', style: { fontFamily: 'serif', fontSize: ICON_SCALE * 0.24, fontWeight: 'bold', fill: 0xf0d8ff } }), numGlow: new PIXI.Graphics(), numBg: new PIXI.Graphics(), numText: new PIXI.Text({ resolution: TEXT_RESOLUTION, text: '1', style: { fontFamily: 'serif', fontSize: ICON_SCALE * 0.32, fontWeight: 'bold', fill: 0xffffff } }) };
        pathLayer.addChild(pool.teleport.outer); pathLayer.addChild(pool.teleport.mid); pathLayer.addChild(pool.teleport.inner);
        pool.teleport.ring.circle(0, 0, BASE_HEX_SIZE * 1.1); pool.teleport.ring.stroke({ color: 0xd0a0ff, width: 2, alpha: 0.7 }); pool.teleport.ring.position.set(startPos.x, startPos.y); pathLayer.addChild(pool.teleport.ring);
        pool.teleport.icon.anchor.set(0.5); pool.teleport.icon.position.set(startPos.x, startPos.y - BASE_HEX_SIZE * 0.55); pathLayer.addChild(pool.teleport.icon);
        pool.teleport.label.anchor.set(0.5); pool.teleport.label.position.set(startPos.x, startPos.y + BASE_HEX_SIZE * 0.78); pathLayer.addChild(pool.teleport.label);
        // Step number for teleport - positioned top-right
        pool.teleport.numGlow.circle(0, 0, ICON_SCALE * 0.32); pool.teleport.numGlow.fill({ color: 0x9060d0, alpha: 0.45 }); pool.teleport.numGlow.position.set(startPos.x + BASE_HEX_SIZE * 0.55, startPos.y - BASE_HEX_SIZE * 0.55); pathLayer.addChild(pool.teleport.numGlow);
        pool.teleport.numBg.circle(0, 0, ICON_SCALE * 0.25); pool.teleport.numBg.fill({ color: 0xc090ff, alpha: 0.95 }); pool.teleport.numBg.circle(0, 0, ICON_SCALE * 0.25); pool.teleport.numBg.stroke({ color: 0xffffff, width: 1, alpha: 0.3 }); pool.teleport.numBg.position.set(startPos.x + BASE_HEX_SIZE * 0.55, startPos.y - BASE_HEX_SIZE * 0.55); pathLayer.addChild(pool.teleport.numBg);
        pool.teleport.numText.anchor.set(0.5); pool.teleport.numText.position.set(startPos.x + BASE_HEX_SIZE * 0.55, startPos.y - BASE_HEX_SIZE * 0.55); pathLayer.addChild(pool.teleport.numText);
        for (let i = 0; i < 6; i++) {
          const sp = new PIXI.Sprite(tex.sparkle); sp.anchor.set(0.5); sp.scale.set(0.2); sp.tint = 0xe0c0ff;
          particleContainer.addParticle(sp);
          pool.sparkles.push({ sprite: sp, index: i, type: 'teleport', baseX: startPos.x, baseY: startPos.y });
        }
      }
      
      let prevX = startPos.x, prevY = startPos.y;
      const stepOffset = hasTeleport ? 1 : 0; // If teleport is step 1, path steps start at 2
      pathPreview.path.forEach((step, i) => {
        const stepPos = hexToPixel(step.q, step.r, BASE_HEX_SIZE);
        const isUndiscovered = step.discovered === false;
        const lineColor = isUndiscovered ? 0xff9050 : 0x70c0ff;
        const glowColor = isUndiscovered ? 0xff6030 : 0x4090d0;
        
        const seg = { 
          isUndiscovered,
          glowLine: new PIXI.Graphics(),
          line: new PIXI.Graphics(), 
          numGlow: new PIXI.Graphics(),
          numBg: new PIXI.Graphics(), 
          numText: new PIXI.Text({ resolution: TEXT_RESOLUTION, text: String(i + 1 + stepOffset), style: { fontFamily: 'serif', fontSize: ICON_SCALE * 0.32, fontWeight: 'bold', fill: 0xffffff } }), 
          dots: [] 
        };
        
        // Calculate line properties
        const dx = stepPos.x - prevX;
        const dy = stepPos.y - prevY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        // Use seed based on position for consistent wobble
        const seed = i + stepPos.x * 0.01 + stepPos.y * 0.01;
        
        // Draw organic curved dotted lines - HD quality
        drawOrganicDottedLine(seg.glowLine, prevX, prevY, stepPos.x, stepPos.y, glowColor, 8, 0.25, seed);
        pathLayer.addChild(seg.glowLine);
        
        drawOrganicDottedLine(seg.line, prevX, prevY, stepPos.x, stepPos.y, lineColor, 3, 0.9, seed);
        pathLayer.addChild(seg.line);
        
        // Number glow - smooth circle
        seg.numGlow.circle(0, 0, ICON_SCALE * 0.36);
        seg.numGlow.fill({ color: glowColor, alpha: 0.45 });
        seg.numGlow.position.set(stepPos.x, stepPos.y - BASE_HEX_SIZE * 0.5);
        pathLayer.addChild(seg.numGlow);
        
        // Number background - smooth circle with subtle border
        seg.numBg.circle(0, 0, ICON_SCALE * 0.28); 
        seg.numBg.fill({ color: lineColor, alpha: 0.95 }); 
        seg.numBg.circle(0, 0, ICON_SCALE * 0.28); 
        seg.numBg.stroke({ color: 0xffffff, width: 1, alpha: 0.3 }); 
        seg.numBg.position.set(stepPos.x, stepPos.y - BASE_HEX_SIZE * 0.5); 
        pathLayer.addChild(seg.numBg);
        
        seg.numText.anchor.set(0.5); seg.numText.position.set(stepPos.x, stepPos.y - BASE_HEX_SIZE * 0.5); 
        pathLayer.addChild(seg.numText);
        
        // Animated dots/dashes along path
        for (let d = 0; d < Math.floor(dist / 15); d++) {
          const dot = new PIXI.Sprite(tex.sparkle); dot.anchor.set(0.5); dot.scale.set(0.08); dot.alpha = 0;
          dot.tint = isUndiscovered ? 0xffaa70 : 0x90d0ff;
          particleContainer.addParticle(dot);
          seg.dots.push({ sprite: dot, t: d / Math.floor(dist / 15), dx, dy, startX: prevX, startY: prevY });
        }
        pool.pathSegments.push(seg);
        prevX = stepPos.x; prevY = stepPos.y;
      });
      
      // Target with ETA
      const targetPos = hexToPixel(pathPreview.targetQ, pathPreview.targetR, BASE_HEX_SIZE);
      const lastStep = pathPreview.path[pathPreview.path.length - 1];
      const targetTex = lastStep?.discovered === false ? tex.orangeGlow : tex.blueGlow;
      const targetColor = lastStep?.discovered === false ? 0xff9050 : 0x70c0ff;
      pool.pathTarget = { pos: targetPos, outer: createGlow(targetTex, targetPos.x, targetPos.y, 2.8), inner: createGlow(targetTex, targetPos.x, targetPos.y, 2.0), ring: new PIXI.Graphics(), icon: new PIXI.Text({ resolution: TEXT_RESOLUTION, text: '◆', style: { fontFamily: 'serif', fontSize: ICON_SCALE * 0.35, fontWeight: 'bold', fill: targetColor } }) };
      pathLayer.addChild(pool.pathTarget.outer); pathLayer.addChild(pool.pathTarget.inner);
      pool.pathTarget.ring.poly(getHexPoints(BASE_HEX_SIZE * 1.05)); pool.pathTarget.ring.stroke({ color: targetColor, width: 2.5, alpha: 0.8, join: 'round' }); pool.pathTarget.ring.position.set(targetPos.x, targetPos.y); pathLayer.addChild(pool.pathTarget.ring);
      pool.pathTarget.icon.anchor.set(0.5); pool.pathTarget.icon.position.set(targetPos.x, targetPos.y + BASE_HEX_SIZE * 0.6); pathLayer.addChild(pool.pathTarget.icon);
      
      // ETA label - add 1 for teleport if applicable
      const etaTurns = pathPreview.teleportFrom ? pathPreview.path.length + 1 : pathPreview.path.length;
      pool.etaLabel = new PIXI.Text({ resolution: TEXT_RESOLUTION, text: `~${etaTurns} turn${etaTurns !== 1 ? 's' : ''}`, style: { fontFamily: 'serif', fontSize: ICON_SCALE * 0.2, fill: targetColor } });
      pool.etaLabel.anchor.set(0.5); pool.etaLabel.position.set(targetPos.x, targetPos.y + BASE_HEX_SIZE * 0.95);
      pathLayer.addChild(pool.etaLabel);
      
      // Movement ghost at destination - smooth hex
      pool.movementGhost = new PIXI.Graphics();
      pool.movementGhost.poly(getHexPoints(BASE_HEX_SIZE * 0.4));
      pool.movementGhost.fill({ color: targetColor, alpha: 0.3 });
      pool.movementGhost.poly(getHexPoints(BASE_HEX_SIZE * 0.4));
      pool.movementGhost.stroke({ color: targetColor, width: 1.5, alpha: 0.5, join: 'round' });
      pool.movementGhost.position.set(targetPos.x, targetPos.y);
      pathLayer.addChild(pool.movementGhost);
    }
    
    // Coordinate labels when zoomed in
    if (zoom >= 1.3) {
      Object.values(discoveredNodes).forEach(node => {
        const { q, r } = node.coordinates || { q: 0, r: 0 };
        const pos = hexToPixel(q, r, BASE_HEX_SIZE);
        const coordLabel = new PIXI.Text({ resolution: TEXT_RESOLUTION, text: `${q},${r}`, style: { fontFamily: 'monospace', fontSize: ICON_SCALE * 0.15, fill: 0x7a7568 } });
        coordLabel.anchor.set(0.5); coordLabel.alpha = 0.5; coordLabel.position.set(pos.x, pos.y + BASE_HEX_SIZE * 0.7);
        animatedLayer.addChild(coordLabel);
        pool.coordLabels.push(coordLabel);
      });
    }

    // Great Work construction timer (updates in ticker without rebuilding tiles)
    if (currentGreatWorkConstruction?.nodeId) {
      const constructionNode = discoveredNodes[currentGreatWorkConstruction.nodeId];
      if (constructionNode) {
        const { q, r } = constructionNode.coordinates || { q: 0, r: 0 };
        const pos = hexToPixel(q, r, BASE_HEX_SIZE);
        const progressInfo = getGreatWorkProgressInfo?.();
        if (progressInfo?.remaining > 0) {
          const secondsLeft = Math.ceil(progressInfo.remaining);
          pool.constructionTimer = new PIXI.Text({
            resolution: TEXT_RESOLUTION,
            text: formatTime(secondsLeft),
            style: { fontFamily: 'Arial', fontSize: ICON_SCALE * 0.32, fill: 0xffffff, fontWeight: 'bold', stroke: { color: 0x000000, width: 3 } }
          });
          pool.constructionTimer.anchor.set(0.5);
          pool.constructionTimer.position.set(pos.x, pos.y + BASE_HEX_SIZE * 0.05);
          animatedLayer.addChild(pool.constructionTimer);
        }
      }
    }

    // Trade Route Lines - golden dotted lines flowing toward Kingdom when overlay is active
    if (tradeRouteOverlay && tradeRouteSegmentCount > 0) {
      const { parentMap } = calculateConnectedTiles(tradeRouteSegments, discoveredNodes);

      // Create graphics for trade route lines
      pool.tradeRouteLines = {
        graphics: new PIXI.Graphics(),
        lines: [], // Array of line segments with positions and dot animation data
      };

      // Build line segments from parentMap - each connected tile points to its parent (toward Kingdom)
      parentMap.forEach((parentId, childId) => {
        const childCoords = childId === 'kingdom' || childId === 'node_0_0'
          ? { q: 0, r: 0 }
          : discoveredNodes[childId]?.coordinates;
        const parentCoords = parentId === 'kingdom' || parentId === 'node_0_0'
          ? { q: 0, r: 0 }
          : discoveredNodes[parentId]?.coordinates;

        if (!childCoords || !parentCoords) return;

        const startPos = hexToPixel(childCoords.q, childCoords.r, BASE_HEX_SIZE);
        const endPos = hexToPixel(parentCoords.q, parentCoords.r, BASE_HEX_SIZE);

        // Create dots for this line segment (3-4 dots per segment)
        const dots = [];
        for (let i = 0; i < 4; i++) {
          dots.push({
            t: i / 4, // Evenly spaced along the line (0 to 1)
            size: 3 + Math.random() * 1.5, // Slight size variation
          });
        }

        pool.tradeRouteLines.lines.push({
          startPos,
          endPos,
          dots,
        });
      });

      // Add to animated layer (below player but above tiles)
      animatedLayer.addChildAt(pool.tradeRouteLines.graphics, 0);

      // Reset animation phase
      tradeRouteAnimation.phase = 0;
    }

    pool.built = true;
  }, [worldMap, getCurrentCoords, beaconNodes, clickedTile, pathPreview, pixiReady, zoom, greatWorkPlacement, tradeRouteOverlay]);

  // Animation ticker
  useEffect(() => {
    const app = pixiAppRef.current;
    if (!app || !pixiReady) return;
    
    const tickerCallback = (ticker) => {
      const pool = animatedPoolSingleton;
      if (!pool.built || !pixiAppRef.current) return;

      // PERF: Track FPS when debug mode is enabled
      if (perfDebugEnabled) {
        const now = performance.now();
        fpsFramesRef.current.push(now);
        // Keep only last 60 frames for rolling average
        while (fpsFramesRef.current.length > 60) fpsFramesRef.current.shift();

        // Update stats every 500ms to avoid excessive state updates
        if (now - lastPerfUpdateRef.current > 500 && fpsFramesRef.current.length >= 2) {
          const frames = fpsFramesRef.current;
          const avgFrameTime = (frames[frames.length - 1] - frames[0]) / (frames.length - 1);
          const fps = Math.round(1000 / avgFrameTime);
          const staticLayer = staticLayerRef.current;
          const animatedLayer = animatedLayerRef.current;
          const fogLayer = fogLayerRef.current;
          const objects = (staticLayer?.children?.length || 0) + (animatedLayer?.children?.length || 0) + (fogLayer?.children?.length || 0);
          const fogTiles = pool.fogTiles?.length || 0;

          // Advanced cache/culling stats
          const tileCache = tileCacheRef.current;
          const borderCache = borderCacheByNodeRef.current;
          const wonderSiteCache = wonderSiteGraphicsRef.current;

          const tilesTotal = tileCache.size;
          const tilesCached = tileCache.size; // All tiles are cached
          let tilesVisible = 0;
          let bordersVisible = 0;
          let wonderSitesVisible = 0;

          // Count visible tiles
          tileCache.forEach((cachedTile) => {
            if (cachedTile.elements?.[0]?.visible) tilesVisible++;
          });

          // Count visible borders
          borderCache.forEach((borderGraphics) => {
            if (borderGraphics.visible) bordersVisible++;
          });

          // Count visible wonder sites
          wonderSiteCache.forEach((cached) => {
            if (cached.elements?.[0]?.visible) wonderSitesVisible++;
          });

          // Estimate memory usage (rough approximation)
          const memoryMB = Math.round((objects * 0.001) * 100) / 100; // ~1KB per object estimate

          setPerfStats({
            fps,
            objects,
            fogTiles,
            tilesTotal,
            tilesCached,
            tilesVisible,
            bordersTotal: borderCache.size,
            bordersVisible,
            wonderSitesTotal: wonderSiteCache.size,
            wonderSitesVisible,
            memoryMB,
          });
          lastPerfUpdateRef.current = now;
        }
      }

      glowPhaseSingleton = (glowPhaseSingleton + 0.025 * ticker.deltaTime) % (Math.PI * 2);
      const phase = glowPhaseSingleton;
      
      // Player breathing effect (very subtle with reduced glows) + movement animation
      if (pool.player) {
        // Update movement animation - FROG HOP or TELEPORT
        if (playerMoveAnimation.isAnimating) {
          playerMoveAnimation.progress += ticker.deltaTime / 60 / playerMoveAnimation.duration;

          if (playerMoveAnimation.progress >= 1) {
            // Animation complete - trigger landing effects
            playerMoveAnimation.isAnimating = false;
            playerMoveAnimation.progress = 1;
            if (!playerMoveAnimation.isTeleport) {
              playerMoveAnimation.landingPulse = 1.0;
              playerMoveAnimation.landingSquash = 1.0;
            }
            // Shockwave for teleport is triggered at 50% (when position switches)
          }

          const t = playerMoveAnimation.progress;
          const isTeleport = playerMoveAnimation.isTeleport;

          // Horizontal movement
          let currentX, currentY;
          if (isTeleport) {
            // Instant teleport at 50% progress
            if (t < 0.5) {
              currentX = playerMoveAnimation.startPos.x;
              currentY = playerMoveAnimation.startPos.y;
            } else {
              currentX = playerMoveAnimation.targetPos.x;
              currentY = playerMoveAnimation.targetPos.y;

              // Trigger shockwave at the moment of teleport (when we switch positions)
              if (t >= 0.5 && playerMoveAnimation.teleportShockwave === 0) {
                playerMoveAnimation.teleportShockwave = 1.0;

                // Create particle burst at arrival
                playerMoveAnimation.teleportBurst = [];
                for (let i = 0; i < 12; i++) {
                  const angle = (i / 12) * Math.PI * 2;
                  const speed = 2 + Math.random() * 2;
                  playerMoveAnimation.teleportBurst.push({
                    x: playerMoveAnimation.targetPos.x,
                    y: playerMoveAnimation.targetPos.y,
                    vx: Math.cos(angle) * speed,
                    vy: Math.sin(angle) * speed,
                    life: 1.0,
                    size: 0.15 + Math.random() * 0.1
                  });
                }
              }
            }
          } else {
            // Linear movement for hop
            currentX = playerMoveAnimation.startPos.x + (playerMoveAnimation.targetPos.x - playerMoveAnimation.startPos.x) * t;
            currentY = playerMoveAnimation.startPos.y + (playerMoveAnimation.targetPos.y - playerMoveAnimation.startPos.y) * t;
          }

          let hopHeight = 0;
          let scaleX = 1, scaleY = 1;

          if (isTeleport) {
            // TELEPORT ANIMATION - fade out/in
            if (t < 0.5) {
              // Fade out + shrink
              pool.player.icon.alpha = 1 - t * 2;
              scaleX = scaleY = 1 - t * 2;
            } else {
              // Fade in + grow
              pool.player.icon.alpha = (t - 0.5) * 2;
              scaleX = scaleY = (t - 0.5) * 2;
            }
          } else {
            // FROG HOP ARC - Improved feel
            // Phase 1 (0-35%): Quick explosive launch
            // Phase 2 (35-55%): Hang time at peak
            // Phase 3 (55-100%): Smooth drop
            if (t < 0.35) {
              // Explosive launch - starts slow, accelerates up (ease-in-out)
              const launchProgress = t / 0.35;
              const eased = launchProgress < 0.5
                ? 2 * launchProgress * launchProgress
                : 1 - Math.pow(-2 * launchProgress + 2, 2) / 2;
              hopHeight = eased * BASE_HEX_SIZE * 1.0;
            } else if (t < 0.55) {
              // Hang time at peak
              hopHeight = BASE_HEX_SIZE * 1.0;
            } else {
              // Smooth drop with slight acceleration (ease-in)
              const dropProgress = (t - 0.55) / 0.45;
              hopHeight = (1 - Math.pow(dropProgress, 1.8)) * BASE_HEX_SIZE * 1.0;
            }

            // Squash and stretch - with ANTICIPATION
            if (t < 0.14) {
              // Squash down
              const squashProgress = t / 0.14;
              scaleX = 1 + squashProgress * 0.5;
              scaleY = 1 - squashProgress * 0.5;
            } else if (t < 0.43) {
              // HOLD the squash (anticipation!)
              scaleX = 1.5;
              scaleY = 0.5;
            } else if (t < 0.5) {
              // Explosive launch
              const launchProgress = (t - 0.43) / 0.07;
              scaleY = 0.5 + launchProgress * 1.0;
              scaleX = 1.5 - launchProgress * 0.7;
            } else if (t < 0.8) {
              // Stretched in air
              scaleY = 1.5;
              scaleX = 0.8;
            } else if (t < 0.9) {
              // Transition back to normal
              const landTransition = (t - 0.8) / 0.1;
              scaleY = 1.5 - landTransition * 0.5;
              scaleX = 0.8 + landTransition * 0.2;
            } else {
              // Final landing squash
              const landProgress = (t - 0.9) / 0.1;
              scaleX = 1 + landProgress * 0.4;
              scaleY = 1 - landProgress * 0.4;
            }
          }

          // Update all player elements to animated position
          pool.player.outer.position.set(currentX, currentY);
          pool.player.mid.position.set(currentX, currentY);
          pool.player.inner.position.set(currentX, currentY);
          pool.player.ring.position.set(currentX, currentY);
          pool.player.icon.position.set(currentX, currentY - BASE_HEX_SIZE * 0.1 - hopHeight);
          pool.player.icon.scale.set(scaleX, scaleY);
        } else {
          // No animation - ensure positions are set to target
          pool.player.outer.position.set(pool.player.pos.x, pool.player.pos.y);
          pool.player.mid.position.set(pool.player.pos.x, pool.player.pos.y);
          pool.player.inner.position.set(pool.player.pos.x, pool.player.pos.y);
          pool.player.ring.position.set(pool.player.pos.x, pool.player.pos.y);

          // Base position with landing bounce
          let iconY = pool.player.pos.y - BASE_HEX_SIZE * 0.1;

          // Small bounce after landing (decreases over time)
          if (playerMoveAnimation.landingSquash > 0) {
            // Bounce up slightly then settle
            const bounceHeight = Math.sin(playerMoveAnimation.landingSquash * Math.PI) * BASE_HEX_SIZE * 0.15;
            iconY -= bounceHeight;
          }

          pool.player.icon.position.set(pool.player.pos.x, iconY);

          // Apply landing squash if active
          if (playerMoveAnimation.landingSquash > 0) {
            const squash = playerMoveAnimation.landingSquash * 0.3;
            pool.player.icon.scale.set(1 + squash, 1 - squash);
          } else {
            pool.player.icon.scale.set(1, 1);
          }
        }

        // Landing effects (pulse ring + squash recovery)
        if (playerMoveAnimation.landingPulse > 0) {
          playerMoveAnimation.landingPulse = Math.max(0, playerMoveAnimation.landingPulse - ticker.deltaTime / 60 * 5);
          const pulseScale = 1 + playerMoveAnimation.landingPulse * 0.5; // Increased from 0.4
          pool.player.ring.scale.set(pulseScale);
          pool.player.ring.alpha = (0.35 + fastSin(phase * 2.5) * 0.015) * (1 + playerMoveAnimation.landingPulse * 1.0); // More visible

          // Add glow pulse to the player glows
          const glowBoost = playerMoveAnimation.landingPulse * 0.5;
          pool.player.outer.alpha = (pool.player.outer.alpha || 0.02) * (1 + glowBoost);
          pool.player.mid.alpha = (pool.player.mid.alpha || 0.025) * (1 + glowBoost);
          pool.player.inner.alpha = (pool.player.inner.alpha || 0.03) * (1 + glowBoost);
        }

        if (playerMoveAnimation.landingSquash > 0) {
          playerMoveAnimation.landingSquash = Math.max(0, playerMoveAnimation.landingSquash - ticker.deltaTime / 60 * 4); // Slower decay for longer bounce
        }

        // Teleport trail - draw and fade out (ENHANCED - more visible)
        if (pool.player && playerMoveAnimation.teleportTrail > 0) {
          // Draw trail line from start to target position
          pool.player.teleportTrail.clear();
          const trailAlpha = playerMoveAnimation.teleportTrail; // Full alpha at start

          // Multi-layer glowing line for maximum visibility
          // Outermost glow - very wide and soft
          pool.player.teleportTrail.moveTo(playerMoveAnimation.startPos.x, playerMoveAnimation.startPos.y);
          pool.player.teleportTrail.lineTo(playerMoveAnimation.targetPos.x, playerMoveAnimation.targetPos.y);
          pool.player.teleportTrail.stroke({ color: 0x9060d0, width: 16, alpha: trailAlpha * 0.3 }); // Wide purple glow

          // Mid glow
          pool.player.teleportTrail.moveTo(playerMoveAnimation.startPos.x, playerMoveAnimation.startPos.y);
          pool.player.teleportTrail.lineTo(playerMoveAnimation.targetPos.x, playerMoveAnimation.targetPos.y);
          pool.player.teleportTrail.stroke({ color: 0xc090ff, width: 10, alpha: trailAlpha * 0.6 }); // Medium glow

          // Inner glow
          pool.player.teleportTrail.moveTo(playerMoveAnimation.startPos.x, playerMoveAnimation.startPos.y);
          pool.player.teleportTrail.lineTo(playerMoveAnimation.targetPos.x, playerMoveAnimation.targetPos.y);
          pool.player.teleportTrail.stroke({ color: 0xe0c0ff, width: 5, alpha: trailAlpha * 0.85 }); // Bright purple

          // Core bright line
          pool.player.teleportTrail.moveTo(playerMoveAnimation.startPos.x, playerMoveAnimation.startPos.y);
          pool.player.teleportTrail.lineTo(playerMoveAnimation.targetPos.x, playerMoveAnimation.targetPos.y);
          pool.player.teleportTrail.stroke({ color: 0xfff0ff, width: 2, alpha: trailAlpha }); // White core

          // Fade out trail slower for visibility
          playerMoveAnimation.teleportTrail = Math.max(0, playerMoveAnimation.teleportTrail - ticker.deltaTime / 60 * 2);
        } else if (pool.player) {
          // Clear trail when fully faded
          pool.player.teleportTrail.clear();
        }

        // Teleport shockwave - expanding ring on arrival (INSTANT - no delay)
        if (pool.player && playerMoveAnimation.teleportShockwave > 0) {
          pool.player.shockwaveRing.clear();
          const shockProgress = 1 - playerMoveAnimation.teleportShockwave; // 0->1 as it expands

          // Fast fade for snappy feel
          const shockAlpha = Math.pow(playerMoveAnimation.teleportShockwave, 1.5) * 0.95; // Exponential fade

          // Smaller, tighter shockwave - expands from 0.8x to 2.0x (was 1.2x to 3.7x)
          const ringRadius = BASE_HEX_SIZE * (0.8 + shockProgress * 1.2);
          const ringWidth = 5 - shockProgress * 3; // Thinner (5 -> 2)

          // Position at target (arrival location)
          pool.player.shockwaveRing.position.set(playerMoveAnimation.targetPos.x, playerMoveAnimation.targetPos.y);

          // Draw multiple rings for thickness with eased expansion
          const easedRadius = ringRadius * (1 - Math.pow(1 - shockProgress, 2)); // Ease-out expansion

          pool.player.shockwaveRing.circle(0, 0, easedRadius);
          pool.player.shockwaveRing.stroke({ color: 0x9060d0, width: ringWidth + 3, alpha: shockAlpha * 0.5 }); // Outer purple glow

          pool.player.shockwaveRing.circle(0, 0, easedRadius);
          pool.player.shockwaveRing.stroke({ color: 0xd0a0ff, width: ringWidth + 1.5, alpha: shockAlpha * 0.8 }); // Mid glow

          pool.player.shockwaveRing.circle(0, 0, easedRadius);
          pool.player.shockwaveRing.stroke({ color: 0xfff0ff, width: ringWidth * 0.7, alpha: shockAlpha }); // Bright core

          // Add second ring for double-shockwave effect
          if (shockProgress > 0.15) {
            const secondRingProgress = (shockProgress - 0.15) / 0.85;
            const secondRadius = easedRadius * 0.7; // Smaller trailing ring
            const secondAlpha = shockAlpha * (1 - secondRingProgress) * 0.6;

            pool.player.shockwaveRing.circle(0, 0, secondRadius);
            pool.player.shockwaveRing.stroke({ color: 0xe0c0ff, width: ringWidth * 0.8, alpha: secondAlpha });
          }

          // Faster decay for snappier animation
          playerMoveAnimation.teleportShockwave = Math.max(0, playerMoveAnimation.teleportShockwave - ticker.deltaTime / 60 * 3.5);
        } else if (pool.player) {
          pool.player.shockwaveRing.clear();
        }

        // Teleport particle burst
        if (pool.player && playerMoveAnimation.teleportBurst.length > 0) {
          pool.player.burstParticles.clear();

          // Update and draw particles
          for (let i = playerMoveAnimation.teleportBurst.length - 1; i >= 0; i--) {
            const p = playerMoveAnimation.teleportBurst[i];

            // Update position
            p.x += p.vx;
            p.y += p.vy;
            p.vx *= 0.95; // Slow down
            p.vy *= 0.95;
            p.life -= ticker.deltaTime / 60 * 4; // Fade fast

            if (p.life <= 0) {
              playerMoveAnimation.teleportBurst.splice(i, 1);
              continue;
            }

            // Draw particle as a glowing circle
            const particleAlpha = p.life * 0.9;
            const particleSize = BASE_HEX_SIZE * p.size * (0.5 + p.life * 0.5); // Shrink as it fades

            // Outer glow
            pool.player.burstParticles.circle(p.x, p.y, particleSize * 1.5);
            pool.player.burstParticles.fill({ color: 0xc090ff, alpha: particleAlpha * 0.3 });

            // Inner bright
            pool.player.burstParticles.circle(p.x, p.y, particleSize);
            pool.player.burstParticles.fill({ color: 0xfff0ff, alpha: particleAlpha * 0.8 });
          }
        } else if (pool.player) {
          pool.player.burstParticles.clear();
        }

        // Gather animations
        if (pool.gatherEffects) {
          // Tile glow pulse (use saved position from when gather was triggered)
          if (gatherAnimation.tilePulse > 0) {
            pool.gatherEffects.tileGlow.clear();

            const glowAlpha = gatherAnimation.tilePulse * 0.6;
            const glowScale = 1 + (1 - gatherAnimation.tilePulse) * 0.3; // Expand as it fades

            // Outer glow - golden/amber for gathering resources
            pool.gatherEffects.tileGlow.circle(gatherAnimation.gatheredTilePos.x, gatherAnimation.gatheredTilePos.y, BASE_HEX_SIZE * 1.1 * glowScale);
            pool.gatherEffects.tileGlow.fill({ color: 0xd4a017, alpha: glowAlpha * 0.4 }); // Dark gold

            // Inner bright - bright yellow/gold
            pool.gatherEffects.tileGlow.circle(gatherAnimation.gatheredTilePos.x, gatherAnimation.gatheredTilePos.y, BASE_HEX_SIZE * 0.9 * glowScale);
            pool.gatherEffects.tileGlow.fill({ color: 0xffd700, alpha: glowAlpha * 0.6 }); // Bright gold

            // Decay glow
            gatherAnimation.tilePulse = Math.max(0, gatherAnimation.tilePulse - ticker.deltaTime / 60 * 3);
          } else {
            pool.gatherEffects.tileGlow.clear();
          }

          // Flying particles
          if (gatherAnimation.particles.length > 0) {
            pool.gatherEffects.particles.clear();

            for (let i = gatherAnimation.particles.length - 1; i >= 0; i--) {
              const p = gatherAnimation.particles[i];

              // Handle delay
              if (p.delay > 0) {
                p.delay -= ticker.deltaTime / 60;
                continue;
              }

              // Update progress
              p.progress += ticker.deltaTime / 60 * 2.5; // Fast flight
              p.life -= ticker.deltaTime / 60 * 2;

              if (p.life <= 0 || p.progress >= 1) {
                gatherAnimation.particles.splice(i, 1);
                continue;
              }

              // Ease-in curve for upward flight
              const t = Math.pow(p.progress, 1.5);
              p.x = p.startX + (p.targetX - p.startX) * t;
              p.y = p.startY + (p.targetY - p.startY) * t;

              // Draw particle - golden/amber gathering particles
              const particleAlpha = p.life * 0.85;
              const particleSize = BASE_HEX_SIZE * 0.12;

              // Glow - amber outer glow
              pool.gatherEffects.particles.circle(p.x, p.y, particleSize * 2);
              pool.gatherEffects.particles.fill({ color: 0xffa500, alpha: particleAlpha * 0.4 }); // Orange amber

              // Core - bright gold center
              pool.gatherEffects.particles.circle(p.x, p.y, particleSize);
              pool.gatherEffects.particles.fill({ color: 0xffeb3b, alpha: particleAlpha * 0.9 }); // Bright yellow-gold
            }
          } else {
            pool.gatherEffects.particles.clear();
          }

          // Tile shake effect - modify the discovered tile at current position
          if (gatherAnimation.tileShake > 0) {
            // Note: Shake is applied when rendering static tiles based on gatherAnimation.tileShake value
            // Decay shake
            gatherAnimation.tileShake = Math.max(0, gatherAnimation.tileShake - ticker.deltaTime / 60 * 5);
          }
        }

        const breathe = 1 + fastSin(phase * 1.5) * 0.002;
        const p1 = 0.35 + fastSin(phase * 2.5) * 0.015;
        const p2 = 0.3 + fastSin(phase * 2.5 + 1) * 0.012;
        const p3 = 0.25 + fastSin(phase * 2.5 + 2) * 0.01;
        pool.player.outer.alpha = p3 * 0.02; pool.player.outer.scale.set((2.8 + fastSin(phase * 2) * 0.015) * breathe);
        pool.player.mid.alpha = p2 * 0.025; pool.player.mid.scale.set((2.0 + fastSin(phase * 2 + 0.5) * 0.01) * breathe);
        pool.player.inner.alpha = p1 * 0.03; pool.player.inner.scale.set((1.4 + fastSin(phase * 2 + 1) * 0.008) * breathe);
        pool.player.ring.alpha = p1; pool.player.ring.scale.set(breathe);

        // Character icon visibility and subtle bounce (only show when NOT at kingdom)
        const currentPos = playerMoveAnimation.isAnimating
          ? { x: pool.player.icon.position.x, y: pool.player.icon.position.y }
          : pool.player.pos;
        const visible = isInViewport(currentPos.x, currentPos.y);
        pool.player.icon.visible = visible && !pool.player.isAtKingdom;
        if (visible && !pool.player.isAtKingdom && !playerMoveAnimation.isAnimating) {
          // Only apply bounce when not moving
          const bounce = 1 + fastSin(phase * 1.8) * 0.03;
          pool.player.icon.scale.set(bounce);
        } else if (playerMoveAnimation.isAnimating) {
          // During movement, keep scale at 1 (no bounce)
          pool.player.icon.scale.set(1);
        }

      }

      // Distance rings pulse
      if (pool.distanceRings) {
        pool.distanceRings.alpha = 0.6 + fastSin(phase * 0.5) * 0.02;
      }
      
      // FOG TILES - subtle breathing animation for mystery
      pool.fogTiles.forEach(fog => {
        const visible = isInViewport(fog.pos.x, fog.pos.y);
        fog.hex.visible = fog.overlay.visible = fog.text.visible = visible;
        if (!visible) return;

        // Subtle breathing effect - fog slowly fades in/out
        const fogPhase = phase * 0.3 + fog.q * 0.2 + fog.r * 0.2;
        const breathe = 0.88 + fastSin(fogPhase) * 0.07;
        fog.overlay.alpha = 0.3 * breathe;
        fog.text.alpha = 0.15 * breathe;
      });

      // REVEAL OVERLAYS - fog lifting animation for newly discovered tiles
      if (pool.revealOverlays?.length > 0) {
        const revealNow = Date.now();
        pool.revealOverlays.forEach(reveal => {
          const elapsed = revealNow - reveal.startTime;
          const duration = 500; // 500ms reveal animation
          const progress = Math.min(elapsed / duration, 1);
          // Ease out cubic for smooth deceleration
          const eased = 1 - Math.pow(1 - progress, 3);

          // Fade out the fog overlay
          reveal.fog.alpha = 1 - eased;

          // Gold glow pulses in then out (sine wave over duration)
          reveal.glow.alpha = Math.sin(progress * Math.PI) * 0.4;
        });
      }

      // ========================================
      // ⚠️ VIEWPORT CULLING SECTION ⚠️
      // ========================================
      // All new static graphics MUST be added here
      // READ WORLDMAP_PERFORMANCE.md section "Adding New Static Graphics"
      //
      // Pattern:
      // myCache.forEach((cached, id) => {
      //   const pos = getPositionForId(id);
      //   const visible = isInViewport(pos.x, pos.y);
      //   cached.elements.forEach(el => el.visible = visible);
      // });
      // ========================================

      // PERF: Static tile viewport culling - hide off-screen tiles to reduce GPU load
      // This allows thousands of tiles while maintaining 60+ FPS
      const tileCache = tileCacheRef.current;
      const borderCache = borderCacheByNodeRef.current;
      tileCache.forEach((cachedTile, nodeId) => {
        // Parse coordinates from nodeId (format: "node_q_r" or "kingdom")
        if (nodeId === 'kingdom') {
          // Kingdom is always at 0,0
          const pos = hexToPixel(0, 0, BASE_HEX_SIZE);
          const visible = isInViewport(pos.x, pos.y);
          cachedTile.elements.forEach(el => el.visible = visible);
        } else {
          const match = nodeId.match(/node_(-?\d+)_(-?\d+)/);
          if (match) {
            const q = parseInt(match[1]), r = parseInt(match[2]);
            const pos = hexToPixel(q, r, BASE_HEX_SIZE);
            const visible = isInViewport(pos.x, pos.y);
            cachedTile.elements.forEach(el => el.visible = visible);
          }
        }
      });

      // PERF: Border culling - hide off-screen borders
      borderCache.forEach((borderGraphics, nodeId) => {
        if (nodeId === 'kingdom') {
          const pos = hexToPixel(0, 0, BASE_HEX_SIZE);
          borderGraphics.visible = isInViewport(pos.x, pos.y);
        } else {
          const match = nodeId.match(/node_(-?\d+)_(-?\d+)/);
          if (match) {
            const q = parseInt(match[1]), r = parseInt(match[2]);
            const pos = hexToPixel(q, r, BASE_HEX_SIZE);
            borderGraphics.visible = isInViewport(pos.x, pos.y);
          }
        }
      });

      // PERF: Wonder site culling - hide off-screen wonder sites
      const wonderSiteCache = wonderSiteGraphicsRef.current;
      wonderSiteCache.forEach((cached, wonderId) => {
        // Wonder sites are 3-tile triangles - check center position
        const wonderSites = worldMap?.wonderSites || {};
        const site = wonderSites[wonderId];
        if (site?.tileDefinitions?.length === 3) {
          const positions = site.tileDefinitions.map(td => hexToPixel(td.coordinates.q, td.coordinates.r, BASE_HEX_SIZE));
          const centerX = (positions[0].x + positions[1].x + positions[2].x) / 3;
          const centerY = (positions[0].y + positions[1].y + positions[2].y) / 3;
          const visible = isInViewport(centerX, centerY, BASE_HEX_SIZE * 3); // Larger padding for 3-tile structure
          cached.elements.forEach(el => el.visible = visible);
        }
      });

      // Adjacent tiles - subtle breathing for explorable fog
      pool.adjacentTiles.forEach(tile => {
        const visible = isInViewport(tile.pos.x, tile.pos.y);
        tile.outer.visible = tile.inner.visible = tile.hex.visible = tile.overlay.visible = tile.text.visible = visible;
        if (!visible) return;

        // Very subtle breathing for adjacent tiles
        const adjPhase = phase * 0.4 + tile.q * 0.2 + tile.r * 0.2;
        const breathe = 0.9 + fastSin(adjPhase) * 0.1;
        tile.overlay.alpha = 0.25 * breathe;
        tile.text.alpha = 0.35 * breathe;
      });
      
      // Beacons
      pool.beacons.forEach(b => {
        const visible = isInViewport(b.pos.x, b.pos.y);
        b.outer.visible = b.mid.visible = b.inner.visible = visible;
        if (!visible) return;
        const bPhase = phase * 1.2 + b.q * 0.3 + b.r * 0.3;
        const intensity = 0.4 + fastSin(bPhase) * 0.02;
        const pulse = 1 + fastSin(bPhase * 0.8) * 0.008;
        b.outer.alpha = intensity * 0.0125; b.outer.scale.set(3.0 * pulse);
        b.mid.alpha = intensity * 0.02; b.mid.scale.set(2.2 * pulse);
        b.inner.alpha = intensity * 0.025;
      });
      
      // Kingdom
      if (pool.kingdom) {
        const kPhase = phase * 1.3;
        const intensity = 0.35 + fastSin(kPhase) * 0.015;
        const pulse = 1 + fastSin(kPhase * 0.7) * 0.006;
        pool.kingdom.outer.alpha = intensity * 0.01; pool.kingdom.outer.scale.set(3.2 * pulse);
        pool.kingdom.mid.alpha = intensity * 0.0175; pool.kingdom.mid.scale.set(2.4 * pulse);
        pool.kingdom.inner.alpha = intensity * 0.0225;
      }

      // Valid Great Work placements - prominent blue tile with strong pulsing
      if (pool.validPlacements?.length > 0) {
        pool.validPlacements.forEach((tile, i) => {
          const visible = isInViewport(tile.pos.x, tile.pos.y);
          tile.outer.visible = tile.inner.visible = tile.border.visible = tile.fill.visible = tile.icon.visible = visible;
          if (!visible) return;
          const vPhase = phase * 3.5 + i * 0.4;  // Faster pulse
          const intensity = 0.6 + fastSin(vPhase) * 0.4;  // Wider range (0.2-1.0)
          const pulse = 1 + fastSin(vPhase * 0.8) * 0.04;
          tile.outer.alpha = intensity * 0.25; tile.outer.scale.set(2.5 * pulse);  // 2.5x stronger glow
          tile.inner.alpha = intensity * 0.35; tile.inner.scale.set(1.8 * pulse);  // Stronger inner glow
          tile.border.alpha = 0.7 + fastSin(vPhase * 2.5) * 0.3;  // More dramatic border pulse
          tile.border.scale.set(1 + fastSin(vPhase * 1.5) * 0.02);  // Border "breathing"
          tile.fill.alpha = 0.5 + fastSin(vPhase) * 0.25;  // Wider fill pulse (0.25-0.75)
          tile.icon.alpha = 0.5 + fastSin(vPhase * 2) * 0.3;  // Much brighter icon (0.2-0.8)
        });
      }

      // Hover - update position/visibility from ref
      if (pool.hoverTile) {
        const hovered = hoveredTileRef.current;
        const coords = currentCoordsRef.current;
        if (hovered && !(hovered.q === coords.q && hovered.r === coords.r)) {
          const hoverPos = hexToPixel(hovered.q, hovered.r, BASE_HEX_SIZE);
          pool.hoverTile.ring.position.set(hoverPos.x, hoverPos.y);
          pool.hoverTile.ring.visible = true;
          pool.hoverTile.ring.alpha = 0.3 + fastSin(phase * 4) * 0.01;
        } else {
          pool.hoverTile.ring.visible = false;
        }
      }

      // Wonder site cinematic fade - fade adjacent tile labels/icons when hovering wonder site
      if (wonderSiteFadeElements.length > 0) {
        const hovered = hoveredTileRef.current;
        const hoveredKey = hovered ? `${hovered.q},${hovered.r}` : null;

        wonderSiteFadeElements.forEach(({ siteTiles, elements }) => {
          // Check if hovering any tile in this wonder site
          const isHoveringThisSite = hoveredKey && siteTiles.has(hoveredKey);

          // Smooth fade: target alpha 0.1 when hovering, 1.0 otherwise
          const targetAlpha = isHoveringThisSite ? 0.08 : 1.0;

          elements.forEach(el => {
            // Store original alpha on first encounter
            if (el._originalAlpha === undefined) {
              el._originalAlpha = el.alpha;
            }
            // Lerp towards target (smooth transition)
            const currentAlpha = el.alpha / el._originalAlpha;
            const newAlpha = currentAlpha + (targetAlpha - currentAlpha) * 0.15;
            el.alpha = el._originalAlpha * newAlpha;
          });
        });
      }

      // Great Work cinematic fade - fade adjacent tile labels/icons when hovering Great Work
      if (greatWorkFadeElements.length > 0) {
        const hovered = hoveredTileRef.current;
        const hoveredKey = hovered ? `${hovered.q},${hovered.r}` : null;

        greatWorkFadeElements.forEach(({ workTileKey, elements }) => {
          // Check if hovering this Great Work tile
          const isHoveringThisWork = hoveredKey && hoveredKey === workTileKey;

          // Smooth fade: target alpha 0.1 when hovering, 1.0 otherwise
          const targetAlpha = isHoveringThisWork ? 0.08 : 1.0;

          elements.forEach(el => {
            // Store original alpha on first encounter
            if (el._originalAlpha === undefined) {
              el._originalAlpha = el.alpha;
            }
            // Lerp towards target (smooth transition)
            const currentAlpha = el.alpha / el._originalAlpha;
            const newAlpha = currentAlpha + (targetAlpha - currentAlpha) * 0.15;
            el.alpha = el._originalAlpha * newAlpha;
          });
        });
      }

      // Click highlight
      if (pool.clickHighlight) {
        const cPulse = 0.6 + fastSin(phase * 4) * 0.02;
        pool.clickHighlight.glow.alpha = cPulse * 0.02; pool.clickHighlight.glow.scale.set(2.2 + fastSin(phase * 3) * 0.012);
        pool.clickHighlight.ring.alpha = cPulse;
      }

      // Trade Route Lines - animated golden dotted lines flowing toward Kingdom
      if (pool.tradeRouteLines?.lines?.length > 0) {
        // Advance animation phase
        tradeRouteAnimation.phase = (tradeRouteAnimation.phase + ticker.deltaTime / 60 * 0.5) % 1;

        // Clear and redraw
        pool.tradeRouteLines.graphics.clear();

        pool.tradeRouteLines.lines.forEach(line => {
          const { startPos, endPos, dots } = line;
          const dx = endPos.x - startPos.x;
          const dy = endPos.y - startPos.y;

          // Draw base line (semi-transparent gold)
          pool.tradeRouteLines.graphics.moveTo(startPos.x, startPos.y);
          pool.tradeRouteLines.graphics.lineTo(endPos.x, endPos.y);
          pool.tradeRouteLines.graphics.stroke({ color: 0xdab050, width: 2, alpha: 0.25 });

          // Draw animated dots flowing toward Kingdom (toward endPos)
          dots.forEach(dot => {
            // Advance dot position
            dot.t = (dot.t + ticker.deltaTime / 60 * 0.8) % 1;

            // Calculate position along line
            const t = dot.t;
            const x = startPos.x + dx * t;
            const y = startPos.y + dy * t;

            // Dot alpha fades at endpoints, brightest in middle
            const fadeAlpha = Math.sin(t * Math.PI) * 0.9 + 0.1;

            // Draw glowing dot
            pool.tradeRouteLines.graphics.circle(x, y, dot.size * 1.5);
            pool.tradeRouteLines.graphics.fill({ color: 0xdab050, alpha: fadeAlpha * 0.3 }); // Outer glow
            pool.tradeRouteLines.graphics.circle(x, y, dot.size);
            pool.tradeRouteLines.graphics.fill({ color: 0xffd060, alpha: fadeAlpha * 0.8 }); // Inner bright
          });
        });
      }

      // Teleport
      if (pool.teleport) {
        const tPulse = 1 + fastSin(phase * 2) * 0.01;
        const tAlpha = 0.6 + fastSin(phase * 3) * 0.015;
        pool.teleport.outer.alpha = tAlpha * 0.015; pool.teleport.outer.scale.set(3.5 * tPulse);
        pool.teleport.mid.alpha = tAlpha * 0.025; pool.teleport.mid.scale.set(2.5 * tPulse);
        pool.teleport.inner.alpha = tAlpha * 0.03;
        pool.teleport.ring.scale.set(tPulse); pool.teleport.ring.alpha = tAlpha;
        pool.teleport.numGlow.alpha = tAlpha * 0.5;
      }
      
      // Path segments - danger indicator for undiscovered
      const flowOffset = phase * 0.6;
      pool.pathSegments.forEach(seg => {
        const baseAlpha = seg.isUndiscovered ? (0.6 + fastSin(phase * 5) * 0.025) : 0.8;
        seg.glowLine.alpha = baseAlpha * 0.3;
        seg.line.alpha = baseAlpha;
        seg.numGlow.alpha = baseAlpha * 0.5;
        seg.dots.forEach(dot => {
          const t = ((dot.t + flowOffset) % 1);
          dot.sprite.alpha = Math.sin(t * Math.PI) * 0.6;
          dot.sprite.position.set(dot.startX + dot.dx * t, dot.startY + dot.dy * t);
        });
      });
      
      // Path target
      if (pool.pathTarget) {
        const tPulse = 1 + fastSin(phase * 2.5) * 0.008;
        const tAlpha = 0.6 + fastSin(phase * 3) * 0.015;
        pool.pathTarget.outer.alpha = tAlpha * 0.0175; pool.pathTarget.outer.scale.set(2.8 * tPulse);
        pool.pathTarget.inner.alpha = tAlpha * 0.025; pool.pathTarget.inner.scale.set(2.0 * tPulse);
        pool.pathTarget.ring.alpha = tAlpha;
        pool.pathTarget.icon.alpha = tAlpha + 0.2;
      }
      
      // ETA label
      if (pool.etaLabel) {
        pool.etaLabel.alpha = 0.7 + fastSin(phase * 2) * 0.02;
      }
      
      // Movement ghost
      if (pool.movementGhost) {
        pool.movementGhost.alpha = 0.2 + fastSin(phase * 3) * 0.015;
        pool.movementGhost.scale.set(0.9 + fastSin(phase * 2) * 0.01);
      }
      
      // Sparkles (also reduced)
      pool.sparkles.forEach(s => {
        if (s.type === 'adjacent') {
          // No animation for adjacent tiles - keep hidden
          s.sprite.visible = false;
        } else if (s.type === 'beacon') {
          const beacon = beaconLookupMap.get(`${s.tileQ},${s.tileR}`);
          if (beacon) {
            const angle = phase * 0.4 + s.index * (Math.PI / 2);
            s.sprite.alpha = 0.015 + fastSin(phase * 2 + s.index) * 0.00125;
            s.sprite.position.set(beacon.pos.x + Math.cos(angle) * BASE_HEX_SIZE * 1.15, beacon.pos.y + Math.sin(angle) * BASE_HEX_SIZE * 1.15);
            s.sprite.visible = beacon.outer.visible;
          }
        } else if (s.type === 'player') {
          s.sprite.alpha = Math.max(0, fastSin(phase * 1.5 + s.index * 1.05) * 0.00225 + 0.005);
          const angle = (s.index * Math.PI / 3) + phase * 0.25;
          s.sprite.position.set(s.baseX + Math.cos(angle) * BASE_HEX_SIZE * 1.2, s.baseY + Math.sin(angle) * BASE_HEX_SIZE * 1.2);
        } else if (s.type === 'teleport' && pool.teleport) {
          const angle = phase * 0.9 + s.index * (Math.PI / 3);
          const dist = BASE_HEX_SIZE * (1.05 + fastSin(phase * 3 + s.index) * 0.01);
          s.sprite.alpha = 0.02 + fastSin(phase * 2 + s.index) * 0.00125;
          s.sprite.position.set(s.baseX + Math.cos(angle) * dist, s.baseY + Math.sin(angle) * dist);
        }
      });

      // Update Great Work construction timer text every frame (without re-rendering tiles)
      if (pool.constructionTimer) {
        const progressInfo = getGreatWorkProgressInfo?.();
        if (progressInfo && progressInfo.remaining > 0) {
          const secondsLeft = Math.ceil(progressInfo.remaining);
          pool.constructionTimer.text = formatTime(secondsLeft);
        } else {
          // Construction complete or cancelled - remove timer
          // First remove from parent to prevent render errors, then destroy
          if (pool.constructionTimer.parent) {
            pool.constructionTimer.parent.removeChild(pool.constructionTimer);
          }
          pool.constructionTimer.destroy();
          pool.constructionTimer = null;
        }
      }
    };

    app.ticker.add(tickerCallback);
    return () => {
      if (pixiAppRef.current?.ticker) {
        pixiAppRef.current.ticker.remove(tickerCallback);
      }
    };
  }, [pixiReady, isInViewport, perfDebugEnabled]);

  // Cooldowns and expedition progress
  useEffect(() => {
    const interval = setInterval(() => {
      setMoveCooldown(getRemainingCooldown?.() || 0);
      setGatherCooldown(getGatherCooldownRemaining?.() || 0);
      // Update expedition progress
      if (isOnExpedition?.()) {
        setExpeditionProgress(getExpeditionProgress?.());
      } else {
        setExpeditionProgress(null);
      }
      // Update Great Work construction progress
      const gwProgress = getGreatWorkProgressInfo?.();
      setGreatWorkRemaining(gwProgress?.remaining || null);
      // Update outpost construction progress (force re-render)
      const hasConstructingOutpost = Object.values(worldMap?.outposts || {}).some(o => o.isConstructing);
      if (hasConstructingOutpost) {
        setOutpostConstructionKey(prev => prev + 1);
      }
    }, 1000); // Update every second for progress bars
    return () => clearInterval(interval);
  }, [getRemainingCooldown, getGatherCooldownRemaining, isOnExpedition, getExpeditionProgress, getGreatWorkProgressInfo, worldMap?.outposts]);

  const showMessage = useCallback((text, isError = false, combo = null) => {
    const id = Date.now() + Math.random();
    setMessages(prev => [...prev, { id, text, isError, combo }]);
    setTimeout(() => setMessages(prev => prev.filter(m => m.id !== id)), 1500); // Match animation duration (1.5s)
  }, []);

  // Track last shown expedition timestamp to avoid duplicate notifications
  const lastShownExpeditionRef = useRef(0);

  // Watch for completed expeditions and show modal
  useEffect(() => {
    if (!worldMap?.lastCompletedExpedition) return;

    const completion = worldMap.lastCompletedExpedition;
    const { timestamp } = completion;

    // Only show modal if we haven't shown this one yet
    if (timestamp <= lastShownExpeditionRef.current) return;
    lastShownExpeditionRef.current = timestamp;

    // Show the completion modal
    setExpeditionComplete(completion);
  }, [worldMap?.lastCompletedExpedition]);
  
  const addEventToLog = useCallback((event, biomeName, biomeIcon, scaledChanges) => {
    if (!event) return;
    let scaledRewards = null, scaledPenalties = null;
    if (scaledChanges) Object.entries(scaledChanges).forEach(([r, a]) => { if (a > 0) { scaledRewards = scaledRewards || {}; scaledRewards[r] = a; } else if (a < 0) { scaledPenalties = scaledPenalties || {}; scaledPenalties[r] = Math.abs(a); } });
    setEventLog(prev => [{ id: Date.now(), biomeName, biomeIcon, text: event.text, rewards: scaledRewards || event.rewards, penalties: scaledPenalties || event.penalties }, ...prev].slice(0, 10));
    setEventLogIndex(0);
  }, []);

  // Sync state with singletons and setup queue processor
  useEffect(() => {
    // Register callbacks for module-level queue processor
    queueCallbacks.moveToNode = moveToNode;
    queueCallbacks.teleportToNode = teleportToNode;
    queueCallbacks.getRemainingCooldown = getRemainingCooldown;
    queueCallbacks.BIOMES = BIOMES;
    queueCallbacks.onMove = (result, wasTeleport) => {
      if (wasTeleport) {
        showMessage(`Teleported to ${result.node?.isHub ? 'Kingdom' : BIOMES?.[result.node?.biome]?.name || 'Waystone'}!`);
      }
      // Track newly discovered tile for reveal animation
      if (result.isNewDiscovery && result.node?.coordinates) {
        const { q, r } = result.node.coordinates;
        const nodeId = `node_${q}_${r}`;
        revealingTilesRef.current[nodeId] = Date.now();
        setTimeout(() => { delete revealingTilesRef.current[nodeId]; }, 500);
      }
      if (result.event && result.node) {
        addEventToLog(result.event, BIOMES?.[result.node.biome]?.name, BIOMES?.[result.node.biome]?.icon, result.scaledChanges);
      }
      // Sync local state from singletons
      setMovementQueue([...movementQueueSingleton]);
      setPathPreview(pathPreviewSingleton ? { ...pathPreviewSingleton } : null);
    };
    queueCallbacks.onComplete = (wasTeleport, finalQ, finalR, finalNodeId) => {
      if (!wasTeleport) showMessage('Arrived!');
      setIsAutoMoving(false);
      setMovementQueue([]);
      setPathPreview(null);
      // Highlight the destination tile but don't set previewTile (which would hide gather UI)
      if (finalQ !== undefined && finalR !== undefined) {
        setClickedTile({ q: finalQ, r: finalR });
        // Don't set previewTile - we're now AT this location, so gather UI should be visible
      }
    };
    queueCallbacks.onError = (error) => {
      showMessage(error, true);
      setIsAutoMoving(false);
      setMovementQueue([]);
      setPathPreview(null);
    };
    
    // Start processor
    startQueueProcessor();
    
    return () => {
      // Don't stop processor - it should keep running
      // Just clear callbacks so it doesn't call stale functions
      queueCallbacks.onMove = null;
      queueCallbacks.onComplete = null;
      queueCallbacks.onError = null;
    };
  }, [moveToNode, teleportToNode, getRemainingCooldown, BIOMES, showMessage, addEventToLog]);

  // Sync local state to singletons when they change
  useEffect(() => {
    movementQueueSingleton = movementQueue;
    isAutoMovingSingleton = isAutoMoving;
    pathPreviewSingleton = pathPreview;
  }, [movementQueue, isAutoMoving, pathPreview]);

  // Sync from singletons periodically when mounted (in case queue processor updated them)
  useEffect(() => {
    const syncInterval = setInterval(() => {
      if (isAutoMovingSingleton !== isAutoMoving) setIsAutoMoving(isAutoMovingSingleton);
      if (movementQueueSingleton.length !== movementQueue.length) setMovementQueue([...movementQueueSingleton]);
      if (pathPreviewSingleton !== pathPreview) setPathPreview(pathPreviewSingleton ? { ...pathPreviewSingleton } : null);
    }, 200);
    return () => clearInterval(syncInterval);
  }, [isAutoMoving, movementQueue.length, pathPreview]);

  // Mouse move for hover
  const handleMouseMove = useCallback((e) => {
    if (isPanning) {
      if (Math.abs(e.clientX - dragStartPos.x) > 5 || Math.abs(e.clientY - dragStartPos.y) > 5) setHasDragged(true);
      setPanOffset({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
    } else {
      // PERF: Throttle hover state updates to ~15 FPS (66ms)
      const now = Date.now();
      if (now - lastHoverUpdateRef.current < 66) return;
      lastHoverUpdateRef.current = now;

      const app = pixiAppRef.current, wc = worldContainerRef.current;
      if (!app || !wc) return;
      const rect = app.canvas.getBoundingClientRect();
      const worldX = (e.clientX - rect.left - wc.position.x) / zoom;
      const worldY = (e.clientY - rect.top - wc.position.y) / zoom;
      const { q, r } = pixelToHex(worldX, worldY, BASE_HEX_SIZE);
      setHoveredTile({ q, r });
    }
  }, [isPanning, dragStartPos, panStart, zoom]);

  // Click handler
  const handleCanvasClick = useCallback((e) => {
    if (hasDragged) return;
    const app = pixiAppRef.current, wc = worldContainerRef.current;
    if (!app || !wc) return;

    const rect = app.canvas.getBoundingClientRect();
    const worldX = (e.clientX - rect.left - wc.position.x) / zoom;
    const worldY = (e.clientY - rect.top - wc.position.y) / zoom;
    const { q, r } = pixelToHex(worldX, worldY, BASE_HEX_SIZE);

    const discoveredNodes = worldMap?.discoveredNodes || {};
    const currentCoords = getCurrentCoords();
    const onCooldown = isMovementOnCooldown?.() || false;
    const nodeId = (q === 0 && r === 0) ? 'kingdom' : `node_${q}_${r}`;
    const node = discoveredNodes[nodeId];

    // Great Work placement mode - handle tile clicks
    if (greatWorkPlacement) {
      const { workId, validPlacements } = greatWorkPlacement;
      const GREAT_WORK_PLACEMENT_RANGE = 5;

      // Check if this is the kingdom tile
      if (q === 0 && r === 0) {
        onNotification?.('Cannot build Great Works on the Kingdom tile', 'warning');
        return;
      }

      const isValidPlacement = validPlacements.some(p => p.nodeId === nodeId);
      const distanceFromPlayer = hexDistanceUtil(currentCoords.q, currentCoords.r, q, r);

      if (isValidPlacement) {
        // Check distance restriction
        if (distanceFromPlayer > GREAT_WORK_PLACEMENT_RANGE) {
          onNotification?.(`Too far away! Move within ${GREAT_WORK_PLACEMENT_RANGE} tiles to build here`, 'warning');
          return;
        }

        const result = startGreatWorkConstruction?.(workId, nodeId);
        if (result?.success) {
          const workName = GREAT_WORKS[workId]?.name || 'Great Work';
          onNotification?.(`🔨 Started building ${workName}!`, 'success');
          onCancelGreatWorkPlacement?.();
        } else {
          onNotification?.(result?.reason || 'Failed to start construction', 'error');
        }
      } else if (node) {
        // Clicked on a discovered tile that's not valid - provide helpful message
        if (distanceFromPlayer > GREAT_WORK_PLACEMENT_RANGE) {
          onNotification?.('This tile is too far away. Move closer to see valid locations.', 'warning');
        } else {
          onNotification?.('This tile doesn\'t meet the biome requirements for this Great Work', 'warning');
        }
      }
      // Don't process normal tile interaction when in placement mode
      return;
    }

    // Building placement mode (from BuildingPanel "Build on Map" button)
    if (buildingPlacementMode) {
      const { buildingId, validTiles } = buildingPlacementMode;

      // Check if this is a valid placement tile
      if (validTiles.has(nodeId)) {
        const result = placeBuildingOnTile(nodeId);
        if (result?.success) {
          const buildingName = buildings[buildingId]?.name || 'Building';
          onNotification?.(`🔨 Built ${buildingName} on tile!`, 'success');
        } else {
          onNotification?.(result?.error || 'Failed to build', 'error');
        }
      } else if (node) {
        onNotification?.('Not a valid build location. Build in this biome first or connect via Trade Route.', 'warning');
      } else {
        // Clicked on undiscovered tile - cancel placement
        exitBuildingPlacementMode?.();
        onNotification?.('Cancelled placement mode', 'info');
      }
      return;
    }

    const now = Date.now();
    const isDoubleClick = (now - lastClickRef.current.time < 350) && (lastClickRef.current.nodeId === nodeId);
    lastClickRef.current = { time: now, nodeId };
    
    const startAutoMove = (path, targetQ, targetR, targetId, teleportInfo = null) => {
      if (path.length === 0) return;
      setMovementQueue(path); setIsAutoMoving(true);
      setPathPreview({ path, turns: path.length, targetQ, targetR, targetId, teleportFrom: teleportInfo });
      showMessage(`Auto-moving: ${path.length} steps`);
    };
    
    if (!isDoubleClick) { setClickedTile({ q, r }); const isOtherTile = node && !(q === currentCoords.q && r === currentCoords.r); setPreviewTile(isOtherTile ? nodeId : null); if (isOtherTile) setSelectedBuilding(null); return; }
    
    setClickedTile(null); setPreviewTile(null);
    
    if (node) {
      const isAdjacent = HEX_DIRECTIONS.some(d => currentCoords.q + d.q === q && currentCoords.r + d.r === r);
      if (q === currentCoords.q && r === currentCoords.r) { if (isAutoMoving) { setIsAutoMoving(false); setMovementQueue([]); setPathPreview(null); setPreviewTile(null); setClickedTile(null); showMessage('Cancelled'); } return; }

      // Dev teleport mode - instant teleport to any discovered tile
      if (devTeleportMode) {
        const result = teleportToNode(nodeId);
        if (result.success) { showMessage('Teleported!'); setPathPreview(null); setIsAutoMoving(false); setMovementQueue([]); }
        else showMessage(result.error, true);
        return;
      }

      if (isAdjacent) {
        if (onCooldown) { setMovementQueue([{ q, r, discovered: true }]); setIsAutoMoving(true); setPathPreview({ path: [{ q, r, discovered: true }], turns: 1, targetQ: q, targetR: r, targetId: nodeId }); showMessage('Queued'); }
        else { const result = moveToNode(q, r); if (!result.success) showMessage(result.error, true); else { setPathPreview(null); setIsAutoMoving(false); setMovementQueue([]); } }
      } else {
        const biome = BIOMES?.[node.biome];
        const outpostAtTile = stableOutposts[nodeId];
        const isCompletedOutpost = outpostAtTile && !outpostAtTile.isConstructing;
        const isBeacon = biome?.isTeleportBeacon || node.isHub || isCompletedOutpost;

        if (isBeacon) {
          if (onCooldown) { setMovementQueue([{ q, r, discovered: true, isTeleport: true, nodeId: node.id }]); setIsAutoMoving(true); setPathPreview({ path: [{ q, r, discovered: true, isTeleport: true }], turns: 1, targetQ: q, targetR: r, targetId: nodeId }); showMessage('Teleport queued'); }
          else { const result = teleportToNode(node.id); if (!result.success) showMessage(result.error, true); else { showMessage(`Teleported!`); setPathPreview(null); setIsAutoMoving(false); setMovementQueue([]); } }
        } else {
          if (isAutoMoving) { setIsAutoMoving(false); setMovementQueue([]); setPathPreview(null); }
          
          // Check if at exploration limit - if so, only use discovered tiles (PERF: use memoized count)
          const maxExploration = getExplorationLimit?.() || 30;
          const atExplorationLimit = discoveredCount >= maxExploration;
          const pathOptions = atExplorationLimit ? { discoveredOnly: true } : {};
          
          const directPath = findPathToTile?.(q, r, pathOptions);
          const directLength = directPath?.path?.length ?? Infinity;
          // PERF: Use memoized teleport options
          let bestOption = null, bestLength = directLength;
          for (const opt of teleportOptions) { const p = findPathToTile?.(q, r, { startFromNode: opt.nodeId, ...pathOptions }); if (p?.path?.length < bestLength) { bestLength = p.path.length; bestOption = { ...opt, path: p.path }; } }
          
          if (bestOption && bestLength < directLength) {
            if (onCooldown) {
              setMovementQueue([{ q: bestOption.q, r: bestOption.r, discovered: true, isTeleport: true, nodeId: bestOption.nodeId }, ...bestOption.path]);
              setIsAutoMoving(true);
              setPathPreview({ path: bestOption.path, turns: bestOption.path.length + 1, targetQ: q, targetR: r, targetId: node.id, teleportFrom: { q: bestOption.q, r: bestOption.r, name: bestOption.name } });
              showMessage(`Will teleport to ${bestOption.name}`);
            } else {
              const tr = teleportToNode(bestOption.nodeId);
              if (tr.success) { if (bestOption.path.length > 0) { showMessage(`Teleported!`); startAutoMove(bestOption.path, q, r, node.id, null); } else { showMessage(`Teleported!`); setPathPreview(null); } }
              else showMessage(tr.error || 'Failed', true);
            }
          } else if (directPath?.path?.length > 0) startAutoMove(directPath.path, q, r, node.id);
          else showMessage(atExplorationLimit ? 'No path (exploration capped)' : 'No path', true);
        }
      }
      return;
    }
    
    const isAdjacentToPlayer = HEX_DIRECTIONS.some(d => currentCoords.q + d.q === q && currentCoords.r + d.r === r);
    if (isAdjacentToPlayer) {
      // Check exploration limit before allowing move to undiscovered tile (PERF: use memoized count)
      const maxExploration = getExplorationLimit?.() || 30;
      if (discoveredCount >= maxExploration) {
        showMessage('Exploration limit reached!', true);
        return;
      }
      
      if (onCooldown) { setMovementQueue([{ q, r, discovered: false }]); setIsAutoMoving(true); setPathPreview({ path: [{ q, r, discovered: false }], turns: 1, targetQ: q, targetR: r, targetId: `node_${q}_${r}` }); showMessage('Queued'); }
      else {
        const result = moveToNode(q, r);
        if (!result.success) showMessage(result.error, true);
        else {
          // Track newly discovered tile for reveal animation
          if (result.isNewDiscovery) {
            const nodeId = `node_${q}_${r}`;
            revealingTilesRef.current[nodeId] = Date.now();
            // Clean up after animation (500ms)
            setTimeout(() => { delete revealingTilesRef.current[nodeId]; }, 500);
          }
          if (result.isDungeonBiome) {
            // Dungeon biome discovered - don't auto-show modal, just log the event if any
            if (result.event) addEventToLog(result.event, BIOMES?.[result.node.biome]?.name, BIOMES?.[result.node.biome]?.icon, result.scaledChanges);
          } else if (result.event) addEventToLog(result.event, BIOMES?.[result.node.biome]?.name, BIOMES?.[result.node.biome]?.icon, result.scaledChanges);
        }
        setPathPreview(null); setIsAutoMoving(false); setMovementQueue([]);
      }
      return;
    }
    
    if (isAutoMoving) { setIsAutoMoving(false); setMovementQueue([]); setPathPreview(null); }
    
    // Check exploration limit before allowing path to undiscovered tile (PERF: use memoized count)
    const maxExploration = getExplorationLimit?.() || 30;
    if (discoveredCount >= maxExploration) {
      showMessage('Exploration limit reached!', true);
      return;
    }
    
    const directPath = findPathToTile?.(q, r);
    const directLength = directPath?.path?.length ?? Infinity;
    // PERF: Use memoized teleport options
    let bestOption = null, bestLength = directLength;
    for (const opt of teleportOptions) { const p = findPathToTile?.(q, r, { startFromNode: opt.nodeId }); if (p?.path?.length < bestLength) { bestLength = p.path.length; bestOption = { ...opt, path: p.path }; } }
    
    if (bestOption && bestLength < directLength) {
      if (onCooldown) {
        setMovementQueue([{ q: bestOption.q, r: bestOption.r, discovered: true, isTeleport: true, nodeId: bestOption.nodeId }, ...bestOption.path]);
        setIsAutoMoving(true);
        setPathPreview({ path: bestOption.path, turns: bestOption.path.length + 1, targetQ: q, targetR: r, targetId: `node_${q}_${r}`, teleportFrom: { q: bestOption.q, r: bestOption.r, name: bestOption.name } });
        showMessage(`Will teleport to ${bestOption.name}`);
      } else {
        const tr = teleportToNode(bestOption.nodeId);
        if (tr.success) { if (bestOption.path.length > 0) { showMessage(`Teleported!`); startAutoMove(bestOption.path, q, r, `node_${q}_${r}`, null); } else { showMessage(`Teleported!`); setPathPreview(null); } }
        else showMessage(tr.error || 'Failed', true);
      }
    } else if (directPath?.path?.length > 0) startAutoMove(directPath.path, q, r, `node_${q}_${r}`);
    else showMessage('Cannot reach', true);
  }, [worldMap, hasDragged, getCurrentCoords, isMovementOnCooldown, moveToNode, teleportToNode, findPathToTile, BIOMES, beaconNodes, teleportOptions, discoveredCount, isAutoMoving, showMessage, addEventToLog, zoom, getExplorationLimit, greatWorkPlacement, startGreatWorkConstruction, onNotification, onCancelGreatWorkPlacement, devTeleportMode, stableOutposts]);

  const handleWheel = useCallback((e) => { e.preventDefault(); setZoom(prev => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev + (e.deltaY > 0 ? -0.1 : 0.1)))); }, []);

  // Set up wheel event listener with { passive: false } to allow preventDefault
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  const handleMouseDown = (e) => { if (e.button === 0) { e.preventDefault(); setIsPanning(true); setHasDragged(false); setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y }); setDragStartPos({ x: e.clientX, y: e.clientY }); } };
  const handleMouseUp = (e) => { if (e.button === 0) setIsPanning(false); };
  const centerOnPlayer = () => { const c = getCurrentCoords(); const p = hexToPixel(c.q, c.r, BASE_HEX_SIZE); setPanOffset({ x: -p.x, y: -p.y }); setPreviewTile(null); setClickedTile(null); };
  const handleGather = () => {
    const r = gatherFromNode();
    if (r.success) {
      // Trigger gather animation on the CURRENT tile (where player is standing)
      const currentCoords = getCurrentCoords();
      const tilePos = hexToPixel(currentCoords.q, currentCoords.r, BASE_HEX_SIZE);
      gatherAnimation.tileShake = 1.0;
      gatherAnimation.tilePulse = 1.0;
      gatherAnimation.gatheredTilePos = { x: tilePos.x, y: tilePos.y }; // Save position

      // Create flying particles (3-5 particles per gather)
      const particleCount = 3 + Math.floor(Math.random() * 3);
      for (let i = 0; i < particleCount; i++) {
        const angle = Math.random() * Math.PI * 2;
        const distance = BASE_HEX_SIZE * 0.3;
        gatherAnimation.particles.push({
          startX: tilePos.x + Math.cos(angle) * distance,
          startY: tilePos.y + Math.sin(angle) * distance,
          x: tilePos.x + Math.cos(angle) * distance,
          y: tilePos.y + Math.sin(angle) * distance,
          targetX: tilePos.x,
          targetY: tilePos.y - BASE_HEX_SIZE * 0.8, // Fly upward
          progress: 0,
          life: 1.0,
          delay: i * 0.05 // Slight stagger
        });
      }

      // Increment counter (no max, just keeps going up!)
      setGatherCombo(prev => prev + 1);

      // Reset counter timeout
      if (comboTimeoutRef.current) clearTimeout(comboTimeoutRef.current);
      comboTimeoutRef.current = setTimeout(() => {
        setGatherCombo(0);
        setMessages([]); // Clear message when counter resets
      }, 1500); // Counter resets after 1.5s of no gathering

      const newCounter = gatherCombo + 1;
      const resourceText = Object.entries(r.gathered).map(([res, amt]) => `+${amt} ${getResourceIcon(res)} ${res}`).join(', ');

      // Update existing message or create new one - use timestamp as key to force re-render/pulse
      const msgId = 'gather-msg'; // Fixed ID so we replace the same message
      const pulseKey = Date.now(); // Unique key to trigger pulse animation
      setMessages([{ id: msgId, text: resourceText, isError: false, combo: newCounter, pulseKey }]);
    } else {
      setMessages([{ id: 'error-msg', text: r.error, isError: true }]);
    }
  };
  const handleBuildOnTile = (id) => {
    const r = buildOnTile(id);
    if (r.success) {
      showMessage(`✓ Built!`);
      setSelectedBuilding(null);
    } else showMessage(r.error, true);
  };

  const currentNodeData = getCurrentNodeData?.() || null;
  const expectedYields = calculateGatherYield?.(worldMap?.currentNode) || null;
  const tileQuality = currentNodeData?.tileQuality || worldMap?.discoveredNodes?.[worldMap?.currentNode]?.tileQuality;
  const availableBuildings = getAvailableTileBuildings?.() || [];

  // Check if current tile is a kingdom/city-state - check BIOMES directly as fallback
  const currentNodeBiomeId = worldMap?.discoveredNodes?.[worldMap?.currentNode]?.biome;
  const isCurrentTileKingdom = currentNodeData?.biomeData?.isKingdomBiome || BIOMES?.[currentNodeBiomeId]?.isKingdomBiome;

  // Buildings available for preview tile (if one is selected)
  const previewTileBuildings = previewTile ? (getAvailableTileBuildings?.(previewTile) || []) : [];
  const totalTileBuildings = Object.keys(worldMap?.tileBuildings || {}).length;
  const totalDiscovered = discoveredCount; // PERF: use memoized count
  const explorationLimit = getExplorationLimit?.() || 30;

  // Track dungeon cooldown for current node
  useEffect(() => {
    if (!currentNodeData?.biomeData?.isDungeonBiome || !currentNodeData?.id) {
      setDungeonCooldownMs(null);
      return;
    }

    const updateCooldown = () => {
      const remaining = getDungeonCooldown(currentNodeData.id);
      setDungeonCooldownMs(remaining);
    };

    // Initial update
    updateCooldown();

    // Update every second while on cooldown
    const interval = setInterval(updateCooldown, 1000);

    return () => clearInterval(interval);
  }, [currentNodeData, getDungeonCooldown]);

  // Track encounter cooldown for current node
  useEffect(() => {
    if (!currentNodeData?.biomeData?.isEncounterBiome || !currentNodeData?.id) {
      setEncounterCooldownMs(null);
      return;
    }

    const updateCooldown = () => {
      const remaining = getDungeonCooldown(currentNodeData.id); // Uses same cooldown system
      setEncounterCooldownMs(remaining);
    };

    // Initial update
    updateCooldown();

    // Update every second while on cooldown
    const interval = setInterval(updateCooldown, 1000);

    return () => clearInterval(interval);
  }, [currentNodeData, getDungeonCooldown]);

  // Track tower cooldown for current node
  useEffect(() => {
    if (!currentNodeData?.biomeData?.isTowerBiome || !currentNodeData?.id) {
      setTowerCooldownMs(null);
      return;
    }

    const updateCooldown = () => {
      const remaining = getDungeonCooldown(currentNodeData.id); // Uses same cooldown system
      setTowerCooldownMs(remaining);
    };

    // Initial update
    updateCooldown();

    // Update every second while on cooldown
    const interval = setInterval(updateCooldown, 1000);

    return () => clearInterval(interval);
  }, [currentNodeData, getDungeonCooldown]);

  // Show pending modal after tutorial is dismissed
  useEffect(() => {
    if (pendingModal && !currentTutorial) {
      // Tutorial was dismissed, now show the pending modal
      if (pendingModal.type === 'dungeon') {
        setDungeonEntryBiomeId(pendingModal.biomeId);
        setDungeonEntryNodeId(pendingModal.nodeId);
      } else if (pendingModal.type === 'encounter') {
        setEncounterEntryBiomeId(pendingModal.biomeId);
        setEncounterEntryNodeId(pendingModal.nodeId);
      } else if (pendingModal.type === 'tower') {
        setTowerEntryBiomeId(pendingModal.biomeId);
        setTowerEntryNodeId(pendingModal.nodeId);
      } else if (pendingModal.type === 'outpost') {
        setOutpostCreationNodeId(pendingModal.nodeId);
      } else if (pendingModal.type === 'tileBuild') {
        setShowTileBuildingModal(true);
      }
      setPendingModal(null);
    }
  }, [currentTutorial, pendingModal]);

  // Compute exploration tech bonus breakdown (values from balance.js)
  const explorationBonusInfo = useMemo(() => {
    const baseLimit = EXPLORATION.baseLimit;
    const unlocked = [];
    const locked = [];
    let totalBonus = 0;

    Object.entries(EXPLORATION_TECH_BONUSES).forEach(([techId, info]) => {
      if (researchedTechs?.includes(techId)) {
        unlocked.push({ ...info, techId });
        totalBonus += info.bonus;
      } else {
        locked.push({ ...info, techId });
      }
    });

    return { baseLimit, unlocked, locked, totalBonus, maxPossible: baseLimit + Object.values(EXPLORATION_TECH_BONUSES).reduce((sum, t) => sum + t.bonus, 0) };
  }, [researchedTechs]);
  const moveOnCooldown = isMovementOnCooldown?.() || false;
  const gatherOnCooldown = isGatherOnCooldown?.() || false;

  // Tutorial callout for first-time players
  const showTutorialCallout = totalDiscovered <= 1;

  // Calculate player's screen position for tutorial callout
  const playerScreenPos = useMemo(() => {
    const coords = getCurrentCoords();
    const worldPos = hexToPixel(coords.q, coords.r, BASE_HEX_SIZE);
    return {
      x: (worldPos.x * zoom) + viewportSize.width / 2 + panOffset.x * zoom,
      y: (worldPos.y * zoom) + viewportSize.height / 2 + panOffset.y * zoom,
    };
  }, [getCurrentCoords, zoom, panOffset, viewportSize]);

  return (
    <div className="wm-layout">
      <div className="wm-map-area">
        <div className="wm-toast-stack">
          {messages.map((msg) => (
            <div
              key={msg.pulseKey || msg.id}
              className={`wm-toast ${msg.isError ? 'error' : 'success'} ${msg.combo ? 'combo-pulse wm-toast-pulse-anim' : ''}`}
            >
              {msg.combo && msg.combo > 1 && (
                <span className="wm-combo-multiplier">x{msg.combo}</span>
              )}
              {msg.text}
            </div>
          ))}
        </div>

        {/* Great Work Placement Mode Banner */}
        {greatWorkPlacement && (() => {
          // Calculate nearby valid placements (within 5 tiles of player)
          const currentCoords = getCurrentCoords();
          const nearbyCount = greatWorkPlacement.validPlacements.filter(({ nodeId }) => {
            const match = nodeId.match(/node_(-?\d+)_(-?\d+)/);
            if (!match) return false;
            const q = parseInt(match[1]), r = parseInt(match[2]);
            if (q === 0 && r === 0) return false; // Exclude kingdom
            const dist = hexDistanceUtil(currentCoords.q, currentCoords.r, q, r);
            return dist <= 5;
          }).length;
          const totalCount = greatWorkPlacement.validPlacements.filter(({ nodeId }) => {
            const match = nodeId.match(/node_(-?\d+)_(-?\d+)/);
            if (!match) return false;
            const q = parseInt(match[1]), r = parseInt(match[2]);
            return !(q === 0 && r === 0);
          }).length;

          return (
            <div className="wm-great-work-banner">
              <div className="wm-gw-banner-content">
                <span className="wm-gw-icon">{GREAT_WORKS[greatWorkPlacement.workId]?.icon || '🗿'}</span>
                <div className="wm-gw-info">
                  <span className="wm-gw-title">Placing: {GREAT_WORKS[greatWorkPlacement.workId]?.name}</span>
                  <span className="wm-gw-hint">
                    {nearbyCount > 0
                      ? `Click a blue tile nearby (${nearbyCount} within range${totalCount > nearbyCount ? `, ${totalCount} total` : ''})`
                      : `Move closer to a valid location (${totalCount} available on map)`
                    }
                  </span>
                </div>
                <button className="wm-gw-cancel" onClick={onCancelGreatWorkPlacement}>
                  Cancel
                </button>
              </div>
            </div>
          );
        })()}

        <div ref={containerRef} className="wm-canvas-wrap" onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={() => { setIsPanning(false); setHoveredTile(null); }} onContextMenu={e => { e.preventDefault(); return false; }} onClick={handleCanvasClick} />

        {/* Wonder site hover tooltip - cinematic Dark Souls style */}
        {(() => {
          if (!hoveredTile) return null;
          const hoveredNodeId = `node_${hoveredTile.q}_${hoveredTile.r}`;
          const hoveredNode = worldMap?.discoveredNodes?.[hoveredNodeId];
          if (!hoveredNode?.wonderSiteId) return null;
          const wonderSiteData = worldMap?.wonderSites?.[hoveredNode.wonderSiteId];
          if (!wonderSiteData?.fullyRevealed) return null;
          const wonder = WONDERS[hoveredNode.wonderSiteId];
          if (!wonder) return null;

          // Calculate CENTER of the wonder site (average of all 3 tiles)
          const worldContainer = worldContainerRef.current;
          if (!worldContainer) return null;

          const sitePositions = wonderSiteData.tileDefinitions.map(tileDef => {
            const pos = hexToPixel(tileDef.coordinates.q, tileDef.coordinates.r, BASE_HEX_SIZE);
            return { x: pos.x * zoom + worldContainer.position.x, y: pos.y * zoom + worldContainer.position.y };
          });
          const centerX = sitePositions.reduce((sum, p) => sum + p.x, 0) / sitePositions.length;
          const centerY = sitePositions.reduce((sum, p) => sum + p.y, 0) / sitePositions.length;

          return (
            <div
              className="wm-wonder-cinematic-tooltip"
              style={{
                position: 'absolute',
                left: `${centerX}px`,
                top: `${centerY}px`,
                transform: `translate(-50%, -50%) scale(${zoom})`,
                transformOrigin: 'center center',
                pointerEvents: 'none',
                zIndex: 200,
              }}
            >
              {/* Dark shadow backdrop */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '200%',
                height: '300%',
                background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 40%, transparent 70%)',
                pointerEvents: 'none',
                zIndex: -1,
                animation: 'wm-cinematic-fade-in 0.5s ease-out forwards',
              }} />
              <div style={{
                position: 'relative',
                color: '#f0e8d8',
                padding: '5px 14px',
                fontSize: '12px', // Scales with zoom via parent transform
                fontWeight: 300,
                fontFamily: '"Palatino Linotype", "Book Antiqua", Palatino, serif',
                letterSpacing: '2.5px',
                textTransform: 'uppercase',
                textShadow: '0 1px 8px rgba(0,0,0,0.95), 0 0 25px rgba(0,0,0,0.8), 0 0 35px rgba(0,0,0,0.5)',
                whiteSpace: 'nowrap',
                textAlign: 'center',
                animation: 'wm-cinematic-fade-in 0.5s ease-out forwards',
              }}>
                {wonder.name}
              </div>
            </div>
          );
        })()}

        {/* Great Work hover tooltip - cinematic Dark Souls style */}
        {(() => {
          if (!hoveredTile) return null;
          const hoveredNodeId = `node_${hoveredTile.q}_${hoveredTile.r}`;
          const hoveredNode = worldMap?.discoveredNodes?.[hoveredNodeId];
          if (!hoveredNode) return null;

          // Check if this tile has a completed Great Work (PERF: O(1) lookup)
          const completedGreatWorkId = nodeIdToGreatWork[hoveredNodeId];
          if (!completedGreatWorkId) return null;
          const greatWork = GREAT_WORKS[completedGreatWorkId];
          if (!greatWork) return null;

          // Calculate position for the tooltip (on the hovered tile)
          const worldContainer = worldContainerRef.current;
          if (!worldContainer) return null;

          const pos = hexToPixel(hoveredTile.q, hoveredTile.r, BASE_HEX_SIZE);
          const centerX = pos.x * zoom + worldContainer.position.x;
          const centerY = pos.y * zoom + worldContainer.position.y;

          return (
            <div
              className="wm-greatwork-cinematic-tooltip"
              style={{
                position: 'absolute',
                left: `${centerX}px`,
                top: `${centerY}px`,
                transform: `translate(-50%, -50%) scale(${zoom})`,
                transformOrigin: 'center center',
                pointerEvents: 'none',
                zIndex: 200,
              }}
            >
              {/* Dark shadow backdrop */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '200%',
                height: '300%',
                background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 40%, transparent 70%)',
                pointerEvents: 'none',
                zIndex: -1,
                animation: 'wm-cinematic-fade-in 0.5s ease-out forwards',
              }} />
              <div style={{
                position: 'relative',
                color: '#e8d8f0',
                padding: '5px 14px',
                fontSize: '12px',
                fontWeight: 300,
                fontFamily: '"Palatino Linotype", "Book Antiqua", Palatino, serif',
                letterSpacing: '2.5px',
                textTransform: 'uppercase',
                textShadow: '0 1px 8px rgba(0,0,0,0.95), 0 0 25px rgba(0,0,0,0.8), 0 0 35px rgba(0,0,0,0.5)',
                whiteSpace: 'nowrap',
                textAlign: 'center',
                animation: 'wm-cinematic-fade-in 0.5s ease-out forwards',
              }}>
                {greatWork.name}
              </div>
            </div>
          );
        })()}

        <style>{`
          @keyframes wm-cinematic-fade-in {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }
        `}</style>

        {/* Tutorial callout for first exploration */}
        {showTutorialCallout && (
          <div
            className="wm-tutorial-callout"
            style={{
              position: 'absolute',
              left: `${playerScreenPos.x}px`,
              top: `${playerScreenPos.y - BASE_HEX_SIZE * zoom}px`,
              transform: `translate(-50%, -100%) scale(${zoom})`,
              transformOrigin: 'bottom center',
              pointerEvents: 'none',
              zIndex: 100,
            }}
          >
            <div style={{
              background: 'linear-gradient(135deg, rgba(255,200,100,0.95) 0%, rgba(255,170,50,0.95) 100%)',
              color: '#1a1a2e',
              padding: '6px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 600,
              boxShadow: '0 3px 12px rgba(255,180,50,0.4)',
              animation: 'wm-tutorial-pulse 2s ease-in-out infinite',
              whiteSpace: 'nowrap',
            }}>
              👆 Double-click to explore!
            </div>
            <div style={{
              width: 0,
              height: 0,
              borderLeft: '7px solid transparent',
              borderRight: '7px solid transparent',
              borderTop: '7px solid rgba(255,170,50,0.95)',
              margin: '0 auto',
            }} />
          </div>
        )}
        
        <style>{`
          @keyframes wm-tutorial-pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.85; }
          }
        `}</style>
        <div className="wm-explore-row">
          <ExplorationLimitBar
            totalDiscovered={totalDiscovered}
            explorationLimit={explorationLimit}
            explorationBonusInfo={explorationBonusInfo}
          />
          <button className="wm-help-btn" onClick={() => setShowHelpModal(true)}>?</button>
          <button
            className={`wm-help-btn wm-perf-btn ${perfDebugEnabled ? 'active' : ''}`}
            onClick={() => setPerfDebugEnabled(p => !p)}
            title="Toggle Performance Debug"
            style={{ marginLeft: '4px', fontSize: '0.65rem' }}
          >📊</button>
        </div>
        {perfDebugEnabled && (
          <div className="wm-perf-overlay" style={{
            position: 'absolute', top: '40px', right: '10px', background: 'rgba(0,0,0,0.9)',
            color: '#0f0', fontFamily: 'monospace', fontSize: '11px', padding: '8px 10px',
            borderRadius: '4px', zIndex: 100, lineHeight: 1.5, minWidth: '180px'
          }}>
            <div style={{ borderBottom: '1px solid #0f0', paddingBottom: '4px', marginBottom: '4px', fontWeight: 'bold' }}>
              PERFORMANCE
            </div>
            <div>FPS: {perfStats.fps}</div>
            <div style={{ borderBottom: '1px solid #333', paddingBottom: '4px', marginTop: '4px', marginBottom: '4px' }}></div>

            <div style={{ color: '#ff0', fontWeight: 'bold', marginTop: '4px' }}>RENDERING</div>
            <div>Total Objects: {perfStats.objects}</div>
            <div>Fog Tiles: {perfStats.fogTiles}</div>
            <div>Memory: ~{perfStats.memoryMB} MB</div>
            <div style={{ borderBottom: '1px solid #333', paddingBottom: '4px', marginTop: '4px', marginBottom: '4px' }}></div>

            <div style={{ color: '#0ff', fontWeight: 'bold', marginTop: '4px' }}>TILES</div>
            <div>Discovered: {discoveredCount}</div>
            <div>Cached: {perfStats.tilesCached}</div>
            <div style={{ color: perfStats.tilesVisible < perfStats.tilesTotal ? '#0f0' : '#f80' }}>
              Visible: {perfStats.tilesVisible} / {perfStats.tilesTotal}
              {perfStats.tilesTotal > 0 && ` (${Math.round(perfStats.tilesVisible / perfStats.tilesTotal * 100)}%)`}
            </div>
            <div style={{ borderBottom: '1px solid #333', paddingBottom: '4px', marginTop: '4px', marginBottom: '4px' }}></div>

            <div style={{ color: '#f0f', fontWeight: 'bold', marginTop: '4px' }}>BORDERS</div>
            <div>Total: {perfStats.bordersTotal}</div>
            <div style={{ color: perfStats.bordersVisible < perfStats.bordersTotal ? '#0f0' : '#f80' }}>
              Visible: {perfStats.bordersVisible}
              {perfStats.bordersTotal > 0 && ` (${Math.round(perfStats.bordersVisible / perfStats.bordersTotal * 100)}%)`}
            </div>
            <div style={{ borderBottom: '1px solid #333', paddingBottom: '4px', marginTop: '4px', marginBottom: '4px' }}></div>

            <div style={{ color: '#fa0', fontWeight: 'bold', marginTop: '4px' }}>WONDER SITES</div>
            <div>Total: {perfStats.wonderSitesTotal}</div>
            <div style={{ color: perfStats.wonderSitesVisible < perfStats.wonderSitesTotal ? '#0f0' : '#f80' }}>
              Visible: {perfStats.wonderSitesVisible}
              {perfStats.wonderSitesTotal > 0 && ` (${Math.round(perfStats.wonderSitesVisible / perfStats.wonderSitesTotal * 100)}%)`}
            </div>

            <div style={{ borderTop: '1px solid #0f0', paddingTop: '4px', marginTop: '6px', fontSize: '9px', color: '#888' }}>
              {perfStats.tilesVisible < perfStats.tilesTotal * 0.5 ? '✅ Culling Active' : '⚠️ Most Tiles Visible'}
            </div>
          </div>
        )}
        <div className="wm-trade-route-container">
          <div
            className={`wm-trade-route-bar ${tradeRouteOverlay ? 'active' : ''}`}
            onClick={() => {
              const newValue = !tradeRouteOverlay;
              setTradeRouteOverlay(newValue);
              // Signal action for guided tour
              if (newValue) {
                triggerGuidedTourAction?.('tradeRouteOverlayOn');
              }
            }}
            title="Toggle Trade Route View"
          >
            <span className="wm-tr-bar-icon">🛤️</span>
            <span className="wm-tr-bar-status">{tradeRouteOverlay ? 'ON' : 'OFF'}</span>
          </div>
        </div>
        <MapControls
          zoom={zoom}
          setZoom={setZoom}
          onCenterPlayer={centerOnPlayer}
        />
        <div className="wm-map-hint">Drag to pan • Scroll to zoom • Double-click to move</div>
      </div>
      <div className="wm-side-panel">
        {/* Special handling for dungeon biomes */}
        {currentNodeData?.biomeData?.isDungeonBiome ? (
          <>
            {/* Show tile preview if another tile is selected, otherwise show dungeon UI */}
            {previewTile ? (
              <TilePreviewPanel
                previewTile={previewTile}
                worldMap={worldMap}
                BIOMES={BIOMES}
                buildings={buildings}
                buildingCounts={buildingCounts}
                availableBuildings={previewTileBuildings}
                calculateGatherYield={calculateGatherYield}
                onClose={() => { setPreviewTile(null); setClickedTile(null); }}
                currentGreatWorkConstruction={currentGreatWorkConstruction}
                greatWorkLocations={greatWorkLocations}
                getGreatWorkProgressInfo={getGreatWorkProgressInfo}
                greatWorkRemaining={greatWorkRemaining}
                isOnTradeRoute={isTileOnTradeRoute(previewTile)}
                hasTradeRouteSegment={!!worldMap.tradeRoutes?.segments?.[previewTile]?.built}
                onCreateOutpost={handleCreateOutpost}
                onManageOutpost={(nodeId) => setOutpostManageNodeId(nodeId)}
                getOutpostConstructionProgress={getOutpostConstructionProgress}
                currentNodeId={worldMap?.currentNode}
                onActivateShrine={handleActivateShrine}
              />
            ) : (
              <>
                <LocationCard
                  currentNodeData={currentNodeData}
                />
                <CombatEntrySection
                  type="dungeon"
                  biomeData={currentNodeData.biomeData}
                  nodeId={currentNodeData.id}
                  cooldownMs={dungeonCooldownMs}
                  completedTutorials={completedTutorials}
                  onTriggerTutorial={triggerTutorial}
                  onSetPendingModal={setPendingModal}
                  onOpenModal={(biomeId, nodeId) => {
                    setDungeonEntryBiomeId(biomeId);
                    setDungeonEntryNodeId(nodeId);
                  }}
                />
              </>
            )}
          </>
        ) : currentNodeData?.biomeData?.isEncounterBiome ? (
          /* Encounter biomes - simpler mini-dungeons with no level requirements */
          <>
            {/* Show tile preview if another tile is selected, otherwise show encounter UI */}
            {previewTile ? (
              <TilePreviewPanel
                previewTile={previewTile}
                worldMap={worldMap}
                BIOMES={BIOMES}
                buildings={buildings}
                buildingCounts={buildingCounts}
                availableBuildings={previewTileBuildings}
                calculateGatherYield={calculateGatherYield}
                onClose={() => { setPreviewTile(null); setClickedTile(null); }}
                currentGreatWorkConstruction={currentGreatWorkConstruction}
                greatWorkLocations={greatWorkLocations}
                getGreatWorkProgressInfo={getGreatWorkProgressInfo}
                greatWorkRemaining={greatWorkRemaining}
                isOnTradeRoute={isTileOnTradeRoute(previewTile)}
                hasTradeRouteSegment={!!worldMap.tradeRoutes?.segments?.[previewTile]?.built}
                onCreateOutpost={handleCreateOutpost}
                onManageOutpost={(nodeId) => setOutpostManageNodeId(nodeId)}
                getOutpostConstructionProgress={getOutpostConstructionProgress}
                currentNodeId={worldMap?.currentNode}
                onActivateShrine={handleActivateShrine}
              />
            ) : (
              <>
                <LocationCard
                  currentNodeData={currentNodeData}
                />
                <CombatEntrySection
                  type="encounter"
                  biomeData={currentNodeData.biomeData}
                  nodeId={currentNodeData.id}
                  cooldownMs={encounterCooldownMs}
                  completedTutorials={completedTutorials}
                  onTriggerTutorial={triggerTutorial}
                  onSetPendingModal={setPendingModal}
                  onOpenModal={(biomeId, nodeId) => {
                    setEncounterEntryBiomeId(biomeId);
                    setEncounterEntryNodeId(nodeId);
                  }}
                />
              </>
            )}
          </>
        ) : currentNodeData?.biomeData?.isTowerBiome ? (
          /* Tower biomes - multi-floor dungeons */
          <>
            {/* Show tile preview if another tile is selected, otherwise show tower UI */}
            {previewTile ? (
              <TilePreviewPanel
                previewTile={previewTile}
                worldMap={worldMap}
                BIOMES={BIOMES}
                buildings={buildings}
                buildingCounts={buildingCounts}
                availableBuildings={previewTileBuildings}
                calculateGatherYield={calculateGatherYield}
                onClose={() => { setPreviewTile(null); setClickedTile(null); }}
                currentGreatWorkConstruction={currentGreatWorkConstruction}
                greatWorkLocations={greatWorkLocations}
                getGreatWorkProgressInfo={getGreatWorkProgressInfo}
                greatWorkRemaining={greatWorkRemaining}
                isOnTradeRoute={isTileOnTradeRoute(previewTile)}
                hasTradeRouteSegment={!!worldMap.tradeRoutes?.segments?.[previewTile]?.built}
                onCreateOutpost={handleCreateOutpost}
                onManageOutpost={(nodeId) => setOutpostManageNodeId(nodeId)}
                getOutpostConstructionProgress={getOutpostConstructionProgress}
                currentNodeId={worldMap?.currentNode}
                onActivateShrine={handleActivateShrine}
              />
            ) : (
              <>
                <LocationCard
                  currentNodeData={currentNodeData}
                />
                <CombatEntrySection
                  type="tower"
                  biomeData={currentNodeData.biomeData}
                  nodeId={currentNodeData.id}
                  cooldownMs={towerCooldownMs}
                  completedTutorials={completedTutorials}
                  onTriggerTutorial={triggerTutorial}
                  onSetPendingModal={setPendingModal}
                  onOpenModal={(biomeId, nodeId) => {
                    setTowerEntryBiomeId(biomeId);
                    setTowerEntryNodeId(nodeId);
                  }}
                />
              </>
            )}
          </>
        ) : isCurrentTileKingdom ? (
          /* Kingdom/City-State biomes - only show LocationCard, no gather/expedition/build */
          <>
            <LocationCard
              currentNodeData={currentNodeData}
            />
            <CooldownDisplay
              moveOnCooldown={moveOnCooldown}
              moveCooldown={moveCooldown}
            />
            <EventLog eventLog={eventLog} eventLogIndex={eventLogIndex} setEventLogIndex={setEventLogIndex} />
            <div className="wm-stats"><div className="wm-stat"><span className="wm-stat-value">{totalDiscovered}/{explorationLimit}</span></div><div className="wm-stat"><span className="wm-stat-value">{totalTileBuildings} 🏛️</span></div></div>
          </>
        ) : (
          <>
            <LocationCard
              currentNodeData={currentNodeData}
              outpost={worldMap.outposts?.[worldMap?.currentNode]}
            />
            <CooldownDisplay
              moveOnCooldown={moveOnCooldown}
              moveCooldown={moveCooldown}
              gatherOnCooldown={gatherOnCooldown}
              gatherCooldown={gatherCooldown}
              expeditionProgress={expeditionProgress}
            />
            <AutoMoveStatus
              isAutoMoving={isAutoMoving}
              movementQueue={movementQueue}
              onCancel={() => { setIsAutoMoving(false); setMovementQueue([]); setPathPreview(null); showMessage('Cancelled'); }}
            />
            <EventLog eventLog={eventLog} eventLogIndex={eventLogIndex} setEventLogIndex={setEventLogIndex} />
            <TilePreviewPanel
              previewTile={previewTile}
              worldMap={worldMap}
              BIOMES={BIOMES}
              buildings={buildings}
              buildingCounts={buildingCounts}
              availableBuildings={previewTileBuildings}
              calculateGatherYield={calculateGatherYield}
              onClose={() => { setPreviewTile(null); setClickedTile(null); }}
              currentGreatWorkConstruction={currentGreatWorkConstruction}
              greatWorkLocations={greatWorkLocations}
              getGreatWorkProgressInfo={getGreatWorkProgressInfo}
              greatWorkRemaining={greatWorkRemaining}
              isOnTradeRoute={previewTile ? isTileOnTradeRoute(previewTile) : false}
              hasTradeRouteSegment={previewTile ? !!worldMap.tradeRoutes?.segments?.[previewTile]?.built : false}
              onCreateOutpost={handleCreateOutpost}
              onManageOutpost={(nodeId) => setOutpostManageNodeId(nodeId)}
              getOutpostConstructionProgress={getOutpostConstructionProgress}
              currentNodeId={worldMap?.currentNode}
              onActivateShrine={handleActivateShrine}
            />
            {!currentNodeData?.isHub && !isAutoMoving && !isCurrentTileKingdom && <div className="wm-actions">
              {/* Check if current tile has an outpost */}
              {(() => {
                const currentOutpost = worldMap.outposts?.[worldMap?.currentNode];
                const hasCurrentOutpost = !!currentOutpost;
                return (
                  <>
                    {!previewTile && (
                      <ExpeditionProgress
                        expeditionProgress={expeditionProgress}
                        onCancel={() => {
                          const result = cancelExpedition?.();
                          // Immediately update UI (don't wait for 1s interval)
                          setExpeditionProgress(isOnExpedition?.() ? getExpeditionProgress?.() : null);
                          return result;
                        }}
                        showMessage={showMessage}
                        playerLevel={playerCharacter?.level || 1}
                      />
                    )}

                    {/* GatheringUI - hide if outpost exists */}
                    {!previewTile && !expeditionProgress && !hasCurrentOutpost && (
                      <GatheringUI
                        expectedYields={expectedYields}
                        tileQuality={tileQuality}
                        gatherOnCooldown={gatherOnCooldown}
                        onGather={handleGather}
                        autoGatherEnabled={worldMap?.autoGatherEnabled}
                        onToggleAutoGather={toggleAutoGather}
                        lastGatherTime={worldMap?.lastGatherTime}
                      />
                    )}

                    {/* Shrine Activation UI - show when standing on shrine */}
                    {!previewTile && !expeditionProgress && BIOMES[currentNodeData?.biome]?.isShrineBiome && (
                      <ShrineActivationPanel
                        biomeId={currentNodeData?.biome}
                        isActivated={worldMap.activatedShrines?.[worldMap?.currentNode]}
                        onActivate={() => handleActivateShrine(worldMap?.currentNode)}
                      />
                    )}

                    {/* Outpost Controls for Current Tile - exclude special biomes */}
                    {!previewTile && !expeditionProgress && !currentNodeData?.isDungeonBiome && !currentNodeData?.isEncounterBiome && !currentNodeData?.isHub && !currentNodeData?.isTowerBiome && currentNodeData?.biome !== 'wonderSite' && !currentNodeData?.wonderSiteId && !BIOMES[currentNodeData?.biome]?.isShrineBiome && (
                      <OutpostControlsPanel
                        currentOutpost={currentOutpost}
                        nodeId={worldMap?.currentNode}
                        onCreateOutpost={handleCreateOutpost}
                        onManageOutpost={(nodeId) => setOutpostManageNodeId(nodeId)}
                        getOutpostConstructionProgress={getOutpostConstructionProgress}
                        isTileOnTradeRoute={isTileOnTradeRoute}
                        worldMap={worldMap}
                      />
                    )}

                    {/* ExpeditionSelector - hide if outpost exists */}
                    {!previewTile && !expeditionProgress && !hasCurrentOutpost && expectedYields?.yields && Object.keys(expectedYields.yields).length > 0 && (
                      <ExpeditionSelector
                        currentNode={worldMap?.currentNode}
                        selectedDuration={selectedExpeditionDuration}
                        setSelectedDuration={setSelectedExpeditionDuration}
                        getExpeditionDurations={getExpeditionDurations}
                        calculateExpeditionRewards={calculateExpeditionRewards}
                        startMapExpedition={(durationId) => {
                          const result = startMapExpedition?.(durationId);
                          // Immediately update UI (don't wait for 1s interval)
                          if (result?.success) {
                            setExpeditionProgress(getExpeditionProgress?.());
                          }
                          return result;
                        }}
                        showMessage={showMessage}
                        triggerTutorial={triggerTutorial}
                      />
                    )}

                    {/* TileBuildingButton - opens full modal UI */}
                    {currentNodeData?.canBuildOnTile && !previewTile && !expeditionProgress && !hasCurrentOutpost && !currentNodeData?.isTowerBiome && currentNodeData?.biome !== 'wonderSite' && !currentNodeData?.wonderSiteId && !BIOMES[currentNodeData?.biome]?.isShrineBiome && (
                      <TileBuildingButton
                        availableBuildings={availableBuildings}
                        onOpenModal={handleOpenTileBuildingModal}
                      />
                    )}
                    {/* BuiltTileInfo - hide if outpost exists */}
                    {!previewTile && !expeditionProgress && !hasCurrentOutpost && (
                      <BuiltTileInfo
                        currentNodeData={currentNodeData}
                        nodeId={worldMap?.currentNode}
                        canBuildRoute={worldMap?.currentNode && canBuildRouteSegment(worldMap.currentNode, worldMap?.tradeRoutes?.segments || {}, worldMap?.discoveredNodes || {})}
                        routeCost={worldMap?.currentNode ? getTradeRouteCost(worldMap.currentNode) : null}
                        isOnTradeRoute={worldMap?.currentNode ? isTileOnTradeRoute(worldMap.currentNode) : false}
                        onBuildRoute={(nodeId) => {
                          const result = buildTradeRouteSegment(nodeId);
                          if (result.success) {
                            showMessage('Trade route built!');
                            // Signal action for guided tour
                            triggerGuidedTourAction?.('tradeRouteBuilt');
                          } else {
                            showMessage(result.error || 'Failed to build route', true);
                          }
                        }}
                        playerResources={playerResources}
                        triggerTutorial={triggerTutorial}
                        startGuidedTour={startGuidedTour}
                        completedGuidedTours={completedGuidedTours}
                      />
                    )}
                  </>
                );
              })()}
            </div>}
            {currentNodeData?.isHub && <div className="wm-kingdom-info"><p>Click tiles to explore. Build at 2× cost!</p></div>}
            <div className="wm-stats"><div className="wm-stat"><span className="wm-stat-value">{totalDiscovered}/{explorationLimit}</span></div><div className="wm-stat"><span className="wm-stat-value">{totalTileBuildings} 🏛️</span></div></div>
          </>
        )}
      </div>

      {/* Help Modal */}
      {showHelpModal && <WorldMapHelp onClose={() => setShowHelpModal(false)} />}

      {/* Tile Building Modal */}
      {showTileBuildingModal && (
        <TileBuildingModal
          onClose={() => setShowTileBuildingModal(false)}
          onBuild={handleBuildOnTile}
        />
      )}

      {/* Dungeon Entry Modal */}
      {dungeonEntryBiomeId && (
        <DungeonEntryModal
          biomeId={dungeonEntryBiomeId}
          playerLevel={playerCharacter?.level || 1}
          currentEra={currentEra || 1}
          onEnter={(selectedDungeonId) => {
            const biome = BIOMES[dungeonEntryBiomeId];
            if (biome?.isDungeonBiome && selectedDungeonId) {
              // Use the selected dungeon ID from the modal
              // Get mob pool from DUNGEON_MOB_POOLS based on selected dungeon, fallback to biome definition
              const mobPool = DUNGEON_MOB_POOLS[selectedDungeonId] || biome.dungeonMobPool || [];
              // Get tier number from dungeon ID (e.g., 'tier1_lv5' -> 'tier1')
              const tierKey = `tier${DUNGEON_CONFIG.TIERS.find(d => d.id === selectedDungeonId)?.tier || 1}`;
              const bossPool = DUNGEON_BOSS_POOLS[tierKey] || biome.dungeonBossPool || [];
              enterDungeon(selectedDungeonId, mobPool, bossPool, dungeonEntryNodeId);
            }
            setDungeonEntryBiomeId(null);
            setDungeonEntryNodeId(null);
          }}
          onCancel={() => {
            setDungeonEntryBiomeId(null);
            setDungeonEntryNodeId(null);
          }}
        />
      )}

      {/* Encounter Entry Modal */}
      {encounterEntryBiomeId && (
        <EncounterEntryModal
          biomeId={encounterEntryBiomeId}
          playerLevel={playerCharacter?.level || 1}
          onEnter={() => {
            const biome = ENCOUNTER_BIOMES[encounterEntryBiomeId];
            if (biome?.isEncounterBiome) {
              enterEncounter(encounterEntryBiomeId, encounterEntryNodeId);
            }
            setEncounterEntryBiomeId(null);
            setEncounterEntryNodeId(null);
          }}
          onCancel={() => {
            setEncounterEntryBiomeId(null);
            setEncounterEntryNodeId(null);
          }}
        />
      )}

      {/* Tower Entry Modal */}
      {towerEntryBiomeId && (
        <TowerEntryModal
          biomeId={towerEntryBiomeId}
          playerLevel={playerCharacter?.level || 1}
          currentEra={currentEra || 1}
          cooldownRemaining={towerCooldownMs}
          onEnter={() => {
            const biome = TOWER_BIOMES[towerEntryBiomeId];
            if (biome?.isTowerBiome) {
              enterTower(towerEntryBiomeId, towerEntryNodeId);
            }
            setTowerEntryBiomeId(null);
            setTowerEntryNodeId(null);
          }}
          onCancel={() => {
            setTowerEntryBiomeId(null);
            setTowerEntryNodeId(null);
          }}
        />
      )}

      {/* Expedition Complete Modal */}
      {expeditionComplete && (
        <ExpeditionCompleteModal
          completion={expeditionComplete}
          onClose={() => {
            setExpeditionComplete(null);
            clearExpeditionCompletion(); // Clear from GameContext to prevent re-showing
          }}
        />
      )}

      {/* Outpost Creation Modal */}
      {outpostCreationNodeId && (
        <OutpostCreationModal
          nodeId={outpostCreationNodeId}
          onClose={() => setOutpostCreationNodeId(null)}
          onSuccess={() => {
            // Optionally open the management modal for the new outpost
            const createdNodeId = outpostCreationNodeId;
            setOutpostCreationNodeId(null);
            // Don't auto-open management - let user click on the tile
          }}
        />
      )}

      {/* Outpost Management Modal */}
      {outpostManageNodeId && (
        <OutpostModal
          nodeId={outpostManageNodeId}
          onClose={() => setOutpostManageNodeId(null)}
        />
      )}

    </div>
  );
}

export default WorldMapPanel;
